"use client"

// src/accordion/index.tsx
import * as RadixAccordion from "@radix-ui/react-accordion";
import { RiArrowDownSLine } from "@remixicon/react";
import React, { useState } from "react";
import { styled } from "styled-components";

// src/theme/colors.ts
import { memo } from "@lowerdeck/memo";

// src/theme/tokens.ts
var toDashCase = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replaceAll("_", "-");
};
var createThemeToken = (defaultValue) => (name) => {
  let id = toDashCase(name);
  return {
    id,
    name,
    value: `var(--${id}${defaultValue ? `, ${defaultValue}` : ""})`,
    defaultValue,
    varName: `--${id}`
  };
};
var recursivelyCallThemeTokenFactory = (tokens) => {
  let result = {};
  for (let key in tokens) {
    let token = tokens[key];
    if (typeof token === "function") {
      result[key] = token(key);
    } else {
      result[key] = recursivelyCallThemeTokenFactory(token);
    }
  }
  return result;
};
var recursivelyGetThemeTokenValues = (tokens) => {
  let result = {};
  for (let key in tokens) {
    let token = tokens[key];
    result[key] = "value" in token ? token.value : recursivelyGetThemeTokenValues(token);
  }
  return result;
};
var createTheme = (tokens) => {
  let values = recursivelyCallThemeTokenFactory(tokens);
  let stringValues = recursivelyGetThemeTokenValues(values);
  let setBodyStyles = (theme2) => {
    for (let value of Object.values(theme2)) {
      if ("value" in value) {
        if (value.defaultValue) {
          document.body.style.setProperty(value.varName, value.defaultValue);
        }
      } else if (typeof value === "object") {
        setBodyStyles(value);
      }
    }
  };
  return {
    ...stringValues,
    setRootStyles: (theme2) => {
      let styles = {};
      let recurse = (theme3, values2) => {
        for (let key in theme3) {
          let value = theme3[key];
          if (typeof value == "string" || value.value) {
            let cssKey = values2[key]?.varName;
            if (value.value) value = value.value;
            if (value && cssKey) {
              styles[cssKey] = value;
            }
          } else {
            recurse(theme3[key], values2[key]);
          }
        }
      };
      recurse(theme2, values);
      return styles;
    },
    setBodyStyles: () => setBodyStyles(values)
  };
};

// src/theme/colors.ts
var colors = {
  gray100: "#fafafa",
  gray150: "#f8f8f8",
  gray200: "#f6f6f6",
  gray250: "#f3f3f3",
  gray300: "#efefef",
  gray350: "#e6e6e6",
  gray400: "#ddd",
  gray450: "#d5d5d5",
  gray500: "#ccc",
  gray550: "#b3b3b3",
  gray600: "#999",
  gray650: "#777",
  gray700: "#555",
  gray750: "#444",
  gray800: "#333",
  gray850: "#2b2b2b",
  gray900: "#222",
  blue100: "#fbfdff",
  blue150: "#f8fcff",
  blue200: "#f4faff",
  blue250: "#edf7ff",
  blue300: "#e6f4fe",
  blue350: "#def2ff",
  blue400: "#d5efff",
  blue450: "#cceaff",
  blue500: "#c2e5ff",
  blue550: "#b7dffe",
  blue600: "#acd8fc",
  blue650: "#9dd0f9",
  blue700: "#8ec8f6",
  blue750: "#76bdf3",
  blue800: "#5eb1ef",
  blue850: "#2fa1f7",
  blue900: "#0090ff",
  pink100: "#fffcfd",
  pink150: "#fffafb",
  pink200: "#fef7f9",
  pink250: "#fff0f5",
  pink300: "#ffe9f0",
  pink350: "#ffe3ec",
  pink400: "#fedce7",
  pink450: "#fcd5e2",
  pink500: "#facedd",
  pink550: "#f7c6d7",
  pink600: "#f3bed1",
  pink650: "#efb5ca",
  pink700: "#eaacc3",
  pink750: "#e5a0bb",
  pink800: "#e093b2",
  pink850: "#e5689a",
  pink900: "#e93d82",
  cyan100: "#fafdfe",
  cyan150: "#f6fcfd",
  cyan200: "#f2fafb",
  cyan250: "#e8f9fa",
  cyan300: "#def7f9",
  cyan350: "#d4f4f8",
  cyan400: "#caf1f6",
  cyan450: "#c0edf3",
  cyan500: "#b5e9f0",
  cyan550: "#a9e3ec",
  cyan600: "#9ddde7",
  cyan650: "#8dd6e2",
  cyan700: "#7dcedc",
  cyan750: "#5dc4d6",
  cyan800: "#3db9cf",
  cyan850: "#1faecb",
  cyan900: "#00a2c7",
  indigo100: "#fdfdfe",
  indigo150: "#fafbff",
  indigo200: "#f7f9ff",
  indigo250: "#f2f6ff",
  indigo300: "#edf2fe",
  indigo350: "#e7eeff",
  indigo400: "#e1e9ff",
  indigo450: "#dae4ff",
  indigo500: "#d2deff",
  indigo550: "#cad7ff",
  indigo600: "#c1d0ff",
  indigo650: "#b6c7fc",
  indigo700: "#abbdf9",
  indigo750: "#9cb1f4",
  indigo800: "#8da4ef",
  indigo850: "#6684e6",
  indigo900: "#3e63dd",
  iris100: "#fdfdff",
  iris150: "#fbfbff",
  iris200: "#f8f8ff",
  iris250: "#f4f5ff",
  iris300: "#f0f1fe",
  iris350: "#ebecff",
  iris400: "#e6e7ff",
  iris450: "#e0e2ff",
  iris500: "#dadcff",
  iris550: "#d3d5ff",
  iris600: "#cbcdff",
  iris650: "#c2c4fc",
  iris700: "#b8baf8",
  iris750: "#aaacf4",
  iris800: "#9b9ef0",
  iris850: "#7b7de3",
  iris900: "#5b5bd6",
  green100: "#fbfefd",
  green150: "#f8fdfa",
  green200: "#f4fbf7",
  green250: "#edf9f2",
  green300: "#e6f7ed",
  green350: "#def4e8",
  green400: "#d6f1e3",
  green450: "#cdeddd",
  green500: "#c3e9d7",
  green550: "#b8e4d0",
  green600: "#acdec8",
  green650: "#9cd6bf",
  green700: "#8bceb6",
  green750: "#71c4ab",
  green800: "#56ba9f",
  green850: "#40af91",
  green900: "#29a383",
  orange100: "#fefcfb",
  orange150: "#fffaf4",
  orange200: "#fff7ed",
  orange250: "#fff3e2",
  orange300: "#ffefd6",
  orange350: "#ffe7c6",
  orange400: "#ffdfb5",
  orange450: "#ffd8a8",
  orange500: "#ffd19a",
  orange550: "#ffc98e",
  orange600: "#ffc182",
  orange650: "#fab87b",
  orange700: "#f5ae73",
  orange750: "#f1a164",
  orange800: "#ec9455",
  orange850: "#f28035",
  orange900: "#f76b15",
  purple100: "#fefcfe",
  purple150: "#fdfafe",
  purple200: "#fbf7fe",
  purple250: "#f9f2fe",
  purple300: "#f7edfe",
  purple350: "#f5e8fd",
  purple400: "#f2e2fc",
  purple450: "#eedcfb",
  purple500: "#ead5f9",
  purple550: "#e5cdf7",
  purple600: "#e0c4f4",
  purple650: "#d9baf0",
  purple700: "#d1afec",
  purple750: "#c8a1e8",
  purple800: "#be93e4",
  purple850: "#a671d5",
  purple900: "#8e4ec6",
  red100: "#fffcfc",
  red150: "#fffafa",
  red200: "#fff7f7",
  red250: "#fff1f2",
  red300: "#feebec",
  red350: "#ffe3e4",
  red400: "#ffdbdc",
  red450: "#ffd4d5",
  red500: "#ffcdce",
  red550: "#f28b8e",
  red600: "#e5484d",
  red650: "#e5484d",
  red700: "#e5484d",
  red750: "#e5484d",
  red800: "#e5484d",
  red850: "#e5484d",
  red900: "#e5484d",
  violet100: "#fdfcfe",
  violet150: "#fcfaff",
  violet200: "#faf8ff",
  violet250: "#f7f4ff",
  violet300: "#f4f0fe",
  violet350: "#f0eaff",
  violet400: "#ebe4ff",
  violet450: "#e6dfff",
  violet500: "#e1d9ff",
  violet550: "#dbd2ff",
  violet600: "#d4cafe",
  violet650: "#cbc0fa",
  violet700: "#c2b5f5",
  violet750: "#b6a7f1",
  violet800: "#aa99ec",
  violet850: "#8c78de",
  violet900: "#6e56cf",
  yellow100: "#fefdfb",
  yellow150: "#fefcf2",
  yellow200: "#fefbe9",
  yellow250: "#fff9d6",
  yellow300: "#fff7c2",
  yellow350: "#fff3af",
  yellow400: "#ffee9c",
  yellow450: "#fdea8a",
  yellow500: "#fbe577",
  yellow550: "#f7de75",
  yellow600: "#f3d673",
  yellow650: "#eecc6b",
  yellow700: "#e9c162",
  yellow750: "#e6b24c",
  yellow800: "#e2a336",
  yellow850: "#f1b43a",
  yellow900: "#ffc53d",
  white100: "#fff",
  white150: "#fff",
  white200: "#fff",
  white250: "#fff",
  white300: "#fff",
  white350: "#fff",
  white400: "#fff",
  white450: "#fff",
  white500: "#fff",
  white550: "#fff",
  white600: "#fff",
  white650: "#fff",
  white700: "#fff",
  white750: "#fff",
  white800: "#fff",
  white850: "#fff",
  white900: "#fff",
  black100: "#000",
  black150: "#000",
  black200: "#000",
  black250: "#000",
  black300: "#000",
  black350: "#000",
  black400: "#000",
  black450: "#000",
  black500: "#000",
  black550: "#000",
  black600: "#000",
  black650: "#000",
  black700: "#000",
  black750: "#000",
  black800: "#000",
  black850: "#000",
  black900: "#000",
  primary100: "#b1e0ff",
  primary150: "#94d4ff",
  primary200: "#76c8ff",
  primary250: "#62c0ff",
  primary300: "#4eb8ff",
  primary350: "#3bb1ff",
  primary400: "#27a9ff",
  primary450: "#14a1ff",
  primary500: "#0099ff",
  primary550: "#008dec",
  primary600: "#0081d8",
  primary650: "#0076c5",
  primary700: "#006ab1",
  primary750: "#005e9d",
  primary800: "#005289",
  primary850: "#004776",
  primary900: "#003b62",
  primary: "#0099ff"
};
var colorThemeVars = Object.fromEntries(Object.entries(colors).map(([key, value]) => [key, createThemeToken(value)]));
var getColorKey = (color, facet) => `${color}${facet}`;
var getForegroundColor = memo((color) => {
  if (color.startsWith("white") || color.startsWith("yellow")) {
    return "#000";
  }
  let value = colors[color];
  if (value.length == 4) {
    value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  let r = parseInt(value.substr(1, 2), 16);
  let g = parseInt(value.substr(3, 2), 16);
  let b = parseInt(value.substr(5, 2), 16);
  let brightness = (r * 299 + g * 587 + b * 114) / 1e3;
  if (Number.isNaN(brightness)) return "#fff";
  return brightness > 125 ? "#000" : "#fff";
});

// src/theme/index.ts
var theme = createTheme({
  colors: {
    foreground: createThemeToken("#111111"),
    background: createThemeToken("#ffffff"),
    ...colorThemeVars
  },
  shadows: {
    small: createThemeToken("0 1px 4px rgba(0, 0, 0, 0.08)"),
    medium: createThemeToken("0 4px 8px rgba(0, 0, 0, 0.1)"),
    large: createThemeToken("0 8px 16px rgba(0, 0, 0, 0.1)")
  }
});

// src/accordion/index.tsx
var Item2 = styled(RadixAccordion.Item).withConfig({
  componentId: "sc-jn5ooz-0"
})`border:1px solid ${theme.colors.gray300};display:flex;flex-direction:column;background:${theme.colors.background};overflow:hidden;border-radius:10px;transition:none;&:focus-within{border-color:${theme.colors.gray500};}&:not(:first-child){margin-top:10px;}`;
var Trigger2 = styled(RadixAccordion.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-jn5ooz-1"
})`min-height:48px;display:flex;align-items:center;justify-content:space-between;width:100%;padding:12px 16px;font-size:14px;font-weight:600;border:none;outline:none;cursor:pointer;background:transparent;text-align:left;user-select:none;color:${theme.colors.gray900};&[data-disabled]{color:${theme.colors.gray600};cursor:not-allowed;}`;
var TriggerTitleText = styled.span.withConfig({
  componentId: "sc-jn5ooz-2"
})`display:inline-flex;flex-direction:column;gap:3px;`;
var TriggerDescription = styled.span.withConfig({
  componentId: "sc-jn5ooz-3"
})`font-size:12px;font-weight:500;color:${theme.colors.gray600};line-height:1.2;`;
var Content2 = styled(RadixAccordion.Content).withConfig({
  componentId: "sc-jn5ooz-4"
})`font-size:14px;border-top:1px solid ${theme.colors.gray300};overflow:hidden;background:${theme.colors.background};&[data-state='open']{height:auto;}`;
var Accordion = (p) => {
  let [internalValue, setInternalValue] = useState(() => {
    if (p.type === "multiple") {
      return p.items.flatMap((item, i) => item.defaultOpen ? [i.toString()] : []);
    }
    let initialIndex = p.items.findIndex((item) => item.defaultOpen);
    return initialIndex >= 0 ? initialIndex.toString() : void 0;
  });
  let isControlled = p.value !== void 0;
  let resolvedValue = isControlled ? p.value : internalValue;
  let handleValueChange = (nextValue) => {
    if (!isControlled) {
      if (Array.isArray(nextValue)) {
        setInternalValue(nextValue);
      } else {
        setInternalValue(nextValue || void 0);
      }
    }
    p.onValueChange?.(nextValue);
  };
  return /* @__PURE__ */ React.createElement(RadixAccordion.Root, { type: p.type ?? "single", collapsible: (p.type ?? "single") === "single" ? p.collapsible : void 0, disabled: p.disabled, value: resolvedValue, onValueChange: handleValueChange }, p.items.map((item, i) => {
    let isOpen = Array.isArray(resolvedValue) ? resolvedValue.includes(i.toString()) : resolvedValue == i.toString();
    return /* @__PURE__ */ React.createElement(Item2, { value: i.toString(), key: i, disabled: item.disabled }, /* @__PURE__ */ React.createElement(Trigger2, { disabled: item.disabled }, /* @__PURE__ */ React.createElement(TriggerTitleText, null, /* @__PURE__ */ React.createElement("span", null, item.title), item.description ? /* @__PURE__ */ React.createElement(TriggerDescription, null, item.description) : null), /* @__PURE__ */ React.createElement(RiArrowDownSLine, { style: {
      transform: `rotate(${isOpen ? 180 : 0}deg)`,
      opacity: 0.5
    }, size: 16 })), /* @__PURE__ */ React.createElement(Content2, null, /* @__PURE__ */ React.createElement("div", { style: {
      padding: "16px"
    } }, item.content)));
  }));
};
var AccordionSingle = ({
  title,
  children,
  disabled,
  defaultOpen
}) => {
  return /* @__PURE__ */ React.createElement(Accordion, { items: [{
    title,
    disabled,
    content: children,
    defaultOpen
  }], type: "multiple" });
};

// src/alert/index.tsx
import * as RadixAlertDialog from "@radix-ui/react-alert-dialog";
import React3 from "react";
import { keyframes, styled as styled2 } from "styled-components";

// src/dialog/state.ts
import React2, { useContext, useEffect, useState as useState2 } from "react";
var useDialog = (initialState = false) => {
  let [isOpen, setIsOpen] = useState2(initialState);
  return {
    state: {
      isOpen,
      onOpenChange: setIsOpen
    },
    isOpen,
    open: () => setIsOpen(true)
  };
};
var currentZIndexRef = {
  value: 1e3
};
var useZindex = (isOpen) => {
  let [zIndex, setZIndex] = useState2(() => currentZIndexRef.value + 5);
  useEffect(() => {
    currentZIndexRef.value += 10;
    setZIndex(currentZIndexRef.value);
  }, [isOpen]);
  return zIndex;
};
var useZIndex = useZindex;
var useDialogZIndex = useZindex;
var DialogContext = React2.createContext(null);
var DialogProvider = DialogContext.Provider;
var useDialogContext = () => useContext(DialogContext);
var useIsInDialog = () => !!useContext(DialogContext);
var preventDialogDismissForPointerGesture = false;
if (typeof document !== "undefined") {
  document.addEventListener("pointerdown", () => {
    preventDialogDismissForPointerGesture = false;
  }, true);
}
var nestedOverlaySelector = "[data-metorial-select-content], [data-metorial-menu-content]";
var openNestedOverlaySelector = '[data-metorial-select-content][data-state="open"], [data-metorial-menu-content][data-state="open"]';
var markSelectPointerDismiss = () => {
  preventDialogDismissForPointerGesture = true;
};
var markOverlayPointerDismiss = markSelectPointerDismiss;
var isNestedOverlayInteraction = (event) => {
  let target = event.detail?.originalEvent?.target ?? event.target;
  return target instanceof Element && target.closest(nestedOverlaySelector) != null;
};
var preventDialogDismissForSelectInteraction = (event) => {
  if (preventDialogDismissForPointerGesture) {
    event.preventDefault();
    return;
  }
  if (isNestedOverlayInteraction(event)) event.preventDefault();
};
var preventPopoverDismissForNestedOverlay = (event) => {
  if (preventDialogDismissForPointerGesture || isNestedOverlayInteraction(event)) {
    event.preventDefault();
    return;
  }
  if (typeof document !== "undefined" && document.querySelector(openNestedOverlaySelector)) {
    event.preventDefault();
  }
};

// src/alert/index.tsx
var fadeInShift = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift = keyframes`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.4;
  }
`;
var fadeOut = keyframes`
  from {
    opacity: 0.4;
  }
  to {
    opacity: 0;
  }
`;
var Overlay2 = styled2(RadixAlertDialog.Overlay).withConfig({
  componentId: "sc-1ag8o3v-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:${theme.colors.gray600};z-index:1000;&[data-state='open']{animation:${fadeIn} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut} 200ms ease-in forwards;}`;
var Content4 = styled2(RadixAlertDialog.Content).withConfig({
  componentId: "sc-1ag8o3v-1"
})`position:fixed;z-index:1001;top:50%;left:50%;transform:translate(-50%,-50%);width:90vw;max-width:400px;max-height:90vh;overflow:auto;background:${theme.colors.gray200};border-radius:10px;box-shadow:0 0 10px rgba(0,0,0,0.1);color:${theme.colors.gray900};&[data-state='open']{animation:${fadeInShift} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift} 200ms ease-in;}`;
var Title2 = styled2(RadixAlertDialog.Title).withConfig({
  componentId: "sc-1ag8o3v-2"
})`padding:20px 20px 0px 20px;font-size:20px;font-weight:600;`;
var Description2 = styled2(RadixAlertDialog.Description).withConfig({
  componentId: "sc-1ag8o3v-3"
})`padding:6px 20px 0px 20px;font-size:14px;color:${theme.colors.gray600};`;
var Actions = styled2("div").withConfig({
  componentId: "sc-1ag8o3v-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:20px;`;
var Alert = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React3.createElement(RadixAlertDialog.Portal, null, /* @__PURE__ */ React3.createElement(Overlay2, { style: {
      zIndex
    } }), /* @__PURE__ */ React3.createElement(Content4, { style: {
      zIndex: zIndex + 1
    } }, children)));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Title2, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Description2, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Actions, null, children);
  },
  Action: ({
    children,
    type
  }) => {
    if (type == "cancel") {
      return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Cancel, { asChild: true }, children);
    }
    return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Action, { asChild: true }, children);
  }
};

// src/animateContent/index.tsx
import { motion } from "framer-motion";
import React4, { useEffect as useEffect2, useMemo, useRef, useState as useState3 } from "react";
var AnimateContent = (props) => {
  let [prevIdentifier, setPrevIdentifier] = useState3(() => props.identifier);
  let direction = useMemo(() => {
    if (props.identifier != prevIdentifier) return "animate";
    return "none";
  }, [props.identifier, prevIdentifier]);
  let prevChildren = useRef(props.children);
  useEffect2(() => {
    let to = setTimeout(() => {
      setPrevIdentifier(props.identifier);
      prevChildren.current = props.children;
    }, 300);
    return () => clearTimeout(to);
  }, [props.identifier]);
  useEffect2(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ React4.createElement("div", { style: {
    // display: 'flex',
    width: "100%",
    position: "relative",
    padding: `0px ${props.paddingInline ?? 20}px`
  } }, /* @__PURE__ */ React4.createElement(motion.div, { initial: direction == "animate" ? {
    opacity: 0,
    scale: 1.2
  } : {
    opacity: 1
  }, animate: {
    opacity: 1,
    scale: 1
  }, transition: {
    duration: 0.2,
    ease: "easeInOut"
  }, key: props.identifier }, props.children), direction != "none" && /* @__PURE__ */ React4.createElement(motion.div, { initial: {
    opacity: 1,
    scale: 1
  }, animate: {
    opacity: 0,
    scale: 0.8
  }, transition: {
    duration: 0.2,
    ease: "easeInOut"
  }, key: prevIdentifier, style: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%"
  } }, prevChildren.current));
};

// src/animateHeight/index.tsx
import { motion as motion2 } from "framer-motion";
import React5 from "react";
import { useMeasure } from "react-use";
var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
var AnimateHeight = (props) => {
  let [ref, {
    height
  }] = useMeasure();
  if (isSafari) {
    return /* @__PURE__ */ React5.createElement("div", null, props.children);
  }
  return /* @__PURE__ */ React5.createElement(motion2.div, { animate: {
    height
  }, transition: {
    duration: 0.2
  }, style: {
    width: "100%",
    overflowY: "hidden"
  } }, /* @__PURE__ */ React5.createElement("div", { ref }, props.children));
};

// src/animatePanes/index.tsx
import { motion as motion3 } from "framer-motion";
import React6, { useEffect as useEffect3, useMemo as useMemo2, useRef as useRef2, useState as useState4 } from "react";
var AnimatePanes = (props) => {
  let [prevOrderedIdentifier, setPrevOrderedIdentifier] = useState4(() => props.orderedIdentifier);
  let direction = useMemo2(() => {
    if (props.orderedIdentifier > prevOrderedIdentifier) return "left";
    if (props.orderedIdentifier < prevOrderedIdentifier) return "right";
    return "none";
  }, [props.orderedIdentifier, prevOrderedIdentifier]);
  let prevChildren = useRef2(props.children);
  useEffect3(() => {
    let to = setTimeout(() => {
      setPrevOrderedIdentifier(props.orderedIdentifier);
      prevChildren.current = props.children;
    }, 300 + (props.delayMs ?? 0));
    return () => clearTimeout(to);
  }, [props.orderedIdentifier, props.delayMs, props.children]);
  useEffect3(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ React6.createElement(AnimateHeight, null, /* @__PURE__ */ React6.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
    position: "relative",
    padding: 3
  } }, /* @__PURE__ */ React6.createElement(motion3.div, { initial: {
    x: direction == "right" ? "-100%" : direction == "left" ? "100%" : 0
  }, animate: {
    x: 0
  }, transition: {
    duration: 0.2,
    ease: "easeInOut",
    delay: (props.delayMs ?? 0) / 1e3
  }, key: props.orderedIdentifier }, props.children), direction != "none" && /* @__PURE__ */ React6.createElement(motion3.div, { initial: {
    x: 0
  }, animate: {
    x: direction == "right" ? "100%" : "-100%"
  }, transition: {
    duration: 0.2,
    ease: "easeInOut",
    delay: (props.delayMs ?? 0) / 1e3
  }, key: prevOrderedIdentifier, style: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%"
  } }, prevChildren.current)));
};

// src/atoms/index.ts
import { useLayoutEffect, useState as useState5 } from "react";

// src/atoms/listeners.ts
var createListeners = () => {
  let listeners = /* @__PURE__ */ new Set();
  let register = (listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  let notify = () => {
    for (let listener of listeners) listener();
  };
  let dispose = () => {
    listeners.clear();
  };
  return {
    register,
    notify,
    dispose
  };
};

// src/atoms/index.ts
var atom = (initialValue) => {
  let value = initialValue;
  let listeners = createListeners();
  let set = (newValue) => {
    if (typeof newValue === "function") {
      value = newValue(value);
    } else {
      value = newValue;
    }
    listeners.notify();
  };
  let get = () => value;
  let subscribe = (listener) => {
    let cb = () => listener(get());
    return listeners.register(cb);
  };
  return {
    set,
    get,
    subscribe
  };
};
var useAtom = (a) => {
  let [_, setState] = useState5({});
  useLayoutEffect(() => a.subscribe(() => setState({})), [a]);
  return a.get();
};

// src/attributes/index.tsx
import React9 from "react";
import { styled as styled5 } from "styled-components";

// src/text/code.tsx
import React7 from "react";
import { styled as styled3 } from "styled-components";

// src/text/constants.ts
var textSizeSteps = {
  "1": {
    "--font-size": "12px",
    "--letter-spacing": "0.0025em",
    "--line-height": "16px",
    "--mobile-scale": "1"
  },
  "2": {
    "--font-size": "14px",
    "--letter-spacing": "0em",
    "--line-height": "20px",
    "--mobile-scale": "1"
  },
  "3": {
    "--font-size": "16px",
    "--letter-spacing": "0em",
    "--line-height": "24px",
    "--mobile-scale": "1"
  },
  "4": {
    "--font-size": "18px",
    "--letter-spacing": "-0.0025em",
    "--line-height": "26px",
    "--mobile-scale": "1"
  },
  "5": {
    "--font-size": "20px",
    "--letter-spacing": "-0.005em",
    "--line-height": "28px",
    "--mobile-scale": "0.95"
  },
  "6": {
    "--font-size": "24px",
    "--letter-spacing": "-0.00625em",
    "--line-height": "30px",
    "--mobile-scale": "0.95"
  },
  "7": {
    "--font-size": "28px",
    "--letter-spacing": "-0.0075em",
    "--line-height": "36px",
    "--mobile-scale": "0.9"
  },
  "8": {
    "--font-size": "35px",
    "--letter-spacing": "-0.01em",
    "--line-height": "40px",
    "--mobile-scale": "0.8"
  },
  "9": {
    "--font-size": "60px",
    "--letter-spacing": "-0.025em",
    "--line-height": "60px",
    "--mobile-scale": "0.7"
  }
};
var textWeights = {
  light: {
    fontWeight: 300
  },
  regular: {
    fontWeight: 400
  },
  medium: {
    fontWeight: 500
  },
  strong: {
    fontWeight: 600
  },
  bold: {
    fontWeight: 700
  }
};
var textAlignments = {
  left: {
    textAlign: "left"
  },
  center: {
    textAlign: "center"
  },
  right: {
    textAlign: "right"
  }
};
var textWrap = {
  wrap: {
    whiteSpace: "normal"
  },
  nowrap: {
    whiteSpace: "nowrap"
  },
  balance: {
    textWrap: "balance"
  }
};
var textTransforms = {
  uppercase: {
    textTransform: "uppercase"
  },
  lowercase: {
    textTransform: "lowercase"
  },
  capitalize: {
    textTransform: "capitalize"
  }
};
var truncateStyles = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};
var getTextStyles = (props) => ({
  margin: 0,
  padding: 0,
  fontFamily: "inherit",
  ...textSizeSteps[props.size || "2"],
  ...textWeights[props.weight || "regular"],
  ...textAlignments[props.align || "left"],
  ...textWrap[props.wrap || "wrap"],
  ...props.transform && textTransforms[props.transform],
  ...props.truncate && truncateStyles,
  color: props.color ? theme.colors[props.color] : theme.colors.foreground
});

// src/text/code.tsx
var StyledCode = styled3("code").withConfig({
  componentId: "sc-18y1v5y-0"
})`font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;background:transparent;padding:0.2em 0.4em;color:${theme.colors.gray900};`;
var Code = (props) => {
  let size = getTextStyles(props);
  return /* @__PURE__ */ React7.createElement(StyledCode, { style: {
    ...size,
    ...props.style
  } }, props.children);
};
var StyledKbd = styled3("kbd").withConfig({
  componentId: "sc-18y1v5y-1"
})`background-color:${theme.colors.gray100};box-shadow:inset 0 -1px 0 ${theme.colors.gray300};border-radius:0.2em;padding:0.1em 0.3em;font-size:0.9em;font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;`;
var Kbd = (props) => {
  return /* @__PURE__ */ React7.createElement(StyledKbd, { style: {
    ...props.style
  } }, props.children);
};

// src/text/text.tsx
import React8 from "react";
import { styled as styled4 } from "styled-components";
var Element2 = styled4("div").withConfig({
  componentId: "sc-1ee7qw3-0"
})`font-size:var(--font-size);letter-spacing:var(--letter-spacing);line-height:var(--line-height);@media (max-width:768px){font-size:calc(var(--font-size) * var(--mobile-scale));line-height:calc(var(--line-height) * var(--mobile-scale));}&[data-balance='true']{text-wrap:balance;}`;
var Text = (props) => {
  let styles = getTextStyles(props);
  let elementProps = {
    style: {
      ...styles,
      ...props.style
    },
    "data-balance": props.balance ? "true" : void 0,
    as: props.as ?? "p"
  };
  return React8.createElement(Element2, elementProps, props.children);
};
var Title3 = (props) => {
  let styles = getTextStyles(props);
  let elementProps = {
    style: {
      ...styles,
      ...props.style
    },
    "data-balance": props.balance ? "true" : void 0,
    as: props.as ?? "h1"
  };
  return React8.createElement(Element2, elementProps, props.children);
};

// src/attributes/index.tsx
var Wrapper = styled5("div").withConfig({
  componentId: "sc-4jzbpy-0"
})`border:1px solid ${theme.colors.gray400};box-shadow:${theme.shadows.small};background:${theme.colors.gray100};border-radius:8px;`;
var Header = styled5("header").withConfig({
  componentId: "sc-4jzbpy-1"
})`margin-bottom:6px;padding:6px 18px 0 18px;`;
var Content5 = styled5("div").withConfig({
  componentId: "sc-4jzbpy-2"
})`display:grid;grid-template-columns:${({
  $columns
}) => $columns ? `repeat(${$columns}, minmax(0, 1fr))` : "repeat(auto-fill, minmax(var(--width), 1fr))"};gap:1px;background:${theme.colors.background};border:1px solid ${theme.colors.gray400};border-radius:8px;overflow:hidden;margin-left:-1px;margin-right:-1px;margin-bottom:-1px;${({
  $columns
}) => $columns ? `
    @media (max-width: 720px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
      grid-template-columns: minmax(0, 1fr);
    }
  ` : ""}`;
var Attribute = styled5("div").withConfig({
  componentId: "sc-4jzbpy-3"
})`background:${theme.colors.background};box-shadow:0 0 0 1px ${theme.colors.gray400};padding:15px;display:flex;align-items:center;`;
var Inner = styled5("div").withConfig({
  componentId: "sc-4jzbpy-4"
})`display:flex;flex-direction:column;gap:3px;`;
var Attributes = ({
  attributes,
  title = "Attributes",
  itemWidth = "300px",
  columns
}) => {
  return /* @__PURE__ */ React9.createElement(Wrapper, null, /* @__PURE__ */ React9.createElement(Header, null, /* @__PURE__ */ React9.createElement(Title3, { as: "h2", size: "2", weight: "strong" }, title)), /* @__PURE__ */ React9.createElement(Content5, { $columns: columns, style: {
    "--width": itemWidth
  } }, attributes.map((attr, i) => /* @__PURE__ */ React9.createElement(Attribute, { key: i }, /* @__PURE__ */ React9.createElement(Inner, null, /* @__PURE__ */ React9.createElement(Text, { weight: "bold", size: "1" }, attr.label), /* @__PURE__ */ React9.createElement(Text, { size: "2", weight: "medium", color: "gray700", as: "div" }, attr.content))))));
};

// src/avatar/index.tsx
import { localizeImageUrl } from "@metorial/brand";
import React12, { useRef as useRef3, useState as useState8 } from "react";
import { useInterval } from "react-use";

// src/tooltip/index.tsx
import * as RadixTooltip from "@radix-ui/react-tooltip";
import React11, { useEffect as useEffect5, useState as useState7 } from "react";
import Balancer from "react-wrap-balancer";
import { keyframes as keyframes2, styled as styled6 } from "styled-components";

// src/tooltip/state.ts
import React10, { useContext as useContext2, useEffect as useEffect4, useMemo as useMemo3, useState as useState6 } from "react";
var OverlayOpenContext = React10.createContext(false);
var OverlayOpenProvider = OverlayOpenContext.Provider;
var useIsInsideOpenOverlay = () => useContext2(OverlayOpenContext);
var TooltipSuppressorContext = React10.createContext(null);
var TooltipSuppressorProvider = TooltipSuppressorContext.Provider;
var useTooltipSuppressor = () => {
  let [holds, setHolds] = useState6(0);
  let suppressor = useMemo3(() => ({
    hold: () => {
      setHolds((holds2) => holds2 + 1);
      let isReleased = false;
      return () => {
        if (isReleased) return;
        isReleased = true;
        setHolds((holds2) => holds2 - 1);
      };
    }
  }), []);
  return {
    isHeld: holds > 0,
    suppressor
  };
};
var useSuppressTooltipWhileOpen = (isOpen) => {
  let suppressor = useContext2(TooltipSuppressorContext);
  useEffect4(() => {
    if (!isOpen) return;
    return suppressor?.hold();
  }, [isOpen, suppressor]);
};

// src/tooltip/index.tsx
var fadeInTop = keyframes2`
  from { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutTop = keyframes2`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
`;
var fadeInBottom = keyframes2`
  from { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutBottom = keyframes2`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInLeft = keyframes2`
  from { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutLeft = keyframes2`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInRight = keyframes2`
  from { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutRight = keyframes2`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
`;
var Content7 = styled6(RadixTooltip.Content).withConfig({
  componentId: "sc-2oojj5-0"
})`display:flex;gap:10px;align-items:center;cursor:default;background:${theme.colors.foreground};color:${theme.colors.background};border-radius:7px;box-shadow:${theme.shadows.medium};will-change:transform,opacity;padding:8px 10px;font-size:12px;font-weight:500;max-width:400px;z-index:99999;&[data-state='instant-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom} 0.2s ease forwards;}&[data-state='instant-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop} 0.2s ease forwards;}&[data-state='instant-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft} 0.2s ease forwards;}&[data-state='instant-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight} 0.2s ease forwards;}`;
var Arrow2 = styled6(RadixTooltip.Arrow).withConfig({
  componentId: "sc-2oojj5-1"
})`fill:${theme.colors.foreground};`;
var SUPPRESSION_LINGER = 300;
var Tooltip = React11.forwardRef(({
  content,
  children,
  arrow,
  delayDuration,
  enabled,
  side = "bottom",
  align = "center",
  ...triggerProps
}, ref) => {
  let [isOpen, setIsOpen] = useState7(false);
  let [isSuppressed, setIsSuppressed] = useState7(true);
  let {
    isHeld,
    suppressor
  } = useTooltipSuppressor();
  let isInsideOpenOverlay = useIsInsideOpenOverlay();
  let shouldSuppress = enabled === false || isHeld || isInsideOpenOverlay;
  useEffect5(() => {
    if (shouldSuppress) {
      setIsSuppressed(true);
      setIsOpen(false);
      return;
    }
    let timeout = setTimeout(() => setIsSuppressed(false), SUPPRESSION_LINGER);
    return () => clearTimeout(timeout);
  }, [shouldSuppress]);
  return /* @__PURE__ */ React11.createElement(RadixTooltip.Provider, { delayDuration }, /* @__PURE__ */ React11.createElement(RadixTooltip.Root, { open: isOpen && !isSuppressed, onOpenChange: (open) => {
    if (open && isSuppressed) return;
    setIsOpen(open);
  } }, /* @__PURE__ */ React11.createElement(TooltipSuppressorProvider, { value: suppressor }, /* @__PURE__ */ React11.createElement(RadixTooltip.Trigger, { asChild: true, ...triggerProps, ref }, children)), /* @__PURE__ */ React11.createElement(RadixTooltip.Portal, null, /* @__PURE__ */ React11.createElement(Content7, { sideOffset: 5, side, align, hideWhenDetached: true }, typeof content == "string" ? /* @__PURE__ */ React11.createElement(React11.Fragment, null, /* @__PURE__ */ React11.createElement(Balancer, null, content)) : /* @__PURE__ */ React11.createElement(React11.Fragment, null, content), arrow && /* @__PURE__ */ React11.createElement(Arrow2, null)))));
});

// src/avatar/index.tsx
var loadedSet = /* @__PURE__ */ new Set();
var Avatar = ({
  entity,
  size = 30,
  noTooltip,
  withInitials,
  radius,
  imageFit = "cover"
}) => {
  let src = localizeImageUrl(entity?.photoUrl || entity?.imageUrl);
  let name = entity?.name;
  let ref = useRef3(null);
  let [loaded, setLoaded] = useState8(() => src ? loadedSet.has(src) : false);
  useInterval(() => {
    if (loaded || !ref.current || !src) return;
    if (ref.current.complete) {
      setLoaded(true);
      loadedSet.add(src);
    }
  }, 100);
  let initials = name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  let inner = /* @__PURE__ */ React12.createElement("div", { style: {
    width: size,
    aspectRatio: "1 / 1",
    borderRadius: typeof radius == "number" ? radius : "50%",
    // backgroundColor: theme.colors.gray200,
    display: "flex",
    position: "relative",
    overflow: "hidden"
  } }, src && /* @__PURE__ */ React12.createElement("img", { src, alt: name, ref, style: {
    width: "100%",
    height: "100%",
    borderRadius: typeof radius == "number" ? radius : "50%",
    objectFit: imageFit,
    opacity: loaded ? 1 : 0,
    display: "block"
  }, onLoad: () => {
    if (!src) return;
    setLoaded(true);
    loadedSet.add(src);
  } }), initials && withInitials && /* @__PURE__ */ React12.createElement("span", { style: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    fontSize: size / 2.5,
    fontWeight: 700,
    userSelect: "none",
    textShadow: "0 0 4px rgba(0, 0, 0, 0.7)"
  } }, initials));
  if (noTooltip) return inner;
  return /* @__PURE__ */ React12.createElement(Tooltip, { content: name }, inner);
};

// src/badge/index.tsx
import React14 from "react";

// src/badge/connection.tsx
import React13 from "react";
import { keyframes as keyframes3, styled as styled7 } from "styled-components";
var pulseConnectionDot = keyframes3`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 144, 255, 0.24);
  }

  70% {
    box-shadow: 0 0 0 5px rgba(0, 144, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 144, 255, 0);
  }
`;
var getConnectionStatusDotColor = (tone) => {
  if (tone == "connected") return theme.colors.blue800;
  if (tone == "review") return theme.colors.orange800;
  return theme.colors.gray500;
};
var ConnectionBadgeRoot = styled7.span.withConfig({
  componentId: "sc-437l1z-0"
})`min-width:${({
  $size
}) => $size == "1" ? "0" : "64px"};height:${({
  $size
}) => $size == "1" ? "20px" : "24px"};padding:${({
  $size
}) => $size == "1" ? "0 7px" : "0 9px"};border-radius:999px;display:inline-flex;align-items:center;justify-content:center;gap:${({
  $size
}) => $size == "1" ? "5px" : "7px"};flex-shrink:0;align-self:flex-start;border:1px solid ${({
  $tone
}) => $tone == "connected" ? theme.colors.blue400 : $tone == "review" ? theme.colors.orange400 : theme.colors.gray300};background:${({
  $tone
}) => $tone == "connected" ? theme.colors.blue200 : $tone == "review" ? theme.colors.orange200 : theme.colors.gray200};color:${({
  $tone
}) => $tone == "connected" ? theme.colors.blue900 : $tone == "review" ? theme.colors.orange900 : theme.colors.gray700};font-size:${({
  $size
}) => $size == "1" ? "10px" : "11px"};line-height:1;font-weight:650;`;
var StatusPulse = styled7.span.withConfig({
  componentId: "sc-437l1z-1"
})`width:${({
  $size
}) => $size == "1" ? "6px" : "7px"};height:${({
  $size
}) => $size == "1" ? "6px" : "7px"};border-radius:999px;background:${theme.colors.blue900};animation:${pulseConnectionDot} 2.2s ease-out infinite;`;
var StatusDot = styled7.span.withConfig({
  componentId: "sc-437l1z-2"
})`width:${({
  $size
}) => $size == "1" ? "6px" : "7px"};height:${({
  $size
}) => $size == "1" ? "6px" : "7px"};border-radius:999px;background:${({
  $tone
}) => $tone == "connected" ? theme.colors.blue900 : $tone == "review" ? theme.colors.orange900 : theme.colors.gray600};`;
var ConnectionBadge = ({
  children,
  tone,
  size = "2",
  style
}) => /* @__PURE__ */ React13.createElement(ConnectionBadgeRoot, { $tone: tone, $size: size, style }, children, tone == "connected" ? /* @__PURE__ */ React13.createElement(StatusPulse, { $size: size }) : /* @__PURE__ */ React13.createElement(StatusDot, { $tone: tone, $size: size }));

// src/badge/constants.ts
import { memo as memo2 } from "@lowerdeck/memo";
var badgeColor = memo2((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes = {
  "1": {
    padding: "2px 6px",
    fontSize: 10,
    borderRadius: 4
  },
  "2": {
    padding: "4px 10px",
    fontSize: 14,
    borderRadius: 6
  },
  "3": {
    padding: "8px 16px",
    fontSize: 20,
    borderRadius: 8
  }
};
var getBadgeStyles = ({
  color,
  size = "2"
}) => ({
  ...badgeColor(color),
  ...sizes[size]
});

// src/badge/index.tsx
var Badge = (props) => {
  if (props.variant == "connection") {
    return /* @__PURE__ */ React14.createElement(ConnectionBadge, { tone: props.tone, size: props.size, style: props.style }, props.children);
  }
  let {
    children,
    color,
    size,
    style
  } = props;
  let styles = getBadgeStyles({
    color,
    size
  });
  return /* @__PURE__ */ React14.createElement("span", { style: {
    ...styles,
    ...style,
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600
  } }, children);
};

// src/button/index.tsx
import React18, { useEffect as useEffect7, useRef as useRef5, useState as useState10 } from "react";
import { Check, ChevronDown } from "react-feather";
import { styled as styled12 } from "styled-components";
import _useDelayed from "use-delayed";

// src/menu/index.tsx
import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import { RiArrowRightSLine } from "@remixicon/react";
import React15, { useEffect as useEffect6, useState as useState9 } from "react";
import { css, keyframes as keyframes4, styled as styled8 } from "styled-components";

// src/menu/useConstrainedMenuHeight.ts
import { useCallback, useLayoutEffect as useLayoutEffect2, useRef as useRef4 } from "react";
var MENU_VIEWPORT_PADDING = 20;
var MENU_MAX_HEIGHT_VAR = "--metorial-menu-max-height";
var MENU_SIDES = /* @__PURE__ */ new Set(["top", "bottom", "left", "right"]);
var getViewportTop = () => {
  if (typeof window == "undefined") return 0;
  try {
    let visualViewport = window.visualViewport;
    if (visualViewport && Number.isFinite(visualViewport.offsetTop)) {
      return visualViewport.offsetTop;
    }
  } catch {
  }
  return 0;
};
var getViewportBottom = () => {
  if (typeof window == "undefined") return 0;
  try {
    let visualViewport = window.visualViewport;
    if (visualViewport && Number.isFinite(visualViewport.height)) {
      return getViewportTop() + visualViewport.height;
    }
  } catch {
  }
  let clientHeight = document.documentElement?.clientHeight;
  if (Number.isFinite(clientHeight) && clientHeight > 0) {
    return getViewportTop() + clientHeight;
  }
  if (Number.isFinite(window.innerHeight)) return getViewportTop() + window.innerHeight;
  return 0;
};
var readPositiveNumber = (value) => {
  let parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
var escapeSelector = (value) => {
  if (typeof CSS != "undefined" && typeof CSS.escape == "function") return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
};
var readCssPx = (node, name) => {
  if (!node) return null;
  try {
    return readPositiveNumber(getComputedStyle(node).getPropertyValue(name));
  } catch {
    return null;
  }
};
var readRadixAvailableHeight = (element) => {
  return readCssPx(element, "--radix-dropdown-menu-content-available-height") ?? readCssPx(element.parentElement, "--radix-dropdown-menu-content-available-height");
};
var getMenuSide = (element) => {
  let side = element.getAttribute("data-side");
  if (side && MENU_SIDES.has(side)) return side;
  return null;
};
var getTriggerRect = (element) => {
  try {
    let id = element.id;
    if (!id) return null;
    let trigger = document.querySelector(`[aria-controls="${escapeSelector(id)}"]`);
    if (!(trigger instanceof Element)) return null;
    let rect = trigger.getBoundingClientRect();
    if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)) return null;
    if (rect.width == 0 && rect.height == 0) return null;
    return rect;
  } catch {
    return null;
  }
};
var measureMenuMaxHeight = (element) => {
  if (!element.isConnected) return null;
  let rect = element.getBoundingClientRect();
  if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)) return null;
  if (rect.width == 0 && rect.height == 0) return null;
  let viewportTop = getViewportTop();
  let viewportBottom = getViewportBottom();
  if (!Number.isFinite(viewportTop) || !Number.isFinite(viewportBottom)) return null;
  if (viewportBottom <= viewportTop) return null;
  let side = getMenuSide(element);
  let triggerRect = getTriggerRect(element);
  let radixAvailable = readRadixAvailableHeight(element);
  let radixMaxHeight = radixAvailable != null ? radixAvailable - MENU_VIEWPORT_PADDING : null;
  let spaceBelow = viewportBottom - rect.top - MENU_VIEWPORT_PADDING;
  let spaceAbove = triggerRect ? triggerRect.top - viewportTop - MENU_VIEWPORT_PADDING : radixMaxHeight;
  let maxHeight;
  if (side == "top") {
    maxHeight = spaceAbove;
  } else if (side == "left" || side == "right") {
    maxHeight = Math.min(spaceBelow, viewportBottom - viewportTop - MENU_VIEWPORT_PADDING * 2);
  } else if (side == "bottom") {
    maxHeight = spaceBelow;
  } else if (spaceAbove != null) {
    maxHeight = Math.min(spaceBelow, spaceAbove);
  } else {
    maxHeight = spaceBelow;
  }
  if (maxHeight == null) return null;
  if (radixMaxHeight != null) {
    maxHeight = Math.min(maxHeight, radixMaxHeight);
  }
  if (!Number.isFinite(maxHeight)) return null;
  return Math.max(0, Math.floor(maxHeight));
};
var addListener = (target, type, listener, options) => {
  try {
    target.addEventListener(type, listener, options);
    return () => {
      try {
        target.removeEventListener(type, listener, options);
      } catch {
      }
    };
  } catch {
    return () => {
    };
  }
};
var attachMenuHeightConstraint = (element) => {
  let frame = 0;
  let detached = false;
  let lastApplied = null;
  let cleanups = [];
  let apply = () => {
    if (detached) return;
    if (!element.isConnected) return;
    let maxHeight = measureMenuMaxHeight(element);
    if (maxHeight == null) return;
    let value = `${maxHeight}px`;
    if (lastApplied == value && element.style.getPropertyValue(MENU_MAX_HEIGHT_VAR) == value) {
      return;
    }
    lastApplied = value;
    element.style.maxHeight = "";
    element.style.setProperty(MENU_MAX_HEIGHT_VAR, value);
  };
  let schedule = () => {
    if (detached || frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      apply();
    });
  };
  apply();
  cleanups.push(addListener(window, "resize", schedule));
  cleanups.push(addListener(window, "orientationchange", schedule));
  cleanups.push(addListener(window, "scroll", schedule, {
    capture: true,
    passive: true
  }));
  cleanups.push(addListener(document, "scroll", schedule, {
    capture: true,
    passive: true
  }));
  try {
    if (window.visualViewport) {
      cleanups.push(addListener(window.visualViewport, "resize", schedule));
      cleanups.push(addListener(window.visualViewport, "scroll", schedule));
    }
  } catch {
  }
  if (typeof ResizeObserver != "undefined") {
    try {
      let resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(element);
      if (element.parentElement) resizeObserver.observe(element.parentElement);
      if (document.documentElement) resizeObserver.observe(document.documentElement);
      cleanups.push(() => resizeObserver.disconnect());
    } catch {
    }
  }
  if (typeof MutationObserver != "undefined") {
    try {
      let mutationObserver = new MutationObserver(schedule);
      mutationObserver.observe(element, {
        attributes: true,
        attributeFilter: ["data-side", "data-align", "data-state"]
      });
      let wrapper = element.parentElement;
      if (wrapper && wrapper != document.body && wrapper != document.documentElement) {
        mutationObserver.observe(wrapper, {
          attributes: true,
          attributeFilter: ["style"]
        });
      }
      cleanups.push(() => mutationObserver.disconnect());
    } catch {
    }
  }
  return {
    apply,
    detach: () => {
      detached = true;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      for (let cleanup of cleanups) cleanup();
      cleanups = [];
      if (lastApplied != null) {
        if (element.style.getPropertyValue(MENU_MAX_HEIGHT_VAR) == lastApplied) {
          element.style.removeProperty(MENU_MAX_HEIGHT_VAR);
        }
        if (element.style.maxHeight) element.style.maxHeight = "";
      }
      lastApplied = null;
    }
  };
};
var useConstrainedMenuHeight = () => {
  let constraintRef = useRef4(null);
  let setRef = useCallback((node) => {
    constraintRef.current?.detach();
    constraintRef.current = null;
    if (node) constraintRef.current = attachMenuHeightConstraint(node);
  }, []);
  useLayoutEffect2(() => {
    constraintRef.current?.apply();
  });
  useLayoutEffect2(() => {
    return () => {
      constraintRef.current?.detach();
      constraintRef.current = null;
    };
  }, []);
  return setRef;
};

// src/menu/index.tsx
var fadeInBottom2 = keyframes4`
  from { opacity: 0; transform: translateY(-10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutBottom2 = keyframes4`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`;
var fadeInTop2 = keyframes4`
  from { opacity: 0; transform: translateY(10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutTop2 = keyframes4`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(10px); }
`;
var fadeInLeft2 = keyframes4`
  from { opacity: 0; transform: translateX(-10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutLeft2 = keyframes4`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-10px); }
`;
var fadeInRight2 = keyframes4`
  from { opacity: 0; transform: translateX(10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutRight2 = keyframes4`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(10px); }
`;
var contentStyles = css`
  display: flex;
  flex-direction: column;
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease;
  padding: 5px;
  color: ${({
  $lightMode
}) => $lightMode ? theme.colors.foreground : theme.colors.background};
  box-shadow: ${theme.shadows.large};
  background: ${({
  $lightMode
}) => $lightMode ? theme.colors.background : theme.colors.foreground};
  border: 1px solid
    ${({
  $lightMode
}) => $lightMode ? theme.colors.gray400 : theme.colors.foreground};
  border-radius: 10px;
  min-width: 200px;
  min-height: 0;
  width: ${({
  $matchTriggerWidth
}) => $matchTriggerWidth ? "var(--radix-dropdown-menu-trigger-width)" : void 0};
  gap: 5px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: min(
    var(${MENU_MAX_HEIGHT_VAR}, 100dvh),
    calc(var(--radix-dropdown-menu-content-available-height, 100dvh) - ${MENU_VIEWPORT_PADDING}px)
  );

  &[data-state='open'][data-side='top'] {
    animation: ${fadeInTop2} 0.2s ease forwards;
  }

  &[data-state='closed'][data-side='top'] {
    animation: ${fadeOutTop2} 0.2s ease forwards;
  }

  &[data-state='open'][data-side='bottom'] {
    animation: ${fadeInBottom2} 0.2s ease forwards;
  }

  &[data-state='closed'][data-side='bottom'] {
    animation: ${fadeOutBottom2} 0.2s ease forwards;
  }

  &[data-state='open'][data-side='left'] {
    animation: ${fadeInLeft2} 0.2s ease forwards;
  }

  &[data-state='closed'][data-side='left'] {
    animation: ${fadeOutLeft2} 0.2s ease forwards;
  }

  &[data-state='open'][data-side='right'] {
    animation: ${fadeInRight2} 0.2s ease forwards;
  }

  &[data-state='closed'][data-side='right'] {
    animation: ${fadeOutRight2} 0.2s ease forwards;
  }
`;
var Content9 = styled8(RadixMenu.Content).withConfig({
  componentId: "sc-12vdhr9-0"
})`${contentStyles}`;
var SubContent2 = styled8(RadixMenu.SubContent).withConfig({
  componentId: "sc-12vdhr9-1"
})`${contentStyles}`;
var ConstrainedContent = (props) => {
  let ref = useConstrainedMenuHeight();
  return /* @__PURE__ */ React15.createElement(Content9, { ...props, ref });
};
var ConstrainedSubContent = (props) => {
  let ref = useConstrainedMenuHeight();
  return /* @__PURE__ */ React15.createElement(SubContent2, { ...props, ref });
};
var Separator2 = styled8(RadixMenu.Separator).withConfig({
  componentId: "sc-12vdhr9-2"
})`height:1px;background:${({
  $lightMode
}) => $lightMode ? theme.colors.gray300 : theme.colors.gray800};`;
var itemStyles = css`
  font-size: 14px;
  font-weight: 500;
  display: flex;
  flex-direction: ${({
  $row
}) => $row ? "row" : "column"};
  align-items: ${({
  $row
}) => $row ? "center" : "stretch"};
  justify-content: ${({
  $row
}) => $row ? "flex-start" : "center"};
  text-align: left;
  gap: 5px;
  padding: 7px 13px;
  border-radius: 6px;
  outline: none;
  border: none;
  background: transparent;
  /* BlackOS: no motion on menu items. */
  transition: none;
  color: ${({
  $lightMode
}) => $lightMode ? theme.colors.foreground : theme.colors.background};
  max-width: ${({
  $matchTriggerWidth
}) => $matchTriggerWidth ? "unset" : "250px"};

  &:not(:disabled) {
    &:hover {
      background: ${({
  $lightMode
}) => $lightMode ? theme.colors.gray300 : theme.colors.gray800};
    }

    &:focus {
      background: ${({
  $lightMode
}) => $lightMode ? theme.colors.gray300 : theme.colors.gray800};
    }
  }

  &:disabled {
    opacity: 0.7;
  }

  h1 {
    font-size: 14px;
    font-weight: 500;
    color: ${({
  $lightMode
}) => $lightMode ? theme.colors.foreground : theme.colors.background};
  }

  p {
    font-size: 12px;
    color: ${theme.colors.gray600};
  }
`;
var Item4 = styled8(RadixMenu.Item).withConfig({
  componentId: "sc-12vdhr9-3"
})`${itemStyles}`;
var SubTrigger2 = styled8(RadixMenu.SubTrigger).withConfig({
  componentId: "sc-12vdhr9-4"
})`${itemStyles} cursor:default;user-select:none;&:not([data-disabled]){&[data-highlighted],&[data-state='open']{background:${({
  $lightMode
}) => $lightMode ? theme.colors.gray300 : theme.colors.gray800};}}&[data-disabled]{opacity:0.7;}`;
var ItemIcon = styled8.span.withConfig({
  componentId: "sc-12vdhr9-5"
})`display:flex;align-items:center;justify-content:center;flex-shrink:0;height:16px;width:16px;svg{height:16px;width:16px;}`;
var ItemCopy = styled8.span.withConfig({
  componentId: "sc-12vdhr9-6"
})`display:flex;flex-direction:column;justify-content:center;gap:5px;min-width:0;flex:1;`;
var ItemChevron = styled8.span.withConfig({
  componentId: "sc-12vdhr9-7"
})`display:flex;align-items:center;margin-left:auto;flex-shrink:0;opacity:0.4;svg{height:16px;width:16px;}`;
var Title4 = styled8(RadixMenu.Label).withConfig({
  componentId: "sc-12vdhr9-8"
})`font-size:14px;font-weight:600;color:${({
  $lightMode
}) => $lightMode ? theme.colors.gray800 : theme.colors.gray100};padding:7px 13px;border-radius:6px;background:transparent;transition:none;display:flex;align-items:center;gap:5px;`;
var Menu = React15.forwardRef(({
  children,
  label,
  onItemClick,
  items,
  title,
  setIsOpen,
  matchTriggerWidth,
  lightMode,
  ...triggerProps
}, ref) => {
  let [open, setOpen] = useState9(false);
  let zIndex = useDialogZIndex(open);
  useEffect6(() => setIsOpen?.(open), [open]);
  useSuppressTooltipWhileOpen(open);
  let renderItems = (items2) => items2.map((item, i) => {
    if (item.type === "separator") return /* @__PURE__ */ React15.createElement(Separator2, { key: i, $lightMode: lightMode });
    if (item.type === "submenu") {
      return /* @__PURE__ */ React15.createElement(RadixMenu.Sub, { key: i }, /* @__PURE__ */ React15.createElement(SubTrigger2, { $lightMode: lightMode, $matchTriggerWidth: matchTriggerWidth, $row: true, disabled: item.disabled }, item.icon && /* @__PURE__ */ React15.createElement(ItemIcon, null, item.icon), /* @__PURE__ */ React15.createElement(ItemCopy, null, /* @__PURE__ */ React15.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React15.createElement("p", null, item.description)), /* @__PURE__ */ React15.createElement(ItemChevron, null, /* @__PURE__ */ React15.createElement(RiArrowRightSLine, null))), /* @__PURE__ */ React15.createElement(RadixMenu.Portal, null, /* @__PURE__ */ React15.createElement(ConstrainedSubContent, { $lightMode: lightMode, "data-metorial-menu-content": "true", sideOffset: 2, alignOffset: -5, style: {
        zIndex: zIndex + 1
      } }, renderItems(item.items))));
    }
    return /* @__PURE__ */ React15.createElement(Item4, { key: i, $lightMode: lightMode, $matchTriggerWidth: matchTriggerWidth, $row: !!item.icon, onClick: () => {
      item.onClick?.();
      if (item.id != null) onItemClick?.(item.id);
    }, disabled: item.disabled, asChild: true }, /* @__PURE__ */ React15.createElement("button", { type: "button", disabled: item.disabled }, item.icon ? /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement(ItemIcon, null, item.icon), /* @__PURE__ */ React15.createElement(ItemCopy, null, /* @__PURE__ */ React15.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React15.createElement("p", null, item.description))) : /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React15.createElement("p", null, item.description))));
  });
  return /* @__PURE__ */ React15.createElement(RadixMenu.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React15.createElement(OverlayOpenProvider, { value: open }, /* @__PURE__ */ React15.createElement(RadixMenu.Trigger, { "aria-label": label, ...triggerProps, asChild: true, ref }, children)), /* @__PURE__ */ React15.createElement(RadixMenu.Portal, null, /* @__PURE__ */ React15.createElement(ConstrainedContent, { $lightMode: lightMode, $matchTriggerWidth: matchTriggerWidth, "data-metorial-menu-content": "true", sideOffset: 5, style: {
    zIndex
  }, onPointerDownOutside: markOverlayPointerDismiss }, title && /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement(Title4, { $lightMode: lightMode }, title), /* @__PURE__ */ React15.createElement(Separator2, { $lightMode: lightMode })), renderItems(items))));
});

// src/skeleton/index.tsx
import React16 from "react";
import { css as css2, keyframes as keyframes5, styled as styled9 } from "styled-components";
var skeletonSwipe = keyframes5`
  0% {
    transform: translateX(-140%) skewX(-12deg);
  }

  60%, 100% {
    transform: translateX(330%) skewX(-12deg);
  }
`;
var SkeletonRoot = styled9.span.withConfig({
  componentId: "sc-1l47hsj-0"
})`position:relative;display:inline-grid;max-width:100%;border-radius:${({
  $borderRadius
}) => typeof $borderRadius == "number" ? `${$borderRadius}px` : $borderRadius};vertical-align:middle;`;
var SkeletonContent = styled9.span.withConfig({
  componentId: "sc-1l47hsj-1"
})`grid-area:1 / 1;display:inline-grid;min-width:0;opacity:${({
  $active
}) => $active ? 0 : 1};pointer-events:${({
  $active
}) => $active ? "none" : "auto"};transition:opacity 100ms ease ${({
  $active
}) => $active ? "0ms" : "35ms"};`;
var SkeletonSurface = styled9.span.withConfig({
  componentId: "sc-1l47hsj-2"
})`position:relative;grid-area:1 / 1;min-width:0;overflow:hidden;border-radius:inherit;background:${theme.colors.gray300};opacity:${({
  $active
}) => $active ? 1 : 0};pointer-events:none;transition:opacity 140ms ease-out;&::after{content:'';position:absolute;top:0;bottom:0;left:0;width:45%;border-radius:inherit;background:rgba(255,255,255,0.65);box-shadow:0 0 12px rgba(255,255,255,0.45);animation:${skeletonSwipe} 1.35s ease-in-out infinite;${({
  $active
}) => !$active && css2`
        animation-play-state: paused;
      `}}@media (prefers-reduced-motion:reduce){&::after{animation:none;}}`;
var Skeleton = ({
  active = false,
  borderRadius = 6,
  children,
  className,
  style
}) => /* @__PURE__ */ React16.createElement(SkeletonRoot, { className, style, $borderRadius: borderRadius, "aria-busy": active || void 0 }, /* @__PURE__ */ React16.createElement(SkeletonContent, { $active: active, "aria-hidden": active, inert: active || void 0 }, children), /* @__PURE__ */ React16.createElement(SkeletonSurface, { $active: active, "aria-hidden": "true" }));

// src/spinner/index.tsx
import React17 from "react";
import { keyframes as keyframes6, styled as styled10 } from "styled-components";
var spinnerTheme = createTheme({
  duration: createThemeToken(),
  size: createThemeToken(),
  foreground: createThemeToken(),
  background: createThemeToken()
});
var rotate = keyframes6`
  0% { transform: rotate(0deg) }
  100% { transform: rotate(360deg) }
`;
var Wrapper2 = styled10("div").withConfig({
  componentId: "sc-xlc9nt-0"
})`display:inline-block;animation:${rotate} ${spinnerTheme.duration} ease infinite;border:calc(${spinnerTheme.size} / 7) solid ${spinnerTheme.background};border-top-color:${spinnerTheme.foreground};border-radius:50%;width:${spinnerTheme.size};height:${spinnerTheme.size};`;
var Spinner = ({
  size,
  foreground,
  background,
  duration,
  center
}) => {
  if (typeof size == "number") size = `${size}px`;
  return /* @__PURE__ */ React17.createElement(Wrapper2, { "aria-live": "polite", title: "Loading...", style: {
    ...spinnerTheme.setRootStyles({
      duration: duration ?? "900ms",
      size: (size ?? "26px").toString(),
      foreground: foreground ?? theme.colors.black500,
      background: background ?? theme.colors.gray300
    }),
    margin: center ? "0px auto" : void 0
  } });
};
var CenteredSpinner = (d) => /* @__PURE__ */ React17.createElement("div", { style: {
  display: "flex",
  justifyContent: "center"
} }, /* @__PURE__ */ React17.createElement(Spinner, { ...d, center: true }));

// src/button/constants.ts
import { memo as memo3 } from "@lowerdeck/memo";

// src/theme/gradient.ts
var linearGradient = (angle, ...colors2) => {
  return `linear-gradient(${angle}deg, ${colors2.join(", ")})`;
};

// src/button/constants.ts
var createSolidButtonStates = (state) => ({
  passive: {
    ...state,
    shadow: theme.shadows.small
  },
  active: {
    ...state,
    shadow: theme.shadows.medium
  }
});
var colorTypes = ["gray", "blue", "pink", "cyan", "indigo", "iris", "green", "orange", "purple", "red", "violet", "yellow", "white", "black"];
var colorFacets = ["100", "200", "300", "400", "500", "600", "700", "800", "900"];
var isColorType = (color) => colorTypes.includes(color);
var isColorKey = (color) => color in colors;
var parseColorKey = (color) => {
  for (let facet of colorFacets) {
    if (color.endsWith(facet)) {
      let colorName = color.slice(0, -facet.length);
      return {
        colorName,
        facet
      };
    }
  }
  return null;
};
var getColorKeyByFacet = (parsed, facet) => {
  if (!parsed) return null;
  let colorKey = `${parsed.colorName}${facet}`;
  return colorKey in colors ? colorKey : null;
};
var getThemeColorByFacet = (parsed, facet, fallback) => {
  let colorKey = getColorKeyByFacet(parsed, facet);
  return colorKey ? theme.colors[colorKey] : fallback;
};
var getShiftedThemeColor = (parsed, offset, fallback) => {
  if (!parsed) return fallback;
  let index = colorFacets.indexOf(parsed.facet);
  let shiftedFacet = colorFacets[Math.min(Math.max(index + offset, 0), colorFacets.length - 1)];
  return getThemeColorByFacet(parsed, shiftedFacet, fallback);
};
var coloredButtonVariants = memo3((color) => ({
  solid: {
    ...createSolidButtonStates({
      color: getForegroundColor(getColorKey(color, "900")),
      background: theme.colors[getColorKey(color, "700")],
      border: theme.colors[getColorKey(color, "700")]
    }),
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors[getColorKey(color, "400")]
    }
  },
  outline: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors.background,
      border: theme.colors[getColorKey(color, "600")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors.background,
      border: theme.colors[getColorKey(color, "700")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    }
  },
  soft: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "100")],
      border: theme.colors[getColorKey(color, "100")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "200")],
      border: theme.colors[getColorKey(color, "200")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    }
  },
  ghost: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "900")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    }
  }
}));
var monoChromeButtonVariants = {
  solid: {
    ...createSolidButtonStates({
      color: theme.colors.background,
      background: linearGradient(0, theme.colors.gray800, theme.colors.gray900),
      border: "transparent"
    }),
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors.gray700
    }
  },
  outline: {
    passive: {
      color: theme.colors.foreground,
      background: theme.colors.background,
      border: theme.colors.gray400,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: theme.colors.background,
      border: theme.colors.gray500,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray100
    }
  },
  soft: {
    passive: {
      color: theme.colors.foreground,
      background: theme.colors.gray200,
      border: theme.colors.gray100,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: theme.colors.gray300,
      border: theme.colors.gray200,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    }
  },
  ghost: {
    passive: {
      color: theme.colors.gray600,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors.gray700,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    }
  }
};
var concreteButtonVariants = memo3((color) => {
  let parsed = parseColorKey(color);
  let baseColor = theme.colors[color];
  let softBackground = getThemeColorByFacet(parsed, "100", monoChromeButtonVariants.soft.passive.background);
  let softActiveBackground = getThemeColorByFacet(parsed, "200", monoChromeButtonVariants.soft.active.background);
  let spinnerBackground = getThemeColorByFacet(parsed, "200", monoChromeButtonVariants.soft.spinner.background);
  return {
    solid: {
      ...createSolidButtonStates({
        color: getForegroundColor(color),
        background: baseColor,
        border: baseColor
      }),
      spinner: {
        foreground: getForegroundColor(color),
        background: getShiftedThemeColor(parsed, -3, baseColor)
      }
    },
    outline: {
      passive: {
        color: baseColor,
        background: theme.colors.background,
        border: baseColor,
        shadow: "none"
      },
      active: {
        color: baseColor,
        background: theme.colors.background,
        border: baseColor,
        shadow: "none"
      },
      spinner: {
        foreground: baseColor,
        background: spinnerBackground
      }
    },
    soft: {
      passive: {
        color: baseColor,
        background: softBackground,
        border: softBackground,
        shadow: "none"
      },
      active: {
        color: baseColor,
        background: softActiveBackground,
        border: softActiveBackground,
        shadow: "none"
      },
      spinner: {
        foreground: baseColor,
        background: spinnerBackground
      }
    },
    ghost: {
      passive: {
        color: baseColor,
        background: "transparent",
        border: "transparent",
        shadow: "none"
      },
      active: {
        color: baseColor,
        background: "transparent",
        border: "transparent",
        shadow: "none"
      },
      spinner: {
        foreground: baseColor,
        background: spinnerBackground
      }
    }
  };
});
var sizes2 = {
  "1": {
    gap: "6px",
    height: "26px",
    padding: "0 8px",
    borderRadius: "6px",
    fontSize: "12px"
  },
  "2": {
    gap: "8px",
    height: "30px",
    padding: "0 10px",
    borderRadius: "8px",
    fontSize: "14px"
  },
  "3": {
    gap: "10px",
    height: "36px",
    padding: "0 12px",
    borderRadius: "10px",
    fontSize: "16px"
  },
  "4": {
    gap: "12px",
    height: "42px",
    padding: "0 14px",
    borderRadius: "12px",
    fontSize: "18px"
  },
  "5": {
    gap: "14px",
    height: "48px",
    padding: "0 16px",
    borderRadius: "14px",
    fontSize: "20px"
  }
};
var buttonTheme = createTheme({
  passive_color: createThemeToken(),
  passive_background: createThemeToken(),
  passive_border: createThemeToken(),
  passive_shadow: createThemeToken(),
  active_color: createThemeToken(),
  active_background: createThemeToken(),
  active_border: createThemeToken(),
  active_shadow: createThemeToken(),
  spinner_foreground: createThemeToken(),
  spinner_background: createThemeToken()
});
var getColorButtonTheme = memo3((color, variant, shadow = true) => {
  let v = (color ? isColorType(color) ? coloredButtonVariants(color) : isColorKey(color) ? concreteButtonVariants(color) : monoChromeButtonVariants : monoChromeButtonVariants)[variant];
  return buttonTheme.setRootStyles({
    passive_color: v.passive.color,
    passive_background: v.passive.background,
    passive_border: v.passive.border,
    passive_shadow: shadow ? v.passive.shadow : "none",
    active_color: v.active.color,
    active_background: v.active.background,
    active_border: v.active.border,
    active_shadow: shadow ? v.active.shadow : "none",
    spinner_foreground: v.spinner.foreground,
    spinner_background: v.spinner.background
  });
});
var getButtonStyles = (props) => {
  let size = sizes2[props.size ?? "3"];
  if (props.variant == "ghost") {
    size = {
      ...size,
      padding: "0",
      height: "auto"
    };
  }
  return {
    margin: 0,
    fontFamily: "inherit",
    ...size,
    ...getColorButtonTheme(props.color, props.variant ?? "solid", props.shadow)
  };
};
var getButtonSize = (size) => sizes2[size];

// src/button/linkButton.ts
import { styled as styled11 } from "styled-components";
var LinkButton = styled11("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-16wmsg7-0"
})`background:none;border:none;cursor:pointer;font-size:inherit;font-weight:inherit;color:inherit;padding:0;text-decoration:underline;transition:none;line-height:inherit;`;

// src/button/index.tsx
var useDelayed = _useDelayed;
if (useDelayed.default) useDelayed = useDelayed.default;
var Wrapper3 = styled12("button").withConfig({
  componentId: "sc-1gmpd00-0"
})`display:inline-flex;align-items:center;flex-shrink:0;position:relative;cursor:pointer;color:${buttonTheme.passive_color} !important;background:${buttonTheme.passive_background};border:solid 1px ${buttonTheme.passive_border};box-shadow:${buttonTheme.passive_shadow};user-select:none;transition:none;&:not(:disabled):hover,&:not(:disabled):focus{color:${buttonTheme.active_color} !important;background:${buttonTheme.active_background};border:solid 1px ${buttonTheme.active_border};box-shadow:${buttonTheme.active_shadow};}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var SplitButton = styled12("div").withConfig({
  componentId: "sc-1gmpd00-1"
})`display:inline-flex;gap:1px;align-items:stretch;`;
var Overlay3 = styled12("div").withConfig({
  componentId: "sc-1gmpd00-2"
})`position:absolute;display:flex;align-items:center;justify-content:center;border-radius:inherit;top:0;left:0;right:0;bottom:0;`;
var OverlayInner = styled12("div").withConfig({
  componentId: "sc-1gmpd00-3"
})`display:flex;align-items:center;justify-content:center;`;
var Inner2 = styled12("div").withConfig({
  componentId: "sc-1gmpd00-4"
})`display:inline-flex;align-items:center;justify-content:center;flex-grow:1;flex-shrink:0;gap:6px;`;
var Icon = styled12("span").withConfig({
  componentId: "sc-1gmpd00-5"
})`display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;svg{height:16px;width:16px;}`;
var Content10 = styled12("span").withConfig({
  componentId: "sc-1gmpd00-6"
})`display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:500;line-height:1.5;margin-block:-0.25em;`;
var Button = React18.forwardRef(({
  children,
  iconLeft,
  iconRight,
  loading,
  success,
  skeleton,
  fullWidth,
  variant,
  color,
  size,
  shadow,
  onHover,
  menu,
  as = "button",
  ...props
}, ref) => {
  let [isHovered, setIsHovered] = useState10(false);
  useEffect7(() => onHover?.(), [isHovered]);
  let isMouseOverRef = useRef5(false);
  let style = getButtonStyles({
    color,
    variant,
    size,
    shadow
  });
  let loadingVisible = useDelayed(loading, 200, [false]);
  let successVisible = useDelayed(success, 200, [false]);
  let contentVisible = useDelayed(!loading && !success, 200);
  let hasMenu = Boolean(menu?.length);
  let buttonStyle = {
    ...style,
    ...props.style,
    width: fullWidth && !hasMenu ? "100%" : void 0,
    ...!children && {
      width: style.height,
      padding: 0
    },
    ...hasMenu && {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      flexGrow: fullWidth ? 1 : void 0
    }
  };
  let button = /* @__PURE__ */ React18.createElement(Wrapper3, { as, ref, ...props, style: buttonStyle, disabled: props.disabled || loading || skeleton, onClick: (e) => {
    if (loading || success) e.preventDefault();
    else props.onClick?.(e);
  }, onMouseEnter: () => {
    isMouseOverRef.current = true;
    setTimeout(() => {
      if (isMouseOverRef.current) setIsHovered(true);
    }, 100);
  }, onMouseLeave: () => {
    isMouseOverRef.current = false;
    setIsHovered(false);
  } }, loadingVisible && !successVisible && /* @__PURE__ */ React18.createElement(Overlay3, { key: "loading", style: {
    backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
  } }, /* @__PURE__ */ React18.createElement(OverlayInner, null, /* @__PURE__ */ React18.createElement(Spinner, { size: 16, foreground: buttonTheme.spinner_foreground, background: buttonTheme.spinner_background }))), successVisible && /* @__PURE__ */ React18.createElement(Overlay3, { key: "success", style: {
    backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
  } }, /* @__PURE__ */ React18.createElement(OverlayInner, null, /* @__PURE__ */ React18.createElement(Icon, null, /* @__PURE__ */ React18.createElement(Check, { size: 16, strokeWidth: 3 })))), /* @__PURE__ */ React18.createElement(Inner2, { style: {
    opacity: contentVisible ? 1 : 0,
    textAlign: fullWidth || !children ? "center" : "left",
    justifyContent: fullWidth || !children ? "center" : "flex-start"
  }, "aria-hidden": loading || success }, iconLeft && /* @__PURE__ */ React18.createElement(Icon, null, iconLeft), children && /* @__PURE__ */ React18.createElement(Content10, null, children), iconRight && /* @__PURE__ */ React18.createElement(Icon, null, iconRight)));
  if (!hasMenu) {
    if (skeleton === void 0) return button;
    return /* @__PURE__ */ React18.createElement(Skeleton, { active: skeleton }, button);
  }
  let splitButton = /* @__PURE__ */ React18.createElement(SplitButton, { style: {
    width: fullWidth ? "100%" : void 0
  } }, button, /* @__PURE__ */ React18.createElement(Menu, { items: menu, label: "More actions" }, /* @__PURE__ */ React18.createElement(Wrapper3, { type: "button", "aria-label": "More actions", disabled: props.disabled || loading || success || skeleton, style: {
    ...style,
    width: style.height,
    padding: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
  } }, /* @__PURE__ */ React18.createElement(Icon, null, /* @__PURE__ */ React18.createElement(ChevronDown, null)))));
  if (skeleton === void 0) return splitButton;
  return /* @__PURE__ */ React18.createElement(Skeleton, { active: skeleton }, splitButton);
});

// src/callout/index.tsx
import { RiAlertLine } from "@remixicon/react";
import React19 from "react";
import { styled as styled13 } from "styled-components";

// src/callout/constants.ts
import { memo as memo4 } from "@lowerdeck/memo";
var calloutColor = memo4((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes3 = {
  "1": {
    padding: "12px 16px",
    fontSize: 12,
    borderRadius: 10,
    gap: 10,
    icon: {
      size: 16
    }
  },
  "2": {
    padding: "16px 24px",
    fontSize: 14,
    borderRadius: 12,
    gap: 14,
    icon: {
      size: 18
    }
  },
  "3": {
    padding: "20px 32px",
    fontSize: 16,
    borderRadius: 14,
    gap: 18,
    icon: {
      size: 20
    }
  }
};
var getCalloutStyles = ({
  color,
  size = "2",
  variant = "default"
}) => ({
  ...calloutColor(color),
  ...sizes3[size],
  ...variant === "inline" && {
    color: `color-mix(in srgb, ${calloutColor(color).color} 80%, ${theme.colors.black900})`,
    padding: 0,
    backgroundColor: "transparent",
    fontWeight: 600
  }
});

// src/callout/index.tsx
var Wrapper4 = styled13("div").withConfig({
  componentId: "sc-56dyzz-0"
})`display:flex;align-items:center;`;
var Icon2 = styled13("div").withConfig({
  componentId: "sc-56dyzz-1"
})`display:flex;align-items:center;justify-content:center;svg{height:100%;aspect-ratio:1;}`;
var Content11 = styled13("div").withConfig({
  componentId: "sc-56dyzz-2"
})`display:flex;align-items:center;flex-grow:1;`;
var ContentInner = styled13("p").withConfig({
  componentId: "sc-56dyzz-3"
})`flex-grow:1;width:100%;`;
var Callout = ({
  children,
  color,
  size,
  variant,
  style
}) => {
  let {
    icon,
    ...styles
  } = getCalloutStyles({
    color,
    size,
    variant
  });
  return /* @__PURE__ */ React19.createElement(Wrapper4, { style: {
    ...styles,
    ...style,
    display: "flex",
    alignItems: "center"
  } }, /* @__PURE__ */ React19.createElement(Icon2, { style: {
    width: icon.size,
    height: icon.size
  } }, /* @__PURE__ */ React19.createElement(RiAlertLine, null)), /* @__PURE__ */ React19.createElement(Content11, null, /* @__PURE__ */ React19.createElement(ContentInner, null, children)));
};

// src/cardLinks/index.tsx
import { RiArrowRightSLine as RiArrowRightSLine2 } from "@remixicon/react";
import React21 from "react";
import { styled as styled14 } from "styled-components";

// src/link.tsx
import React20 from "react";
var defaultLink = ({
  to,
  children,
  className,
  style
}) => {
  return /* @__PURE__ */ React20.createElement("a", { href: to, className, style }, children);
};
var getLink = () => {
  let linkFromWindow = typeof window !== "undefined" ? window.LinkComponent : void 0;
  let linkFromGlobal = typeof global !== "undefined" ? global.LinkComponent : void 0;
  return linkFromWindow ?? linkFromGlobal ?? defaultLink;
};

// src/cardLinks/index.tsx
var LinkWrapper = styled14("span").withConfig({
  componentId: "sc-3fd4jm-0"
})`padding:20px;border-radius:10px;border:1px solid ${theme.colors.gray400};box-shadow:${theme.shadows.small};display:flex;gap:10px;transition:none;flex-grow:1;cursor:pointer;background:${theme.colors.background};&:hover,&:focus{border-color:${theme.colors.gray500} !important;}`;
var LinkContent = styled14("div").withConfig({
  componentId: "sc-3fd4jm-1"
})`display:flex;flex-direction:column;gap:4px;flex-grow:1;`;
var LinkTitle = styled14("p").withConfig({
  componentId: "sc-3fd4jm-2"
})`font-size:15px;font-weight:600;color:${theme.colors.foreground};`;
var LinkDescription = styled14("p").withConfig({
  componentId: "sc-3fd4jm-3"
})`font-size:12px;font-weight:500;color:${theme.colors.gray600};`;
var Icon3 = styled14("div").withConfig({
  componentId: "sc-3fd4jm-4"
})`display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${theme.colors.gray600};svg{height:20px;width:20px;}`;
var CardLinks = {
  Items: styled14("div").withConfig({
    componentId: "sc-3fd4jm-5"
  })`display:grid;display:flex;flex-direction:column;gap:10px;`,
  Group: styled14("div").withConfig({
    componentId: "sc-3fd4jm-6"
  })`display:flex;flex-direction:column;`,
  GroupPanel: styled14("div").withConfig({
    componentId: "sc-3fd4jm-7"
  })`display:flex;flex-direction:column;padding:13px 10px 10px 10px;border-radius:10px;background:${theme.colors.gray200};box-shadow:${theme.shadows.small};.link{border:solid 1px transparent;}& > h2{font-weight:700 !important;padding:0 4px;}& > p{font-weight:600;color:${theme.colors.gray700};padding:0 4px;}`,
  GroupTitle: styled14("h2").withConfig({
    componentId: "sc-3fd4jm-8"
  })`font-size:18px;font-weight:600;margin-bottom:10px;`,
  GroupPreTitle: styled14("p").withConfig({
    componentId: "sc-3fd4jm-9"
  })`font-size:14px;font-weight:600;margin-bottom:4px;color:${theme.colors.gray700};`,
  Link: ({
    to,
    title,
    description
  }) => {
    let Link = getLink();
    return /* @__PURE__ */ React21.createElement(Link, { to, style: {
      textDecoration: "none",
      color: "inherit",
      display: "flex",
      flexDirection: "column"
    } }, /* @__PURE__ */ React21.createElement(LinkWrapper, { className: "link" }, /* @__PURE__ */ React21.createElement(LinkContent, null, /* @__PURE__ */ React21.createElement(LinkTitle, null, title), /* @__PURE__ */ React21.createElement(LinkDescription, null, description)), /* @__PURE__ */ React21.createElement(Icon3, null, /* @__PURE__ */ React21.createElement(RiArrowRightSLine2, null))));
  }
};

// src/checkbox/index.tsx
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as VisuallyHidden2 from "@radix-ui/react-visually-hidden";
import clsx from "clsx";
import React24, { useId as useId2 } from "react";
import { Check as Check2 } from "react-feather";
import { keyframes as keyframes7, styled as styled17 } from "styled-components";

// src/input/index.tsx
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import React23, { useId } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { css as css3, styled as styled16 } from "styled-components";

// src/error/index.tsx
import { RiErrorWarningLine } from "@remixicon/react";
import React22 from "react";
import { styled as styled15 } from "styled-components";

// src/theme/calc.ts
var calc = {
  add: (a, b) => `calc(${a} + ${b})`,
  subtract: (a, b) => `calc(${a} - ${b})`,
  multiply: (a, b) => `calc(${a} * ${b})`,
  divide: (a, b) => `calc(${a} / ${b})`
};

// src/error/index.tsx
var Wrapper5 = styled15("p").withConfig({
  componentId: "sc-1n1or6r-0"
})`display:flex;gap:7px;align-items:center;color:${theme.colors.red900};font-weight:500;`;
var Error = ({
  size,
  children,
  style
}) => {
  let sizeString = (typeof size == "number" ? `${size}px` : size) || "14px";
  let inner = children;
  if (typeof children == "string" && children.startsWith("[") && children.includes("]")) {
    let endIndex = children.indexOf("]");
    let str = children.substring(endIndex + 1);
    if (str.startsWith(":")) {
      str = str.substring(1);
    }
    inner = str.trim();
  }
  return /* @__PURE__ */ React22.createElement(Wrapper5, { style: {
    fontSize: sizeString,
    ...style
  } }, /* @__PURE__ */ React22.createElement("span", { style: {
    display: "inline-flex"
  } }, /* @__PURE__ */ React22.createElement(RiErrorWarningLine, { size: calc.multiply(sizeString, 1.3) })), /* @__PURE__ */ React22.createElement("span", null, inner));
};

// src/input/index.tsx
var Wrapper6 = styled16("div").withConfig({
  componentId: "sc-1b88xuj-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var InputLabel = styled16("label").withConfig({
  componentId: "sc-1b88xuj-1"
})`font-size:12px;color:${theme.colors.gray900};margin-bottom:5px;font-weight:500;user-select:none;white-space:nowrap;`;
var InputDescription = styled16("p").withConfig({
  componentId: "sc-1b88xuj-2"
})`font-size:12px;color:${theme.colors.gray600};margin-bottom:8px;user-select:none;`;
var LabelRow = styled16("div").withConfig({
  componentId: "sc-1b88xuj-3"
})`display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;width:100%;min-width:0;margin-bottom:5px;${InputLabel}{display:block;min-width:0;overflow:hidden;text-overflow:ellipsis;margin-bottom:0;}`;
var LabelHelp = styled16("div").withConfig({
  componentId: "sc-1b88xuj-4"
})`display:flex;align-items:center;justify-content:flex-end;min-width:0;overflow:hidden;text-overflow:ellipsis;`;
var inputSurfaceStyles = css3`
  outline: 1px solid transparent;
  background: ${theme.colors.gray300};
  color: ${theme.colors.foreground};
  display: flex;
  width: 100%;
  transition: all 0.3s ease;
  gap: 8px;
  align-items: center;

  &:focus-within {
    background: ${theme.colors.gray400};
    outline: 1px solid ${theme.colors.gray600};
  }
`;
var inputFieldStyles = css3`
  flex-grow: 1;
  border: none;
  background: transparent;
  height: 100%;
  outline: none;

  &::placeholder {
    color: ${theme.colors.gray700};
  }
`;
var InputWrapper = styled16("div").withConfig({
  componentId: "sc-1b88xuj-5"
})`${inputSurfaceStyles}`;
var InputField = styled16("input").withConfig({
  componentId: "sc-1b88xuj-6"
})`${inputFieldStyles} &[type='number']{-moz-appearance:textfield;}&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}`;
var TextareaField = styled16(TextareaAutosize).withConfig({
  componentId: "sc-1b88xuj-7"
})`flex-grow:1;border:none;background:transparent;height:100%;width:100%;outline:none;resize:none;&::placeholder{color:${theme.colors.gray700};}`;
var ColorSwatchInput = styled16("input").withConfig({
  componentId: "sc-1b88xuj-8"
})`appearance:none;-webkit-appearance:none;-moz-appearance:none;border:none;background:none;cursor:pointer;padding:0px;border-radius:5px;&::-webkit-color-swatch-wrapper{padding:0;}&::-webkit-color-swatch{border:none;border-radius:5px;}`;
var InputHeader = ({
  id,
  label,
  hideLabel,
  help,
  description
}) => /* @__PURE__ */ React23.createElement(React23.Fragment, null, hideLabel ? /* @__PURE__ */ React23.createElement(VisuallyHidden, null, /* @__PURE__ */ React23.createElement(InputLabel, { htmlFor: id }, label)) : /* @__PURE__ */ React23.createElement(LabelRow, null, /* @__PURE__ */ React23.createElement(InputLabel, { htmlFor: id }, label), help && /* @__PURE__ */ React23.createElement(LabelHelp, null, help)), description && /* @__PURE__ */ React23.createElement(InputDescription, null, description));
var Input = ({
  style,
  size = "3",
  label,
  description,
  help,
  hideLabel,
  error,
  as = "input",
  minRows,
  maxRows,
  onInput,
  type = "text",
  ...props
}) => {
  let sizeStyles = getButtonSize(size);
  let id = useId();
  return /* @__PURE__ */ React23.createElement(Wrapper6, { style }, /* @__PURE__ */ React23.createElement(InputHeader, { id, label, hideLabel, help, description }), /* @__PURE__ */ React23.createElement(InputWrapper, { style: {
    height: as == "textarea" ? void 0 : sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    outline: error ? `1px solid ${theme.colors.red900}` : void 0,
    paddingBlock: as == "textarea" ? calc.divide(sizeStyles.height, 3.7) : void 0
  } }, type == "color" && /* @__PURE__ */ React23.createElement("div", { style: {
    paddingLeft: sizeStyles.padding
  } }, /* @__PURE__ */ React23.createElement(ColorSwatchInput, { type: "color", onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, value: props.value, style: {
    height: `calc(${sizeStyles.height} * 0.6)`,
    width: `calc(${sizeStyles.height} * 0.6)`
  } })), as == "textarea" ? /* @__PURE__ */ React23.createElement(TextareaField, { id, minRows, maxRows, style: {
    padding: sizeStyles.padding,
    fontSize: "14px",
    height: style?.height
  }, onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, type: type == "color" ? "text" : type, ...props }) : /* @__PURE__ */ React23.createElement(InputField, { id, style: {
    padding: sizeStyles.padding,
    height: sizeStyles.height,
    fontSize: "14px"
  }, onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, type: type == "color" ? "text" : type, ...props })), typeof error == "string" && /* @__PURE__ */ React23.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};

// src/checkbox/index.tsx
var pendingPulse = keyframes7`
  50% {
    background: ${theme.colors.gray500};
  }
`;
var Wrapper7 = styled17("div").withConfig({
  componentId: "sc-sme67s-0"
})`display:flex;align-items:center;cursor:pointer;width:100%;`;
var Root7 = styled17(RadixCheckbox.Root).withConfig({
  componentId: "sc-sme67s-1"
})`display:flex;height:20px;width:20px;border:none;padding:0px;justify-content:center;align-items:center;background:${theme.colors.gray100};border:1px solid ${theme.colors.gray300};border-radius:5px;color:${theme.colors.gray100};transition:none;position:relative;overflow:hidden;flex-shrink:0;&.disabled{opacity:0.5;}&.readonly{opacity:1;}&::before{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:0px;height:0px;content:'';border-radius:50%;background:${theme.colors.gray800};transition:none;}&[data-state='checked']{box-shadow:${theme.shadows.small};&::before{width:30px;height:30px;}}&.pending::before{background:${theme.colors.gray550};animation:${pendingPulse} 1.4s ease-in-out infinite;}`;
var Indicator2 = styled17(RadixCheckbox.Indicator).withConfig({
  componentId: "sc-sme67s-2"
})`display:flex;justify-content:center;align-items:center;&[data-state='checked']{transform:rotate(-10deg);}`;
var Checkbox = ({
  checked,
  onCheckedChange,
  description,
  label,
  disabled,
  hideLabel,
  readOnly,
  pending
}) => {
  let id = useId2();
  return /* @__PURE__ */ React24.createElement(React24.Fragment, null, /* @__PURE__ */ React24.createElement(Wrapper7, { style: {
    cursor: readOnly ? "default" : void 0
  } }, /* @__PURE__ */ React24.createElement(Root7, { className: clsx({
    disabled,
    readonly: readOnly,
    pending
  }), id, checked, onCheckedChange: readOnly ? void 0 : (state) => onCheckedChange?.(state === true), disabled: disabled || readOnly }, /* @__PURE__ */ React24.createElement(Indicator2, null, /* @__PURE__ */ React24.createElement(Check2, { size: 12, strokeWidth: 3 }))), !hideLabel ? /* @__PURE__ */ React24.createElement(InputLabel, { htmlFor: id, style: {
    margin: 0,
    width: "100%",
    minHeight: 20,
    paddingLeft: 10,
    display: "flex",
    alignItems: "center",
    cursor: readOnly ? "default" : "pointer"
  } }, label) : /* @__PURE__ */ React24.createElement(VisuallyHidden2.Root, null, /* @__PURE__ */ React24.createElement("label", { htmlFor: id }, label))), description && /* @__PURE__ */ React24.createElement(InputDescription, { style: {
    marginTop: 5,
    marginBottom: 0
  } }, description));
};

// src/codeInput/index.tsx
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import React25 from "react";
import { css as css4, keyframes as keyframes8, styled as styled18 } from "styled-components";
var shake = keyframes8`
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
`;
var Wrapper8 = styled18.div.withConfig({
  componentId: "sc-e9wtgd-0"
})`display:flex;justify-content:center;align-items:center;gap:10px;${({
  $error
}) => $error && css4`
      animation: ${shake} 360ms ease-in-out;
    `}`;
var Group = styled18.div.withConfig({
  componentId: "sc-e9wtgd-1"
})`display:flex;`;
var SlotWrapper = styled18.div.withConfig({
  componentId: "sc-e9wtgd-2"
})`height:55px;width:40px;border:1px solid ${({
  $error
}) => $error ? theme.colors.red550 : theme.colors.gray500};position:relative;font-size:20px;font-weight:500;color:${theme.colors.gray900};background:${({
  $active
}) => $active ? theme.colors.gray100 : theme.colors.background};display:flex;justify-content:center;align-items:center;&:first-child{border-top-left-radius:10px;border-bottom-left-radius:10px;}&:last-child{border-top-right-radius:10px;border-bottom-right-radius:10px;}&:not(:last-child){border-right:none;}`;
var Caret = styled18.div.withConfig({
  componentId: "sc-e9wtgd-3"
})`position:absolute;pointer-events:none;height:30px;width:1px;background:${theme.colors.gray900};`;
var Dash = styled18.div.withConfig({
  componentId: "sc-e9wtgd-4"
})`width:40px;display:flex;justify-content:center;&::after{content:'';width:12px;height:2px;background:${theme.colors.gray500};}`;
var Slot = ({
  error,
  ...slot
}) => /* @__PURE__ */ React25.createElement(SlotWrapper, { $active: slot.isActive, $error: error }, slot.char !== null && /* @__PURE__ */ React25.createElement("div", null, slot.char), slot.hasFakeCaret && /* @__PURE__ */ React25.createElement(Caret, null));
var CodeInput = (p) => /* @__PURE__ */ React25.createElement(OTPInput, { maxLength: 6, value: p.value, disabled: p.disabled, autoFocus: p.autoFocus, autoComplete: "one-time-code", "aria-label": "Verification Code", inputMode: "numeric", pattern: REGEXP_ONLY_DIGITS, pasteTransformer: (pasted) => pasted.replace(/[^0-9]/g, ""), onChange: (code) => {
  p.onChange?.(code);
  if (code.length == 6) p.onComplete(code);
}, render: ({
  slots
}) => /* @__PURE__ */ React25.createElement(Wrapper8, { $error: !!p.error }, /* @__PURE__ */ React25.createElement(Group, null, slots.slice(0, 3).map((slot, index) => /* @__PURE__ */ React25.createElement(Slot, { key: index, ...slot, error: !!p.error }))), /* @__PURE__ */ React25.createElement(Dash, null), /* @__PURE__ */ React25.createElement(Group, null, slots.slice(3).map((slot, index) => /* @__PURE__ */ React25.createElement(Slot, { key: index, ...slot, error: !!p.error })))) });

// src/control/index.tsx
import * as RadixToggle from "@radix-ui/react-toggle-group";
import React26, { memo as memo5, useEffect as useEffect8, useReducer, useState as useState11 } from "react";
import { styled as styled19 } from "styled-components";
var Root9 = styled19(RadixToggle.Root).withConfig({
  componentId: "sc-1q4kxxc-0"
})`background:${theme.colors.gray300};border-radius:10px;width:fit-content;padding:3px;`;
var List = styled19("div").withConfig({
  componentId: "sc-1q4kxxc-1"
})`display:flex;position:relative;`;
var Item6 = styled19(RadixToggle.Item).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1q4kxxc-2"
})`display:flex;align-items:center;border:none;background:transparent;padding:0px;font-size:14px;color:${theme.colors.gray600};transition:none;position:relative;z-index:2;font-weight:500;&:hover{color:${theme.colors.blue900};}&:focus{color:${theme.colors.blue900};}&[data-state='active']{color:${theme.colors.gray100};}`;
var ItemInner = styled19("div").withConfig({
  componentId: "sc-1q4kxxc-3"
})`height:30px;padding:0px 10px;display:flex;align-items:center;justify-content:center;`;
var Indicator3 = styled19("div").withConfig({
  componentId: "sc-1q4kxxc-4"
})`position:absolute;border-radius:7px;z-index:1;background:${theme.colors.blue900};transition:none;`;
var Control = memo5(({
  items,
  value,
  onChange
}) => {
  let [elements, setElements] = useState11([]);
  let currentItemIndex = items.findIndex((item) => item.id == value);
  let [, rerender] = useReducer((s) => s + 1, 0);
  useEffect8(() => {
    setTimeout(() => {
      rerender();
      setTimeout(() => {
        rerender();
      }, 400);
    }, 100);
  }, []);
  return /* @__PURE__ */ React26.createElement(Root9, { value, onValueChange: (v) => {
    if (v == value || !v) return;
    onChange?.(v);
  }, type: "single" }, /* @__PURE__ */ React26.createElement(List, null, items.map((item, i) => /* @__PURE__ */ React26.createElement(Item6, { value: item.id, key: i, "data-state": item.id == value ? "active" : "inactive", ref: (el) => {
    if (el) {
      setElements((e) => {
        e[i] = el;
        return e;
      });
    }
  } }, /* @__PURE__ */ React26.createElement(ItemInner, null, item.label))), elements[currentItemIndex] && /* @__PURE__ */ React26.createElement(Indicator3, { style: {
    height: 30,
    top: 0,
    left: elements[currentItemIndex].offsetLeft,
    width: elements[currentItemIndex].offsetWidth
  } })));
});

// src/contextMenu/index.tsx
import React27, { useEffect as useEffect9, useState as useState12 } from "react";
import { createPortal } from "react-dom";
import { keyframes as keyframes9, styled as styled20 } from "styled-components";
var fadeInBottom3 = keyframes9`
  from { opacity: 0; transform: translateY(-10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutBottom3 = keyframes9`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`;
var Trigger5 = styled20.div.withConfig({
  componentId: "sc-1g6i9gp-0"
})`display:contents;`;
var Content12 = styled20.div.withConfig({
  componentId: "sc-1g6i9gp-1"
})`position:fixed;display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;color:${theme.colors.background};box-shadow:${theme.shadows.large};background:${theme.colors.foreground};border-radius:10px;min-width:200px;gap:5px;animation:${(p) => p.$closing ? fadeOutBottom3 : fadeInBottom3} 0.2s ease forwards;`;
var Separator3 = styled20.div.withConfig({
  componentId: "sc-1g6i9gp-2"
})`height:1px;background:${theme.colors.gray800};`;
var Item7 = styled20.button.withConfig({
  componentId: "sc-1g6i9gp-3"
})`font-size:14px;font-weight:500;display:flex;flex-direction:column;justify-content:center;text-align:left;gap:5px;padding:7px 13px;border-radius:6px;outline:none;border:none;background:transparent;transition:none;color:${theme.colors.background};max-width:250px;&:not(:disabled){&:hover{background:${theme.colors.gray800};}&:focus{background:${theme.colors.gray800};}}&:disabled{opacity:0.7;}h1{font-size:14px;font-weight:500;color:${theme.colors.background};}p{font-size:12px;color:${theme.colors.gray600};}`;
var Title5 = styled20.div.withConfig({
  componentId: "sc-1g6i9gp-4"
})`font-size:14px;font-weight:600;color:${theme.colors.gray100};padding:7px 13px;border-radius:6px;background:transparent;transition:none;display:flex;align-items:center;gap:5px;`;
var isSeparator = (item) => "type" in item && item.type == "separator";
var ContextMenu = ({
  children,
  disabled,
  label,
  onItemClick,
  items,
  title,
  setIsOpen
}) => {
  let [open, setOpen] = useState12(false);
  let [closing, setClosing] = useState12(false);
  let [position, setPosition] = useState12({
    x: 0,
    y: 0
  });
  let zIndex = useDialogZIndex(open);
  let close = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 200);
  };
  useEffect9(() => setIsOpen?.(open), [open]);
  useSuppressTooltipWhileOpen(open);
  useEffect9(() => {
    if (!open) return;
    let handlePointerDown = () => close();
    let handleClose = () => close();
    let handleKeyDown = (e) => {
      if (e.key == "Escape") close();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("resize", handleClose);
    window.addEventListener("scroll", handleClose, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("resize", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
  return /* @__PURE__ */ React27.createElement(Trigger5, { "aria-label": label, onContextMenu: (e) => {
    if (disabled) return;
    e.preventDefault();
    setClosing(false);
    setPosition({
      x: e.clientX,
      y: e.clientY
    });
    setOpen(true);
  } }, /* @__PURE__ */ React27.createElement(OverlayOpenProvider, { value: open }, children), open ? createPortal(/* @__PURE__ */ React27.createElement(Content12, { $closing: closing, onContextMenu: (e) => e.preventDefault(), onPointerDown: (e) => e.stopPropagation(), role: "menu", style: {
    left: position.x,
    top: position.y,
    zIndex
  } }, title && /* @__PURE__ */ React27.createElement(React27.Fragment, null, /* @__PURE__ */ React27.createElement(Title5, null, title), /* @__PURE__ */ React27.createElement(Separator3, null)), items.map((item, i) => isSeparator(item) ? /* @__PURE__ */ React27.createElement(Separator3, { key: i, role: "separator" }) : /* @__PURE__ */ React27.createElement(Item7, { disabled: item.disabled, key: i, onClick: () => {
    if (item.disabled) return;
    onItemClick?.(item.id);
    close();
  }, role: "menuitem", type: "button" }, /* @__PURE__ */ React27.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React27.createElement("p", null, item.description)))), document.body) : null);
};

// src/copy/index.tsx
import { RiCheckLine } from "@remixicon/react";
import copy from "copy-to-clipboard";
import React28, { useRef as useRef6, useState as useState13 } from "react";
import { toast } from "sonner";
import { styled as styled21 } from "styled-components";
var Value = styled21("pre").withConfig({
  componentId: "sc-1w5cxx8-0"
})`white-space:pre-wrap;word-wrap:break-word;margin:0;display:block;font-size:14px;padding:10px;border-radius:8px;background:${theme.colors.gray100};color:${theme.colors.foreground};border:1px solid ${theme.colors.gray300};`;
var CopyOverlay = styled21("div").withConfig({
  componentId: "sc-1w5cxx8-1"
})`position:absolute;top:0;right:0;left:0;bottom:0;padding:10px;border-radius:8px;background:rgba(255,255,255,0.8);border:1px solid ${theme.colors.gray300};z-index:1;display:flex;justify-content:center;align-items:center;`;
var CopyIcon = styled21("div").withConfig({
  componentId: "sc-1w5cxx8-2"
})`transform:rotate(-10deg);color:white;background:${theme.colors.gray800};height:26px;width:26px;border-radius:20px;display:flex;justify-content:center;align-items:center;`;
var useCopy = (topVal) => {
  let [copied, setCopied] = useState13(false);
  let topValRef = useRef6(topVal);
  topValRef.current = topVal;
  let doCopy = (innerVal) => {
    let value = innerVal || topValRef.current;
    if (!value) return;
    if (copied) return;
    copy(value);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2e3);
  };
  return {
    copied,
    copy: doCopy
  };
};
var Copy = ({
  value,
  label,
  copyValue
}) => {
  let {
    copied,
    copy: doCopy
  } = useCopy(copyValue ?? value);
  return /* @__PURE__ */ React28.createElement("div", { onClick: (c) => doCopy(), role: "button", onKeyDown: (c) => doCopy(), tabIndex: 0, style: {
    display: "flex",
    flexDirection: "column"
  } }, label && /* @__PURE__ */ React28.createElement(InputLabel, null, label), /* @__PURE__ */ React28.createElement("div", { style: {
    position: "relative"
  } }, /* @__PURE__ */ React28.createElement(Value, null, value), copied && /* @__PURE__ */ React28.createElement(CopyOverlay, null, /* @__PURE__ */ React28.createElement(CopyIcon, null, /* @__PURE__ */ React28.createElement(RiCheckLine, { size: 12, strokeWidth: 3 })))));
};

// src/combobox/index.tsx
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { VisuallyHidden as VisuallyHidden3 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine2, RiCheckLine as RiCheckLine2 } from "@remixicon/react";
import React29, { useEffect as useEffect10, useId as useId3, useMemo as useMemo4, useRef as useRef7, useState as useState14 } from "react";
import { keyframes as keyframes10, styled as styled22 } from "styled-components";
var fadeIn2 = keyframes10`
  from {
    transform: scale(0.98);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Wrapper9 = styled22("div").withConfig({
  componentId: "sc-1rueb2t-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var InputGroup = styled22(BaseCombobox.InputGroup).withConfig({
  componentId: "sc-1rueb2t-1"
})`outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};display:flex;width:100%;transition:all 0.3s ease;gap:8px;align-items:center;&:focus-within{background:${theme.colors.gray400};outline:1px solid ${theme.colors.gray600};}&[data-disabled]{opacity:0.6;cursor:not-allowed;}`;
var Input2 = styled22(BaseCombobox.Input).withConfig({
  componentId: "sc-1rueb2t-2"
})`flex-grow:1;border:none;background:transparent;height:100%;outline:none;min-width:0;&::placeholder{color:${theme.colors.gray700};}&:disabled{cursor:not-allowed;}`;
var Trigger6 = styled22(BaseCombobox.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1rueb2t-3"
})`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;border:none;background:transparent;color:${theme.colors.gray700};cursor:pointer;padding:0;opacity:${(d) => d.$loading ? 0 : 1};transform:scale(${(d) => d.$loading ? 0.85 : 1});transition:none;&:focus{outline:none;color:${theme.colors.gray900};}&:disabled{cursor:not-allowed;}`;
var EndSlot = styled22("div").withConfig({
  componentId: "sc-1rueb2t-4"
})`width:28px;align-self:stretch;position:relative;display:flex;align-items:center;justify-content:center;padding-right:12px;flex-shrink:0;`;
var SpinnerSlot = styled22("div").withConfig({
  componentId: "sc-1rueb2t-5"
})`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:${(d) => d.$visible ? 1 : 0};transform:scale(${(d) => d.$visible ? 1 : 0.85});transition:opacity 0.3s ease,transform 0.3s ease;pointer-events:none;`;
var Positioner = styled22(BaseCombobox.Positioner).withConfig({
  componentId: "sc-1rueb2t-6"
})`width:var(--anchor-width);`;
var Popup = styled22(BaseCombobox.Popup).withConfig({
  componentId: "sc-1rueb2t-7"
})`display:flex;flex-direction:column;gap:6px;padding:6px;box-shadow:${theme.shadows.medium};background:${theme.colors.gray200};border:1px solid ${theme.colors.gray400};border-radius:10px;max-height:min(320px,var(--available-height));overflow:hidden;&[data-open]{animation:${fadeIn2} 0.15s ease forwards;}`;
var List2 = styled22(BaseCombobox.List).withConfig({
  componentId: "sc-1rueb2t-8"
})`display:flex;flex-direction:column;gap:4px;overflow:auto;padding-right:2px;`;
var Group2 = styled22(BaseCombobox.Group).withConfig({
  componentId: "sc-1rueb2t-9"
})`display:flex;flex-direction:column;gap:4px;`;
var GroupLabel = styled22(BaseCombobox.GroupLabel).withConfig({
  componentId: "sc-1rueb2t-10"
})`font-size:11px;font-weight:600;color:${theme.colors.gray700};padding:6px 8px 2px;letter-spacing:0.04em;`;
var Item8 = styled22(BaseCombobox.Item).withConfig({
  componentId: "sc-1rueb2t-11"
})`display:flex;align-items:center;gap:6px;min-height:38px;padding:8px 10px;border-radius:8px;outline:none;cursor:pointer;transition:none;&:hover,&[data-highlighted]{background:${theme.colors.gray300};}&[data-selected]{background:${theme.colors.gray400};}&[data-disabled]{opacity:0.5;cursor:not-allowed;}`;
var ItemIndicator = styled22(BaseCombobox.ItemIndicator).withConfig({
  componentId: "sc-1rueb2t-12"
})`display:flex;align-items:center;justify-content:center;color:${theme.colors.gray800};`;
var ItemIndicatorSlot = styled22("div").withConfig({
  componentId: "sc-1rueb2t-13"
})`width:20px;height:20px;display:flex;align-items:center;justify-content:center;flex-shrink:0;`;
var ItemContent = styled22("div").withConfig({
  componentId: "sc-1rueb2t-14"
})`min-width:0;display:flex;flex-direction:column;gap:2px;flex:1;`;
var ItemLabel = styled22("div").withConfig({
  componentId: "sc-1rueb2t-15"
})`font-size:14px;font-weight:500;color:${theme.colors.foreground};line-height:1.2;`;
var ItemDescription = styled22("div").withConfig({
  componentId: "sc-1rueb2t-16"
})`font-size:12px;color:${theme.colors.gray700};line-height:1.35;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
var EmptyState = styled22(BaseCombobox.Empty).withConfig({
  componentId: "sc-1rueb2t-17"
})`font-size:13px;color:${theme.colors.gray700};padding:10px;text-align:center;&:empty{display:none;padding:0;}`;
var Footer = styled22("div").withConfig({
  componentId: "sc-1rueb2t-18"
})`font-size:12px;color:${theme.colors.gray700};padding:4px 8px 2px;`;
var useDebouncedValue = (value, delay) => {
  let [debouncedValue, setDebouncedValue] = useState14(() => value);
  useEffect10(() => {
    let timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, value]);
  return debouncedValue;
};
var normalizeGroups = (items) => {
  if (!items || items.length === 0) return [];
  if ("items" in items[0]) return items;
  return [{
    label: null,
    items
  }];
};
var flattenGroups = (groups) => {
  return groups.flatMap((group) => group.items);
};
var matchesItem = (item, searchQuery) => {
  if (!searchQuery) return true;
  let query = searchQuery.toLocaleLowerCase();
  let searchable = [item.label, ...item.keywords ?? []];
  if (typeof item.description == "string") searchable.push(item.description);
  return searchable.some((value) => value.toLocaleLowerCase().includes(query));
};
var filterGroups = (items, searchQuery) => {
  let groups = normalizeGroups(items);
  if (!searchQuery) return groups;
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => matchesItem(item, searchQuery))
  })).filter((group) => group.items.length > 0);
};
var createLabelCache = (items) => {
  return items.reduce((acc, item) => {
    acc[item.id] = item.label;
    return acc;
  }, {});
};
var ComboboxInner = ({
  items,
  isLoading,
  status,
  empty,
  search,
  setSearch,
  size = "3",
  label,
  description,
  hideLabel,
  error,
  placeholder,
  disabled,
  readOnly,
  required,
  value,
  valueLabel,
  noResultsMessage = "No results found.",
  inputRef,
  onChange,
  onSearchChange,
  renderItem,
  ...inputProps
}) => {
  let generatedId = useId3();
  let {
    id: providedId,
    name,
    onFocus,
    onClick: onInputClick,
    ...restInputProps
  } = inputProps;
  let id = providedId ?? generatedId;
  let sizeStyles = getButtonSize(size);
  let normalizedValue = value ?? null;
  let normalizedGroups = useMemo4(() => normalizeGroups(items), [items]);
  let flatItems = useMemo4(() => flattenGroups(normalizedGroups), [normalizedGroups]);
  let [labelCache, setLabelCache] = useState14(() => createLabelCache(flatItems));
  let selectedItem = flatItems.find((item) => item.id === normalizedValue) ?? null;
  let selectedLabel = selectedItem?.label ?? (normalizedValue ? labelCache[normalizedValue] : void 0) ?? valueLabel ?? "";
  let [open, setOpen] = useState14(false);
  let zIndex = useZindex(open);
  let previousLoading = useRef7(!!isLoading);
  let [animateArrowIn, setAnimateArrowIn] = useState14(false);
  let dialog = useDialogContext();
  let showLoading = open && !!isLoading;
  let hidePopover = !!isLoading && flatItems.length === 0;
  let clearSelectedLabelSearch = () => {
    if (search !== selectedLabel) return;
    setSearch("");
    onSearchChange?.("");
  };
  useEffect10(() => {
    if (flatItems.length === 0) return;
    setLabelCache((current) => ({
      ...current,
      ...createLabelCache(flatItems)
    }));
  }, [flatItems]);
  useEffect10(() => {
    if (!normalizedValue || !valueLabel) return;
    setLabelCache((current) => {
      if (current[normalizedValue] === valueLabel) return current;
      return {
        ...current,
        [normalizedValue]: valueLabel
      };
    });
  }, [normalizedValue, valueLabel]);
  useEffect10(() => {
    if (open) return;
    setSearch(selectedLabel);
  }, [open, selectedLabel, setSearch]);
  useEffect10(() => {
    let wasLoading = previousLoading.current;
    let isCurrentlyLoading = !!isLoading;
    if (wasLoading && !isCurrentlyLoading) {
      setAnimateArrowIn(true);
      let timeout = window.setTimeout(() => {
        setAnimateArrowIn(false);
      }, 300);
      previousLoading.current = isCurrentlyLoading;
      return () => window.clearTimeout(timeout);
    }
    if (isCurrentlyLoading) {
      setAnimateArrowIn(false);
    }
    previousLoading.current = isCurrentlyLoading;
  }, [isLoading]);
  return /* @__PURE__ */ React29.createElement(Wrapper9, null, label && (hideLabel ? /* @__PURE__ */ React29.createElement(VisuallyHidden3, null, /* @__PURE__ */ React29.createElement(InputLabel, { htmlFor: id }, label)) : /* @__PURE__ */ React29.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React29.createElement(InputDescription, null, description), /* @__PURE__ */ React29.createElement(BaseCombobox.Root, { name, value: normalizedValue, itemToStringLabel: (itemValue) => labelCache[itemValue] ?? itemValue, onValueChange: (nextValue) => {
    let nextItem = flatItems.find((item) => item.id === nextValue) ?? null;
    if (nextItem) {
      setLabelCache((current) => ({
        ...current,
        [nextItem.id]: nextItem.label
      }));
    }
    setSearch(nextItem?.label ?? "");
    onChange?.(nextItem?.id ?? null, nextItem);
    setOpen(false);
  }, inputValue: search, onInputValueChange: (nextValue) => {
    setSearch(nextValue);
    onSearchChange?.(nextValue);
  }, open, onOpenChange: (nextOpen, eventDetails) => {
    setOpen(nextOpen);
    if (nextOpen) {
      if (eventDetails.reason !== "input-change") clearSelectedLabelSearch();
      return;
    }
    if (!nextOpen && selectedLabel) {
      setSearch(selectedLabel);
    }
  }, items: flatItems, filteredItems: flatItems, autoHighlight: true, disabled, readOnly, required, inputRef }, /* @__PURE__ */ React29.createElement(InputGroup, { style: {
    height: sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    outline: error ? `1px solid ${theme.colors.red900}` : void 0
  } }, /* @__PURE__ */ React29.createElement(Input2, { id, placeholder, disabled, readOnly, onFocus: (e) => {
    setOpen(true);
    clearSelectedLabelSearch();
    onFocus?.(e);
  }, onClick: (e) => {
    onInputClick?.(e);
  }, style: {
    padding: sizeStyles.padding,
    paddingRight: 0,
    height: sizeStyles.height,
    fontSize: "14px"
  }, ...restInputProps }), /* @__PURE__ */ React29.createElement(EndSlot, null, /* @__PURE__ */ React29.createElement(SpinnerSlot, { $visible: showLoading, "aria-hidden": !showLoading }, /* @__PURE__ */ React29.createElement(Spinner, { size: 14, foreground: theme.colors.gray700, background: "transparent" })), /* @__PURE__ */ React29.createElement(Trigger6, { $animateIn: animateArrowIn, $loading: showLoading, "aria-label": typeof label == "string" ? label : "Toggle options" }, /* @__PURE__ */ React29.createElement(RiArrowDownSLine2, { size: 16 })))), /* @__PURE__ */ React29.createElement(BaseCombobox.Portal, { container: dialog?.contentRef }, /* @__PURE__ */ React29.createElement(Positioner, { sideOffset: 6, style: {
    zIndex,
    display: hidePopover ? "none" : void 0
  } }, /* @__PURE__ */ React29.createElement(Popup, null, /* @__PURE__ */ React29.createElement(List2, null, normalizedGroups.map((group, groupIndex) => group.label ? /* @__PURE__ */ React29.createElement(Group2, { key: groupIndex }, /* @__PURE__ */ React29.createElement(GroupLabel, null, group.label), group.items.map((item) => /* @__PURE__ */ React29.createElement(Item8, { key: item.id, value: item.id, disabled: item.disabled }, /* @__PURE__ */ React29.createElement(ItemIndicatorSlot, null, /* @__PURE__ */ React29.createElement(ItemIndicator, null, /* @__PURE__ */ React29.createElement(RiCheckLine2, { size: 14 }))), /* @__PURE__ */ React29.createElement(ItemContent, null, renderItem ? renderItem(item) : /* @__PURE__ */ React29.createElement(React29.Fragment, null, /* @__PURE__ */ React29.createElement(ItemLabel, null, item.label), item.description && /* @__PURE__ */ React29.createElement(ItemDescription, null, item.description)))))) : group.items.map((item) => /* @__PURE__ */ React29.createElement(Item8, { key: item.id, value: item.id, disabled: item.disabled }, /* @__PURE__ */ React29.createElement(ItemIndicatorSlot, null, /* @__PURE__ */ React29.createElement(ItemIndicator, null, /* @__PURE__ */ React29.createElement(RiCheckLine2, { size: 14 }))), /* @__PURE__ */ React29.createElement(ItemContent, null, renderItem ? renderItem(item) : /* @__PURE__ */ React29.createElement(React29.Fragment, null, /* @__PURE__ */ React29.createElement(ItemLabel, null, item.label), item.description && /* @__PURE__ */ React29.createElement(ItemDescription, null, item.description)))))), !isLoading && /* @__PURE__ */ React29.createElement(EmptyState, null, empty ?? noResultsMessage)), status && /* @__PURE__ */ React29.createElement(Footer, null, /* @__PURE__ */ React29.createElement(BaseCombobox.Status, null, status)))))), error && /* @__PURE__ */ React29.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};
var ProvidedCombobox = ({
  provider,
  value,
  ...props
}) => {
  let [search, setSearch] = useState14(props.valueLabel ?? "");
  let immediateSearchQuery = search.trim() || void 0;
  let debouncedSearchQuery = useDebouncedValue(immediateSearchQuery, props.debounceMs ?? 500) || void 0;
  let searchQuery = immediateSearchQuery ? debouncedSearchQuery : void 0;
  let isSearchPending = immediateSearchQuery !== searchQuery;
  let cacheRef = useRef7(/* @__PURE__ */ new Map());
  let liveResult = provider({
    search,
    searchQuery,
    value
  });
  let resolvedQueryKey = searchQuery ?? "";
  let pendingQueryKey = immediateSearchQuery ?? "";
  let activeCacheKey = isSearchPending ? pendingQueryKey : resolvedQueryKey;
  useEffect10(() => {
    if (liveResult.isLoading) return;
    cacheRef.current.set(resolvedQueryKey, {
      items: liveResult.items,
      status: liveResult.status,
      empty: liveResult.empty
    });
  }, [liveResult.empty, liveResult.isLoading, liveResult.items, liveResult.status, resolvedQueryKey]);
  let cachedResult = cacheRef.current.get(activeCacheKey);
  let result = cachedResult && (liveResult.isLoading || isSearchPending) ? {
    items: cachedResult.items,
    status: liveResult.status ?? cachedResult.status,
    empty: liveResult.empty ?? cachedResult.empty,
    isLoading: true
  } : liveResult;
  return /* @__PURE__ */ React29.createElement(ComboboxInner, { ...props, value, search, setSearch, ...result, isLoading: result.isLoading || isSearchPending && !cachedResult, onSearchChange: props.onSearchChange });
};
var StaticCombobox = ({
  items,
  ...props
}) => {
  let [search, setSearch] = useState14(props.valueLabel ?? "");
  let filteredItems = useMemo4(() => filterGroups(items, search), [items, search]);
  return /* @__PURE__ */ React29.createElement(ComboboxInner, { ...props, items: filteredItems, search, setSearch, onSearchChange: props.onSearchChange });
};
var Combobox = (props) => {
  if ("provider" in props && props.provider) {
    return /* @__PURE__ */ React29.createElement(ProvidedCombobox, { ...props });
  }
  return /* @__PURE__ */ React29.createElement(StaticCombobox, { ...props });
};

// src/datalist/index.tsx
import React30, { Fragment } from "react";
import { styled as styled23 } from "styled-components";
var Wrapper10 = styled23("div").withConfig({
  componentId: "sc-1wdw6ce-0"
})``;
var DL = styled23("dl").withConfig({
  componentId: "sc-1wdw6ce-1"
})`display:grid;margin:0;padding:0;font-size:14px;&[data-variant='default']{gap:10px;grid-template-columns:auto 1fr;}&[data-variant='large']{gap:30px;grid-template-columns:1fr;div{gap:7px;display:grid;grid-template-columns:1fr;}}&[data-variant='table']{grid-template-columns:1fr;border-top:1px solid ${theme.colors.gray400};> div{display:grid;grid-template-columns:minmax(96px,30%) 1fr;align-items:baseline;gap:16px;padding:16px 0;border-bottom:1px solid ${theme.colors.gray400};}dd{min-width:0;overflow-wrap:anywhere;}}`;
var DT = styled23("dt").withConfig({
  componentId: "sc-1wdw6ce-2"
})`font-weight:600;color:${theme.colors.gray600};margin:0 !important;dl[data-variant='table'] &{font-weight:400;}`;
var DD = styled23("dd").withConfig({
  componentId: "sc-1wdw6ce-3"
})`font-weight:400;margin:0 !important;`;
var Datalist = ({
  items,
  variant = "default"
}) => {
  return /* @__PURE__ */ React30.createElement(Wrapper10, null, /* @__PURE__ */ React30.createElement(DL, { "data-variant": variant }, items.map((item, i) => variant == "default" ? /* @__PURE__ */ React30.createElement(Fragment, { key: i }, /* @__PURE__ */ React30.createElement(DT, null, item.label), /* @__PURE__ */ React30.createElement(DD, null, item.value)) : /* @__PURE__ */ React30.createElement("div", { key: i }, /* @__PURE__ */ React30.createElement(DT, null, item.label), /* @__PURE__ */ React30.createElement(DD, null, item.value)))));
};

// src/date/index.tsx
import React31, { useLayoutEffect as useLayoutEffect3, useMemo as useMemo5, useReducer as useReducer2, useRef as useRef8, useState as useState15 } from "react";
import { useInterval as useInterval2 } from "react-use";
import { shleemy } from "shleemy";
import { styled as styled24 } from "styled-components";
var Header2 = styled24("h1").withConfig({
  componentId: "sc-vlf3ci-0"
})`font-size:14px;font-weight:600;`;
var Description3 = styled24("p").withConfig({
  componentId: "sc-vlf3ci-1"
})`font-size:13px;margin-top:5px;opacity:0.6;font-weight:500;strong{font-weight:700;}`;
var normalizeDate = (date) => {
  if (!date) return null;
  return typeof date == "string" ? new Date(date) : date;
};
var useStaticDate = (date) => {
  let [currentDate, setCurrentDate] = useState15(() => normalizeDate(date));
  let currentDateIdentifier = useRef8(!date ? "none" : String(date));
  let inputDateIdentifier = !date ? "none" : String(date);
  useLayoutEffect3(() => {
    if (currentDateIdentifier.current === inputDateIdentifier) return;
    currentDateIdentifier.current = inputDateIdentifier;
    setCurrentDate(normalizeDate(date));
  }, [inputDateIdentifier]);
  return currentDate;
};
var RenderDate = ({
  date,
  format = "full"
}) => {
  let [retriggerIndex, doRetrigger] = useReducer2((s) => s + 1, 0);
  let normalizedDate = useStaticDate(date);
  let result = useMemo5(() => {
    if (!normalizedDate) return {
      date: null,
      utc: "",
      local: "",
      pretty: "",
      timeZone: ""
    };
    let parsed = normalizedDate;
    let utc = new Date(parsed.toUTCString().replace("GMT", ""));
    let humanOffset = shleemy(parsed).forHumans;
    let pretty = format == "date" ? parsed.toLocaleDateString() : format == "time" ? parsed.toLocaleTimeString() : parsed.toLocaleString();
    return {
      date: parsed,
      humanOffset,
      utc: utc.toLocaleString(),
      local: parsed.toLocaleString(),
      pretty,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }, [format, normalizedDate, retriggerIndex]);
  useInterval2(() => doRetrigger(), 60 * 1e3);
  if (!result.date) return /* @__PURE__ */ React31.createElement("time", { style: {
    cursor: "pointer"
  } }, "No date");
  return /* @__PURE__ */ React31.createElement(Tooltip, { content: /* @__PURE__ */ React31.createElement("div", { style: {
    padding: 5
  } }, /* @__PURE__ */ React31.createElement(Header2, null, "Time"), /* @__PURE__ */ React31.createElement(Description3, null, /* @__PURE__ */ React31.createElement("strong", null, "UTC"), ": ", /* @__PURE__ */ React31.createElement("time", null, result.utc)), /* @__PURE__ */ React31.createElement(Description3, null, /* @__PURE__ */ React31.createElement("strong", null, "Local"), ": ", /* @__PURE__ */ React31.createElement("time", null, result.local), " (", result.timeZone, ")"), /* @__PURE__ */ React31.createElement(Description3, null, /* @__PURE__ */ React31.createElement("strong", null, "Offset"), ": ", /* @__PURE__ */ React31.createElement("time", null, result.humanOffset))) }, /* @__PURE__ */ React31.createElement("span", { style: {
    display: "inline-block",
    width: "fit-content",
    height: "fit-content"
  }, onMouseEnter: doRetrigger }, /* @__PURE__ */ React31.createElement("span", { style: {
    cursor: "pointer",
    borderBottom: `1px dashed ${theme.colors.gray500}`,
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap",
    lineHeight: void 0
  } }, result.pretty)));
};

// src/datePicker/index.tsx
import * as RadixPopover from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden4 } from "@radix-ui/react-visually-hidden";
import { RiArrowLeftSFill, RiArrowRightSLine as RiArrowRightSLine3 } from "@remixicon/react";
import React32, { useState as useState17 } from "react";
import { keyframes as keyframes11, styled as styled25 } from "styled-components";

// src/datePicker/state.ts
import { addMonths, eachDayOfInterval, eachWeekOfInterval, endOfDay, endOfMonth, endOfWeek, isAfter, isBefore, isSameDay, isSameMonth, isToday, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { useEffect as useEffect11, useMemo as useMemo6, useState as useState16 } from "react";
var dateToRange = (date) => {
  if (Array.isArray(date)) return date;
  return [date, date];
};
var useDatePickerState = ({
  value,
  onChange,
  type,
  open,
  maxDate,
  minDate
}) => {
  let [selectedDate, setSelectedDate] = useState16(() => value ? dateToRange(value) : void 0);
  useEffect11(() => {
    setSelectedDate(value ? dateToRange(value) : void 0);
  }, [value, open]);
  let [currentMonth, setCurrentMonth] = useState16(() => startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
  useEffect11(() => {
    let start = startOfWeek(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    let end = endOfWeek(endOfMonth(selectedDate ? selectedDate[1] : /* @__PURE__ */ new Date()));
    if (!isWithinInterval(currentMonth, {
      start,
      end
    })) {
      setCurrentMonth(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    }
  }, [selectedDate]);
  let prevMonthEnabled = !minDate || isAfter(currentMonth, minDate);
  let nextMonthEnabled = !maxDate || isBefore(addMonths(currentMonth, 1), maxDate);
  let nextMonth = () => setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)));
  let prevMonth = () => setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)));
  let days = useMemo6(() => {
    let startDate = currentMonth;
    let endDate = endOfMonth(currentMonth);
    let weeks = eachWeekOfInterval({
      start: startDate,
      end: endDate
    });
    return weeks.map((week) => {
      return {
        id: `week_${week.getTime()}`,
        days: eachDayOfInterval({
          start: startOfWeek(week),
          end: endOfWeek(week)
        }).map((day) => {
          let isInSelectedRange = !!(selectedDate && isWithinInterval(day, {
            start: selectedDate[0],
            end: selectedDate[1]
          }));
          let isStartOfRange = !!selectedDate && isSameDay(day, selectedDate[0]);
          let isEndOfRange = !!selectedDate && isSameDay(day, selectedDate[1]);
          return {
            id: `day_${day.getTime()}`,
            date: day,
            isToday: isToday(day),
            isInSelectedRange,
            isSelected: isStartOfRange || isEndOfRange,
            isStartOfRange,
            isEndOfRange,
            isCurrentMonth: isSameMonth(day, currentMonth)
          };
        })
      };
    });
  }, [currentMonth, selectedDate]);
  let selectDate = (date) => {
    if (type == "single") {
      let range = dateToRange(date);
      setSelectedDate(range);
      onChange(range[0]);
    } else {
      let range;
      if (Array.isArray(date)) {
        range = date;
      } else {
        if (!selectedDate || isSameDay(date, selectedDate[0]) || isSameDay(date, selectedDate[1])) {
          range = [date, date];
        } else if (isBefore(date, selectedDate[0])) {
          range = [date, selectedDate[1]];
        } else if (isAfter(date, selectedDate[1])) {
          range = [selectedDate[0], date];
        } else {
          range = [selectedDate[0], date];
        }
      }
      let normalizedRange = [startOfDay(range[0]), endOfDay(range[1])];
      setSelectedDate(normalizedRange);
      onChange(normalizedRange);
    }
  };
  let stringValue = useMemo6(() => {
    if (!selectedDate) return void 0;
    if (type == "single") {
      return selectedDate[0].toLocaleDateString();
    } else {
      return selectedDate.map((d) => d.toLocaleDateString()).join(" - ");
    }
  }, [selectedDate, type]);
  let reset = () => {
    setSelectedDate(void 0);
    onChange(void 0);
  };
  return {
    currentMonth,
    nextMonth,
    prevMonth,
    days,
    selectDate,
    stringValue,
    reset,
    nextMonthEnabled,
    prevMonthEnabled
  };
};

// src/datePicker/index.tsx
var fadeIn3 = keyframes11`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var fadeOut2 = keyframes11`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;
var Trigger8 = styled25(RadixPopover.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1wby6r1-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:none;border:none;font-weight:500;&:focus-within,&:focus{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}& [data-placeholder]{font-weight:500;}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var Content14 = styled25(RadixPopover.Content).withConfig({
  componentId: "sc-1wby6r1-1"
})`background:${theme.colors.gray100};border:1px solid ${theme.colors.gray400};border-radius:10px;box-shadow:${theme.shadows.medium};overflow:hidden;display:flex;flex-direction:column;pointer-events:auto;&[data-state='open']{animation:${fadeIn3} 0.15s ease-in-out;}&[data-state='closed']{animation:${fadeOut2} 0.15s ease-in-out;}`;
var Header3 = styled25("header").withConfig({
  componentId: "sc-1wby6r1-2"
})`display:flex;justify-content:space-between;padding:10px;align-items:center;border-bottom:1px solid ${theme.colors.gray300};`;
var Month = styled25("div").withConfig({
  componentId: "sc-1wby6r1-3"
})`display:flex;flex-direction:column;padding:10px;gap:2px;`;
var Week = styled25("div").withConfig({
  componentId: "sc-1wby6r1-4"
})`display:grid;grid-template-columns:repeat(7,1fr);gap:2px;`;
var Day = styled25("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1wby6r1-5"
})`display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:5px;cursor:pointer;background:transparent;color:${theme.colors.gray800};border:none;transition:none;font-weight:500;font-size:14px;&[data-today='true']{background:${theme.colors.blue100};color:${theme.colors.blue700};}&[data-in-range='true']{background:${theme.colors.blue100};color:${theme.colors.blue800};}&[data-selected='true']{background:${theme.colors.blue400};color:${theme.colors.blue900};}&[data-not-in-month='true']{opacity:0.5;}&:disabled{cursor:not-allowed;opacity:0.5;}`;
var Footer2 = styled25("footer").withConfig({
  componentId: "sc-1wby6r1-6"
})`display:flex;justify-content:flex-end;padding:0px 10px 10px 10px;gap:10px;`;
var WeekDay = styled25("div").withConfig({
  componentId: "sc-1wby6r1-7"
})`text-align:center;font-size:12px;padding-bottom:5px;color:${theme.colors.gray600};font-weight:500;`;
var DatePicker = (props) => {
  let state = useDatePickerState(props);
  let [open, setOpen] = useState17(false);
  let sizeStyles = getButtonSize(props.size ?? "3");
  let zIndex = useDialogZIndex(open);
  return /* @__PURE__ */ React32.createElement("div", null, /* @__PURE__ */ React32.createElement(RadixPopover.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React32.createElement("div", { style: {
    display: "flex",
    flexDirection: "column"
  } }, props.hideLabel ? /* @__PURE__ */ React32.createElement(VisuallyHidden4, null, /* @__PURE__ */ React32.createElement("label", null, props.label)) : /* @__PURE__ */ React32.createElement(InputLabel, null, props.label), props.description && /* @__PURE__ */ React32.createElement(InputDescription, null, props.description), /* @__PURE__ */ React32.createElement(Trigger8, { style: {
    ...sizeStyles,
    fontSize: 14
  }, disabled: props.disabled }, state.stringValue || /* @__PURE__ */ React32.createElement("span", { "data-placeholder": true }, props.placeholder))), /* @__PURE__ */ React32.createElement(RadixPopover.Portal, null, /* @__PURE__ */ React32.createElement(Content14, { sideOffset: 5, side: "bottom", style: {
    zIndex
  } }, /* @__PURE__ */ React32.createElement(Header3, null, /* @__PURE__ */ React32.createElement(Tooltip, { content: "Previous Month" }, /* @__PURE__ */ React32.createElement(Button, { type: "button", size: "2", variant: "soft", iconLeft: /* @__PURE__ */ React32.createElement(RiArrowLeftSFill, null), onClick: state.prevMonth, disabled: !state.prevMonthEnabled })), /* @__PURE__ */ React32.createElement(AnimateContent, { identifier: state.currentMonth.getTime() }, /* @__PURE__ */ React32.createElement(Title3, { as: "h1", weight: "bold", size: "2", align: "center" }, state.currentMonth.toLocaleDateString("en", {
    month: "long",
    year: "numeric"
  }))), /* @__PURE__ */ React32.createElement(Tooltip, { content: "Next Month" }, /* @__PURE__ */ React32.createElement(Button, { type: "button", size: "2", variant: "soft", iconLeft: /* @__PURE__ */ React32.createElement(RiArrowRightSLine3, null), onClick: state.nextMonth, disabled: !state.nextMonthEnabled }))), /* @__PURE__ */ React32.createElement(AnimatePanes, { orderedIdentifier: state.currentMonth.getTime() }, /* @__PURE__ */ React32.createElement(Month, null, /* @__PURE__ */ React32.createElement(Week, null, state.days[0].days.map((day) => /* @__PURE__ */ React32.createElement(WeekDay, { key: day.id }, day.date.toLocaleDateString("en", {
    weekday: "short"
  })))), state.days.map((week) => /* @__PURE__ */ React32.createElement(Week, { key: week.id }, week.days.map((day) => /* @__PURE__ */ React32.createElement(Day, { key: day.id, "data-today": day.isToday, "data-in-range": day.isInSelectedRange, "data-selected": day.isSelected, "data-not-in-month": !day.isCurrentMonth, onClick: () => state.selectDate(day.date), disabled: props.maxDate && day.date > props.maxDate || props.minDate && day.date < props.minDate }, day.date.getDate())))))), /* @__PURE__ */ React32.createElement(Footer2, null, props.resettable && /* @__PURE__ */ React32.createElement(Button, { type: "button", size: "2", variant: "soft", onClick: () => {
    state.reset();
  } }, "Reset"), /* @__PURE__ */ React32.createElement(RadixPopover.Close, { asChild: true }, /* @__PURE__ */ React32.createElement(Button, { type: "button", size: "2", variant: "soft", autoFocus: true, onClick: () => {
    setOpen(false);
  } }, "Done")))))));
};

// src/dialog/dialog.tsx
import * as RadixDialogDialog2 from "@radix-ui/react-dialog";
import { RiCloseLine } from "@remixicon/react";
import React33, { useRef as useRef10 } from "react";
import { styled as styled27 } from "styled-components";

// src/dialog/focus.ts
import { useCallback as useCallback2, useEffect as useEffect12, useRef as useRef9, useState as useState18 } from "react";
var textFieldSelector = "input, textarea";
var textInputTypes = /* @__PURE__ */ new Set(["text", "search", "email", "url", "tel", "password", "number"]);
var isEligibleField = (field, content) => {
  if (field.closest('[role="dialog"]') !== content || field.matches(":disabled") || field.readOnly || field.tabIndex < 0 || // A combobox opens its list on focus; autofocusing one popped the list open
  // over the rest of the form as soon as the dialog or drawer appeared.
  field.getAttribute("role") === "combobox" || field instanceof HTMLInputElement && !textInputTypes.has(field.type)) {
    return false;
  }
  let view = field.ownerDocument.defaultView;
  for (let ancestor = field; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.matches('[hidden], [inert], [aria-hidden="true"]')) return false;
    let style2 = view?.getComputedStyle(ancestor);
    if (style2?.display === "none") return false;
  }
  let style = view?.getComputedStyle(field);
  return style?.visibility !== "hidden" && style?.visibility !== "collapse";
};
var focusFirstField = (content) => {
  for (let field of content.querySelectorAll(textFieldSelector)) {
    if (!isEligibleField(field, content)) continue;
    field.focus({
      preventScroll: true
    });
    if (content.ownerDocument.activeElement === field) return true;
  }
  return false;
};
var useModalAutoFocus = (contentRef, isOpen) => {
  let [content, setContent] = useState18(null);
  let session = useRef9({
    handledOpen: false,
    completed: false
  });
  let ref = useCallback2((node) => {
    contentRef.current = node;
    setContent(node);
  }, [contentRef]);
  let onOpenAutoFocus = useCallback2((event) => {
    let node = contentRef.current;
    session.current = {
      handledOpen: true,
      completed: false
    };
    if (!node) return;
    if (node.contains(node.ownerDocument.activeElement) || focusFirstField(node)) {
      session.current.completed = true;
      event.preventDefault();
    }
  }, [contentRef]);
  useEffect12(() => {
    if (!isOpen || !content || session.current.completed) return;
    let doc = content.ownerDocument;
    let cancelled = false;
    let observer;
    let fallback = null;
    let stop = () => {
      session.current.completed = true;
      observer?.disconnect();
      doc.removeEventListener("pointerdown", stop, true);
      doc.removeEventListener("keydown", stop, true);
      doc.removeEventListener("focusin", onFocus, true);
    };
    let onFocus = () => {
      if (doc.activeElement !== fallback) stop();
    };
    let tryFocus = () => {
      if (session.current.completed) return;
      if (doc.activeElement !== fallback || !content.contains(fallback)) {
        stop();
        return;
      }
      if (focusFirstField(content)) stop();
    };
    queueMicrotask(() => {
      if (cancelled || session.current.completed) return;
      if (!session.current.handledOpen) {
        stop();
        return;
      }
      fallback = doc.activeElement;
      observer = new MutationObserver(tryFocus);
      observer.observe(content, {
        childList: true,
        subtree: true,
        attributes: true
      });
      doc.addEventListener("pointerdown", stop, true);
      doc.addEventListener("keydown", stop, true);
      doc.addEventListener("focusin", onFocus, true);
      tryFocus();
    });
    return () => {
      cancelled = true;
      observer?.disconnect();
      doc.removeEventListener("pointerdown", stop, true);
      doc.removeEventListener("keydown", stop, true);
      doc.removeEventListener("focusin", onFocus, true);
    };
  }, [content, isOpen]);
  return {
    ref,
    onOpenAutoFocus
  };
};

// src/dialog/styles.ts
import * as RadixDialogDialog from "@radix-ui/react-dialog";
import { keyframes as keyframes12, styled as styled26 } from "styled-components";
var fadeInShift2 = keyframes12`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift2 = keyframes12`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn4 = keyframes12`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
var fadeOut3 = keyframes12`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
var fadeInRight3 = keyframes12`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
var fadeOutRight3 = keyframes12`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(50px);
  }
`;
var Overlay5 = styled26(RadixDialogDialog.Overlay).withConfig({
  componentId: "sc-1xhkfuh-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(100,100,100,0.1);backdrop-filter:blur(5px);&[data-state='open']{animation:${fadeIn4} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut3} 200ms ease-in forwards;}`;
var Content16 = styled26(RadixDialogDialog.Content).withConfig({
  componentId: "sc-1xhkfuh-1"
})`position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:${theme.colors.background};border-radius:10px;box-shadow:${theme.shadows.large};color:${theme.colors.gray900};padding:20px;&[data-state='open']{animation:${fadeInShift2} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift2} 200ms ease-in;}`;
var ContentSide = styled26(RadixDialogDialog.Content).withConfig({
  componentId: "sc-1xhkfuh-2"
})`position:fixed;top:10px;right:10px;bottom:10px;background:${theme.colors.background};box-shadow:${theme.shadows.large};height:calc(100% - 20px);width:min(600px,90vw);border-radius:10px;display:flex;flex-direction:column;overflow:hidden;max-width:calc(100% - 20px);--dialog-radius:10px;&[data-state='open']{animation:${fadeInRight3} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutRight3} 200ms ease-in;}`;
var Close2 = styled26("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1xhkfuh-3"
})`position:absolute;top:15px;right:15px;height:26px;width:26px;display:flex;align-items:center;justify-content:center;padding:0;border-radius:50%;border:none;transition:none;background:${theme.colors.gray400};&:hover{background:${theme.colors.gray500};}`;

// src/dialog/dialog.tsx
var Wrapper11 = styled27(Content16).withConfig({
  componentId: "sc-1big1xm-0"
})`width:90vw;max-height:90vh;overflow:auto;background:${theme.colors.background};box-shadow:0 0 10px rgba(0,0,0,0.1);padding:0px;border-radius:15px;border:solid 1px ${theme.colors.gray500};transition:all 0.2s ease-in-out;`;
var Inner3 = styled27("div").withConfig({
  componentId: "sc-1big1xm-1"
})`color:${theme.colors.gray900};${({
  $variant
}) => $variant === "padded" ? `
    padding: 120px 60px;

    @media (max-width: 800px) {
      padding: 20px;
    }
  ` : `
    padding: 20px;
  `}`;
var Title7 = styled27(RadixDialogDialog2.Title).withConfig({
  componentId: "sc-1big1xm-2"
})`padding:0;font-size:20px;font-weight:600;padding:0px 0px 6px 0px;`;
var Description5 = styled27(RadixDialogDialog2.Description).withConfig({
  componentId: "sc-1big1xm-3"
})`padding:0px 0px 20px 0px;font-size:14px;font-weight:500;color:${theme.colors.gray600};`;
var Actions2 = styled27("div").withConfig({
  componentId: "sc-1big1xm-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:0;`;
var Dialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    variant = "slim",
    width
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef10(null);
    let autoFocus = useModalAutoFocus(contentRef, isOpen);
    return /* @__PURE__ */ React33.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React33.createElement(RadixDialogDialog2.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React33.createElement(RadixDialogDialog2.Portal, null, /* @__PURE__ */ React33.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React33.createElement(Wrapper11, { ref: autoFocus.ref, onOpenAutoFocus: autoFocus.onOpenAutoFocus, onPointerDownOutside: preventDialogDismissForSelectInteraction, onInteractOutside: preventDialogDismissForSelectInteraction, onFocusOutside: preventDialogDismissForSelectInteraction, style: {
      zIndex: zIndex + 1,
      maxWidth: width ? width : variant == "padded" ? 480 : 400,
      ...style
    } }, /* @__PURE__ */ React33.createElement(Inner3, { $variant: variant }, children, /* @__PURE__ */ React33.createElement(RadixDialogDialog2.Close, { asChild: true }, /* @__PURE__ */ React33.createElement(Close2, { "aria-label": "Close" }, /* @__PURE__ */ React33.createElement(RiCloseLine, { size: 16 }))))))));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React33.createElement(Title7, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React33.createElement(Description5, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React33.createElement(Actions2, null, children);
  }
};

// src/dialog/focusDialog.tsx
import * as RadixDialogDialog3 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine2 } from "@remixicon/react";
import React34, { useRef as useRef11 } from "react";
import { styled as styled28 } from "styled-components";
var Wrapper12 = styled28(Content16).withConfig({
  componentId: "sc-yyq0mi-0"
})`width:calc(100vw - 40px);max-height:calc(100vh - 40px);overflow:auto;padding:0;border-radius:15px;border:solid 1px ${theme.colors.gray500};display:flex;flex-direction:column;`;
var Inner4 = styled28("div").withConfig({
  componentId: "sc-yyq0mi-1"
})`flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 40px;text-align:center;color:${theme.colors.gray900};@media (max-width:500px){padding:60px 20px;}`;
var Title9 = styled28(RadixDialogDialog3.Title).withConfig({
  componentId: "sc-yyq0mi-2"
})`margin:0;padding:0 0 8px 0;font-size:20px;font-weight:600;`;
var Description7 = styled28(RadixDialogDialog3.Description).withConfig({
  componentId: "sc-yyq0mi-3"
})`margin:0;max-width:300px;font-size:14px;font-weight:500;line-height:1.5;color:${theme.colors.gray600};`;
var Children = styled28("div").withConfig({
  componentId: "sc-yyq0mi-4"
})`width:100%;margin-top:48px;display:flex;flex-direction:column;align-items:center;`;
var FocusDialog = ({
  title,
  description,
  children,
  isOpen,
  onOpenChange,
  width = 420,
  minHeight = 600
}) => {
  let zIndex = useDialogZIndex(isOpen);
  let contentRef = useRef11(null);
  let autoFocus = useModalAutoFocus(contentRef, isOpen);
  return /* @__PURE__ */ React34.createElement(DialogProvider, { value: {
    isOpen,
    onOpenChange,
    contentRef
  } }, /* @__PURE__ */ React34.createElement(RadixDialogDialog3.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React34.createElement(RadixDialogDialog3.Portal, null, /* @__PURE__ */ React34.createElement(Overlay5, { style: {
    zIndex
  } }), /* @__PURE__ */ React34.createElement(Wrapper12, { ref: autoFocus.ref, onOpenAutoFocus: autoFocus.onOpenAutoFocus, onPointerDownOutside: preventDialogDismissForSelectInteraction, onInteractOutside: preventDialogDismissForSelectInteraction, onFocusOutside: preventDialogDismissForSelectInteraction, style: {
    zIndex: zIndex + 1,
    maxWidth: width,
    minHeight: `min(${minHeight}px, calc(100vh - 40px))`
  } }, /* @__PURE__ */ React34.createElement(Inner4, null, /* @__PURE__ */ React34.createElement(Title9, null, title), description && /* @__PURE__ */ React34.createElement(Description7, null, description), children && /* @__PURE__ */ React34.createElement(Children, null, children)), /* @__PURE__ */ React34.createElement(RadixDialogDialog3.Close, { asChild: true }, /* @__PURE__ */ React34.createElement(Close2, { "aria-label": "Close" }, /* @__PURE__ */ React34.createElement(RiCloseLine2, { size: 16 })))))));
};

// src/dialog/largePanel.tsx
import * as RadixDialogDialog4 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine3 } from "@remixicon/react";
import React35, { useRef as useRef12 } from "react";
import { styled as styled29 } from "styled-components";
var PanelContent = styled29(Content16).withConfig({
  componentId: "sc-1y70n6v-0"
})`display:flex;flex-direction:column;max-width:90vw;width:min(max(400px,80vw),1100px);height:min(90vh,850px);max-height:90vh;box-sizing:border-box;padding:0;overflow:hidden;`;
var PanelBody = styled29.div.withConfig({
  componentId: "sc-1y70n6v-1"
})`display:flex;flex:1;min-width:0;min-height:0;width:100%;overflow:hidden;`;
var LargePanelDialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    closeButton = true
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef12(null);
    let autoFocus = useModalAutoFocus(contentRef, isOpen);
    return /* @__PURE__ */ React35.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React35.createElement(RadixDialogDialog4.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React35.createElement(RadixDialogDialog4.Portal, null, /* @__PURE__ */ React35.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React35.createElement(PanelContent, { ref: autoFocus.ref, onOpenAutoFocus: autoFocus.onOpenAutoFocus, onPointerDownOutside: preventDialogDismissForSelectInteraction, onInteractOutside: preventDialogDismissForSelectInteraction, onFocusOutside: preventDialogDismissForSelectInteraction, style: {
      ...style,
      zIndex: zIndex + 1
    } }, /* @__PURE__ */ React35.createElement(PanelBody, null, children), closeButton && /* @__PURE__ */ React35.createElement(RadixDialogDialog4.Close, { asChild: true }, /* @__PURE__ */ React35.createElement(Close2, { "aria-label": "Close" }, /* @__PURE__ */ React35.createElement(RiCloseLine3, { size: 16 })))))));
  }
};

// src/dialog/panel.tsx
import * as RadixDialogDialog5 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine4 } from "@remixicon/react";
import React36, { useRef as useRef13 } from "react";
import { styled as styled30 } from "styled-components";
var Wrapper13 = styled30(ContentSide).withConfig({
  componentId: "sc-4b717a-0"
})`transition:width 0.2s ease;overflow-y:auto;overflow-x:hidden;overscroll-behavior:contain;`;
var Header4 = styled30("header").withConfig({
  componentId: "sc-4b717a-1"
})`display:flex;padding:15px 20px;border-bottom:1px solid ${theme.colors.gray400};background:rgba(255,255,255,0.7);z-index:9999;backdrop-filter:blur(10px);justify-content:space-between;align-items:center;position:sticky;top:0;border-top-right-radius:var(--dialog-radius);border-top-left-radius:var(--dialog-radius);`;
var Content17 = styled30("main").withConfig({
  componentId: "sc-4b717a-2"
})`display:flex;flex-direction:column;flex:1 0 auto;padding:15px 20px;`;
var Title11 = styled30(RadixDialogDialog5.Title).withConfig({
  componentId: "sc-4b717a-3"
})`padding:0;font-size:20px;font-weight:600;`;
var Description9 = styled30(RadixDialogDialog5.Description).withConfig({
  componentId: "sc-4b717a-4"
})`font-size:14px;font-weight:500;color:${theme.colors.gray600};margin-top:4px;`;
var Actions3 = styled30("div").withConfig({
  componentId: "sc-4b717a-5"
})`display:flex;justify-content:flex-end;gap:10px;padding:0;`;
var Panel = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    width
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef13(null);
    let autoFocus = useModalAutoFocus(contentRef, isOpen);
    return /* @__PURE__ */ React36.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React36.createElement(RadixDialogDialog5.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React36.createElement(RadixDialogDialog5.Portal, null, /* @__PURE__ */ React36.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React36.createElement(Wrapper13, { ref: autoFocus.ref, onOpenAutoFocus: autoFocus.onOpenAutoFocus, onPointerDownOutside: preventDialogDismissForSelectInteraction, onInteractOutside: preventDialogDismissForSelectInteraction, onFocusOutside: preventDialogDismissForSelectInteraction, style: {
      ...style,
      width,
      zIndex: zIndex + 1
    } }, children))));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React36.createElement(Title11, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React36.createElement(Description9, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React36.createElement(Actions3, null, children);
  },
  Content: ({
    children
  }) => {
    return /* @__PURE__ */ React36.createElement(Content17, null, children);
  },
  Header: ({
    children
  }) => {
    return /* @__PURE__ */ React36.createElement(Header4, null, /* @__PURE__ */ React36.createElement("div", null, children), /* @__PURE__ */ React36.createElement(RadixDialogDialog5.Close, { asChild: true }, /* @__PURE__ */ React36.createElement(Button, { type: "button", "aria-label": "Close", iconLeft: /* @__PURE__ */ React36.createElement(RiCloseLine4, null) })));
  }
};

// src/durationInput/index.tsx
import React40, { useEffect as useEffect14, useId as useId6, useRef as useRef14, useState as useState20 } from "react";
import { styled as styled34 } from "styled-components";

// src/numberInput/field.tsx
import { NumberField } from "@base-ui/react/number-field";
import { RiAddLine, RiSubtractLine } from "@remixicon/react";
import React37 from "react";
import { css as css5, styled as styled31 } from "styled-components";
var Root15 = styled31(NumberField.Root).withConfig({
  componentId: "sc-1oykzlj-0"
})`width:100%;min-width:0;`;
var Group3 = styled31(NumberField.Group).withConfig({
  componentId: "sc-1oykzlj-1"
})`${inputSurfaceStyles} &[data-disabled]{opacity:0.6;cursor:not-allowed;}`;
var Field = styled31(NumberField.Input).withConfig({
  componentId: "sc-1oykzlj-2"
})`${inputFieldStyles} min-width:0;font-variant-numeric:tabular-nums;&:disabled{cursor:not-allowed;}`;
var Unit = styled31("span").withConfig({
  componentId: "sc-1oykzlj-3"
})`flex-shrink:0;font-size:14px;color:${theme.colors.gray600};white-space:nowrap;user-select:none;`;
var StepButtons = styled31("div").withConfig({
  componentId: "sc-1oykzlj-4"
})`display:flex;align-items:center;align-self:stretch;gap:2px;padding:4px;flex-shrink:0;`;
var stepButtonStyles = css5`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-radius: ${({
  $radius
}) => $radius};
  background: transparent;
  color: ${theme.colors.gray700};
  cursor: pointer;
  /* BlackOS: no motion on the stepper buttons. */
  transition: none;

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${theme.colors.gray450};
    color: ${theme.colors.gray900};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
var DecrementButton = styled31(NumberField.Decrement).withConfig({
  componentId: "sc-1oykzlj-5"
})`${stepButtonStyles}`;
var IncrementButton = styled31(NumberField.Increment).withConfig({
  componentId: "sc-1oykzlj-6"
})`${stepButtonStyles}`;
var NumberInputField = ({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  unit,
  min,
  max,
  step = 1,
  largeStep,
  stepper,
  format,
  size = "3",
  placeholder,
  name,
  disabled,
  readOnly,
  required,
  autoFocus
}) => {
  let sizeStyles = getButtonSize(size);
  let horizontalPadding = sizeStyles.padding.split(" ")[1];
  let stepButtonRadius = `calc(${sizeStyles.borderRadius} - 4px)`;
  return /* @__PURE__ */ React37.createElement(Root15, { id, value, onValueChange: (nextValue) => onChange(nextValue), min, max, step, largeStep, format, name, disabled, readOnly, required }, /* @__PURE__ */ React37.createElement(Group3, { style: {
    height: sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    outline: invalid ? `1px solid ${theme.colors.red900}` : void 0
  } }, /* @__PURE__ */ React37.createElement(Field, { placeholder, autoFocus, onBlur, "aria-invalid": invalid || void 0, style: {
    padding: sizeStyles.padding,
    paddingRight: unit || stepper ? 0 : void 0,
    fontSize: "14px"
  } }), unit && /* @__PURE__ */ React37.createElement(Unit, { style: {
    paddingRight: stepper ? 0 : horizontalPadding
  } }, unit), stepper && /* @__PURE__ */ React37.createElement(StepButtons, null, /* @__PURE__ */ React37.createElement(DecrementButton, { "aria-label": "Decrease", $radius: stepButtonRadius }, /* @__PURE__ */ React37.createElement(RiSubtractLine, null)), /* @__PURE__ */ React37.createElement(IncrementButton, { "aria-label": "Increase", $radius: stepButtonRadius }, /* @__PURE__ */ React37.createElement(RiAddLine, null)))));
};

// src/select/index.tsx
import * as RadixSelect from "@radix-ui/react-select";
import { VisuallyHidden as VisuallyHidden5 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine3, RiArrowUpSLine, RiCheckLine as RiCheckLine3 } from "@remixicon/react";
import React38, { useEffect as useEffect13, useId as useId4, useMemo as useMemo7, useState as useState19 } from "react";
import { keyframes as keyframes13, styled as styled32 } from "styled-components";
var fadeIn5 = keyframes13`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger10 = styled32(RadixSelect.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-d1sfay-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:none;border:none;font-weight:500;flex-shrink:0;&:focus{background:${theme.colors.gray300};outline:none;}&:focus-visible{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content19 = styled32(RadixSelect.Content).withConfig({
  componentId: "sc-d1sfay-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;box-shadow:${theme.shadows.medium};background:${theme.colors.gray200};border:1px solid ${theme.colors.gray400};font-size:14px;font-weight:500;border-radius:10px;gap:5px;&[data-state='open']{animation:${fadeIn5} 0.15s ease forwards;}`;
var Separator5 = styled32(RadixSelect.Separator).withConfig({
  componentId: "sc-d1sfay-2"
})`height:1;background:${theme.colors.gray300};margin:5px 0;`;
var Item10 = styled32(RadixSelect.Item).withConfig({
  componentId: "sc-d1sfay-3"
})`font-size:14;font-weight:500;display:flex;align-items:center;gap:5px;padding:5px 10px 5px 5px;border-radius:6;outline:none;cursor:pointer;height:30px;border-radius:5px;&:hover{background:${theme.colors.gray300};}&:focus{background:${theme.colors.gray300};}&[data-state='checked']{background:${theme.colors.gray400};}&:disabled,&[data-disabled]{opacity:0.5;cursor:not-allowed;}`;
var Indicator4 = styled32(RadixSelect.ItemIndicator).withConfig({
  componentId: "sc-d1sfay-4"
})`display:flex;align-items:center;justify-content:center;width:20px;height:20px;opacity:0.5;`;
var Select = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId4();
  let sizeStyles = getButtonSize(size);
  let normalizedValue = value === "" ? void 0 : value;
  let [isOpen, setIsOpen] = useState19(false);
  let zIndex = useZindex(isOpen);
  useSuppressTooltipWhileOpen(isOpen);
  let disabledItems = useMemo7(() => items.filter((item) => item.disabled).map((item) => item.id).sort().join(","), [items]);
  useEffect13(() => {
    let disabledSelected = items.find((item) => item.id === normalizedValue && item.disabled);
    if (disabledSelected) {
      let notDisabled = items.find((item) => !item.disabled);
      if (notDisabled) onChange?.(notDisabled.id);
    }
  }, [normalizedValue, disabledItems, items, onChange]);
  return /* @__PURE__ */ React38.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    textAlign: "initial"
  } }, label && (hideLabel ? /* @__PURE__ */ React38.createElement(VisuallyHidden5, null, /* @__PURE__ */ React38.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React38.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React38.createElement(InputDescription, null, description), /* @__PURE__ */ React38.createElement(
    RadixSelect.Root,
    {
      value: value === void 0 ? void 0 : normalizedValue ?? "",
      onValueChange: onChange,
      open: isOpen,
      onOpenChange: setIsOpen
    },
    /* @__PURE__ */ React38.createElement(Trigger10, { id, disabled, "aria-label": label, style: {
      ...sizeStyles,
      fontSize: 14
    } }, /* @__PURE__ */ React38.createElement(RadixSelect.Value, { placeholder }), /* @__PURE__ */ React38.createElement(RadixSelect.Icon, null, /* @__PURE__ */ React38.createElement(RiArrowDownSLine3, { size: 14, style: {
      opacity: 0.5
    } }))),
    /* @__PURE__ */ React38.createElement(RadixSelect.Portal, null, /* @__PURE__ */ React38.createElement(Content19, { "data-metorial-select-content": "true", style: {
      zIndex
    }, onPointerDownOutside: markSelectPointerDismiss }, /* @__PURE__ */ React38.createElement(RadixSelect.ScrollUpButton, { className: "SelectScrollButton" }, /* @__PURE__ */ React38.createElement(RiArrowUpSLine, { size: 14 })), /* @__PURE__ */ React38.createElement(RadixSelect.Viewport, { className: "SelectViewport" }, items.map((item, i) => item.type == "separator" ? /* @__PURE__ */ React38.createElement(Separator5, { key: i }) : /* @__PURE__ */ React38.createElement(Item10, { value: item.id, key: i, disabled: item.disabled }, /* @__PURE__ */ React38.createElement("div", { style: {
      width: 20
    } }, /* @__PURE__ */ React38.createElement(Indicator4, null, /* @__PURE__ */ React38.createElement(RiCheckLine3, { size: 12, strokeWidth: 3.5 }))), /* @__PURE__ */ React38.createElement(RadixSelect.ItemText, null, item.label)))), /* @__PURE__ */ React38.createElement(RadixSelect.ScrollDownButton, { className: "SelectScrollButton" }, /* @__PURE__ */ React38.createElement(RiArrowDownSLine3, { size: 14 }))))
  ), error && /* @__PURE__ */ React38.createElement(Error, null, error));
};

// src/switch/index.tsx
import * as RadixSwitch from "@radix-ui/react-switch";
import { VisuallyHidden as VisuallyHidden6 } from "@radix-ui/react-visually-hidden";
import clsx2 from "clsx";
import React39, { createRef, useId as useId5 } from "react";
import { styled as styled33 } from "styled-components";
var Wrapper14 = styled33("div").withConfig({
  componentId: "sc-xrz9xh-0"
})`display:flex;gap:10px;align-items:center;cursor:${({
  $readOnly,
  $disabled
}) => {
  if ($readOnly) return "default";
  if ($disabled) return "not-allowed";
  return "pointer";
}};`;
var Root18 = styled33(RadixSwitch.Root).withConfig({
  componentId: "sc-xrz9xh-1"
})`display:flex;height:20px;width:30px;padding:0;justify-content:center;align-items:center;border-radius:50px;background:${theme.colors.gray300};border:solid 1px ${theme.colors.gray300};position:relative;transition:none;flex-shrink:0;&:focus{border:solid 1px ${theme.colors.blue900};}&[data-state='checked']{background:${theme.colors.blue900};}&.disabled{opacity:0.5;}`;
var Thumb2 = styled33(RadixSwitch.Thumb).withConfig({
  componentId: "sc-xrz9xh-2"
})`display:flex;height:14px;width:14px;border-radius:500;background:${theme.colors.background};box-shadow:none;position:absolute;transition:none;border-radius:50px;top:2px;&[data-state='checked']{left:12px;}&[data-state='unchecked']{left:2px;}`;
var Switch = ({
  checked,
  onCheckedChange,
  label,
  disabled,
  readOnly,
  description,
  hideLabel
}) => {
  let id = useId5();
  let root = createRef();
  let isInteractive = !disabled && !readOnly;
  return /* @__PURE__ */ React39.createElement(React39.Fragment, null, /* @__PURE__ */ React39.createElement(Wrapper14, { $readOnly: readOnly, $disabled: disabled }, /* @__PURE__ */ React39.createElement(Root18, { className: clsx2({
    disabled: disabled && !readOnly
  }), id, checked, onCheckedChange, disabled: disabled || readOnly, ref: root }, /* @__PURE__ */ React39.createElement(Thumb2, null)), /* @__PURE__ */ React39.createElement("div", { onClick: (e) => {
    if (!isInteractive) return;
    root.current?.click();
  }, style: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "4px"
  } }, hideLabel ? /* @__PURE__ */ React39.createElement(VisuallyHidden6, null, /* @__PURE__ */ React39.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React39.createElement(InputLabel, { htmlFor: id, style: {
    margin: 0
  }, onClick: (e) => e.stopPropagation() }, label), description && /* @__PURE__ */ React39.createElement(InputDescription, { style: {
    marginTop: 0,
    marginBottom: 0
  } }, description))));
};

// src/durationInput/lib.ts
var durationUnitSeconds = {
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  days: 24 * 60 * 60
};
var defaultDurationUnits = ["seconds", "minutes", "hours", "days"];
var sortUnitsAscending = (units) => [...units].sort((a, b) => durationUnitSeconds[a] - durationUnitSeconds[b]);
var pickDurationUnit = (seconds, units = defaultDurationUnits) => {
  let ascending = sortUnitsAscending(units.length ? units : defaultDurationUnits);
  if (!seconds) return ascending.includes("minutes") ? "minutes" : ascending[0];
  let evenUnit = [...ascending].reverse().find((unit) => seconds % durationUnitSeconds[unit] == 0);
  return evenUnit ?? ascending[0];
};
var getDurationParts = (seconds, units = defaultDurationUnits) => {
  let unit = pickDurationUnit(seconds, units);
  return {
    amount: seconds / durationUnitSeconds[unit],
    unit
  };
};

// src/durationInput/index.tsx
var unitLabels = {
  seconds: {
    one: "Second",
    other: "Seconds"
  },
  minutes: {
    one: "Minute",
    other: "Minutes"
  },
  hours: {
    one: "Hour",
    other: "Hours"
  },
  days: {
    one: "Day",
    other: "Days"
  }
};
var Wrapper15 = styled34("div").withConfig({
  componentId: "sc-jvnfmf-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var Row = styled34("div").withConfig({
  componentId: "sc-jvnfmf-1"
})`display:flex;gap:8px;width:100%;`;
var UnitSelect = styled34("div").withConfig({
  componentId: "sc-jvnfmf-2"
})`width:130px;flex-shrink:0;`;
var DurationInput = ({
  label,
  value,
  onChange,
  description,
  hideLabel,
  error,
  units = defaultDurationUnits,
  min,
  max,
  allowNever,
  neverLabel = "Never",
  defaultValue,
  size = "3",
  disabled,
  style
}) => {
  let id = useId6();
  let [unit, setUnit] = useState20(() => pickDurationUnit(value, units));
  let lastEmittedRef = useRef14(value);
  let lastDurationRef = useRef14(value);
  let unitSeconds = durationUnitSeconds[unit];
  let isNever = value === null;
  let amount = value === null ? null : value / unitSeconds;
  useEffect14(() => {
    if (value !== null) lastDurationRef.current = value;
    if (value === lastEmittedRef.current || value === null) return;
    lastEmittedRef.current = value;
    if (value % durationUnitSeconds[unit] != 0) setUnit(pickDurationUnit(value, units));
  }, [value]);
  let emit = (nextValue) => {
    lastEmittedRef.current = nextValue;
    onChange(nextValue);
  };
  let setNever = (never) => {
    if (never) return emit(null);
    let restored = lastDurationRef.current ?? defaultValue ?? Math.max(min ?? 0, unitSeconds);
    emit(restored);
  };
  return /* @__PURE__ */ React40.createElement(Wrapper15, { style }, /* @__PURE__ */ React40.createElement(InputHeader, { id, label, hideLabel, description, help: allowNever ? /* @__PURE__ */ React40.createElement(Switch, { label: neverLabel, checked: isNever, onCheckedChange: setNever, disabled }) : void 0 }), /* @__PURE__ */ React40.createElement(Row, null, /* @__PURE__ */ React40.createElement(NumberInputField, { id, value: amount, onChange: (nextAmount) => {
    if (nextAmount === null) return;
    emit(Math.round(nextAmount * unitSeconds));
  }, min: min === void 0 ? void 0 : min / unitSeconds, max: max === void 0 ? void 0 : max / unitSeconds, step: "any", format: {
    maximumFractionDigits: 2
  }, placeholder: isNever ? neverLabel : void 0, invalid: !!error, size, disabled: disabled || isNever }), /* @__PURE__ */ React40.createElement(UnitSelect, null, /* @__PURE__ */ React40.createElement(Select, { label: "Unit", hideLabel: true, size, value: unit, disabled: disabled || isNever, items: units.map((item) => ({
    id: item,
    label: amount === 1 ? unitLabels[item].one : unitLabels[item].other
  })), onChange: (nextUnit) => {
    let next = nextUnit;
    setUnit(next);
    if (amount !== null) emit(Math.round(amount * durationUnitSeconds[next]));
  } }))), typeof error == "string" && /* @__PURE__ */ React40.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};

// src/entity/content.tsx
import React41 from "react";
import { styled as styled35 } from "styled-components";

// src/entity/context.ts
import { createContext, useContext as useContext3 } from "react";
var EntityContext = createContext({
  aligned: false
});
var useEntityContext = () => useContext3(EntityContext);

// src/entity/content.tsx
var Wrapper16 = styled35("main").withConfig({
  componentId: "sc-d0rsg6-0"
})`display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;padding:0px 20px;@media (max-width:600px){padding:0px;flex-direction:column;flex-wrap:nowrap;}&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var ContentEntityWrapper = styled35("div").withConfig({
  shouldForwardProp: (prop) => prop !== "$aligned"
}).withConfig({
  componentId: "sc-d0rsg6-1"
})`display:flex;${({
  $aligned
}) => $aligned ? `
        flex: 1 1 0;
        min-width: 0;
      ` : `
        flex-grow: 1;
      `}`;
var MobileBorderStyle = styled35(ContentEntityWrapper).withConfig({
  componentId: "sc-d0rsg6-2"
})`@media (max-width:600px){border-bottom:1px solid ${theme.colors.gray300};}`;
var ContentEntity = ({
  children,
  last,
  aligned
}) => {
  let Element3 = last ? ContentEntityWrapper : MobileBorderStyle;
  return /* @__PURE__ */ React41.createElement(Element3, { $aligned: aligned }, children);
};
var EntityContent = ({
  children,
  aligned
}) => {
  let context = useEntityContext();
  let isAligned = aligned ?? context.aligned;
  let childArray = Array.isArray(children) ? children : [children];
  return /* @__PURE__ */ React41.createElement(Wrapper16, null, childArray.map((child, i) => /* @__PURE__ */ React41.createElement(ContentEntity, { key: i, last: i == childArray.length - 1, aligned: isAligned }, child)));
};

// src/entity/contentRaw.tsx
import { styled as styled36 } from "styled-components";
var EntityContentRaw = styled36("section").withConfig({
  componentId: "sc-otha5r-0"
})`padding:10px 20px;display:flex;flex-direction:column;flex-wrap:wrap;flex:1;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;

// src/entity/field.tsx
import React43 from "react";
import { styled as styled37 } from "styled-components";

// src/spacer/index.tsx
import React42 from "react";
var Spacer = ({
  size,
  height,
  width,
  grow,
  shrink = false
}) => {
  return /* @__PURE__ */ React42.createElement("div", { style: {
    height: height ?? size,
    width: width ?? size,
    flexGrow: !height && !width && !size || grow ? 1 : void 0,
    flexShrink: shrink === false ? 0 : shrink ? 1 : void 0
  } });
};

// src/entity/field.tsx
var Wrapper17 = styled37("div").withConfig({
  componentId: "sc-13k0pcr-0"
})`display:flex;padding:20px 0px;align-items:center;@media (max-width:600px){padding:20px;}`;
var Content20 = styled37("div").withConfig({
  componentId: "sc-13k0pcr-1"
})`display:flex;flex-direction:column;`;
var Title12 = styled37("h1").withConfig({
  componentId: "sc-13k0pcr-2"
})`font-size:14px;font-weight:600;line-height:1;`;
var Description10 = styled37("p").withConfig({
  componentId: "sc-13k0pcr-3"
})`font-size:12px;font-weight:400;color:${theme.colors.gray700};margin-top:4px;line-height:1;`;
var EntityField = ({
  title,
  description,
  value,
  width,
  children,
  position,
  style,
  right,
  prefix,
  suffix,
  prefixNoSpace,
  suffixNoSpace,
  skeleton
}) => {
  if (!position) position = "grow";
  if (!description) description = value;
  return /* @__PURE__ */ React43.createElement(Wrapper17, { style: {
    width,
    flex: position == "grow" ? 1 : void 0,
    textAlign: right ? "right" : void 0,
    ...style
  } }, right && /* @__PURE__ */ React43.createElement(Spacer, null), prefix && /* @__PURE__ */ React43.createElement("div", { style: {
    marginRight: prefixNoSpace ? 0 : 10
  } }, prefix), /* @__PURE__ */ React43.createElement(Content20, null, children ? children : /* @__PURE__ */ React43.createElement(React43.Fragment, null, /* @__PURE__ */ React43.createElement(Title12, null, title), description && /* @__PURE__ */ React43.createElement(React43.Fragment, null, /* @__PURE__ */ React43.createElement(Spacer, { height: 3 }), /* @__PURE__ */ React43.createElement(Description10, null, description)))), suffix && /* @__PURE__ */ React43.createElement("div", { style: {
    marginLeft: suffixNoSpace ? 0 : 10
  } }, suffix));
};

// src/entity/footer.tsx
import React44 from "react";
import { styled as styled38 } from "styled-components";
var Footer3 = styled38("footer").withConfig({
  componentId: "sc-16o8pum-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityFooter = ({
  children
}) => {
  return /* @__PURE__ */ React44.createElement(Footer3, null, children);
};

// src/entity/header.tsx
import React45 from "react";
import { styled as styled39 } from "styled-components";
var Header5 = styled39("header").withConfig({
  componentId: "sc-rim8id-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityHeader = ({
  children
}) => {
  return /* @__PURE__ */ React45.createElement(Header5, null, children);
};

// src/entity/wrapper.tsx
import React46, { useMemo as useMemo8 } from "react";
import { styled as styled40 } from "styled-components";
var Wrapper18 = styled40("div").withConfig({
  shouldForwardProp: (prop) => prop !== "$hasHeader"
}).withConfig({
  componentId: "sc-15ldyue-0"
})`border-radius:${({
  $hasHeader
}) => $hasHeader ? "8px" : "12px"};border:1px solid ${theme.colors.gray400};box-shadow:${theme.shadows.small};display:flex;flex-direction:column;position:relative;color:${theme.colors.foreground};${({
  $hasHeader
}) => $hasHeader ? `
        background: ${theme.colors.gray100};
      ` : ""}`;
var Header6 = styled40("header").withConfig({
  componentId: "sc-15ldyue-1"
})`padding:6px 18px 0 18px;margin-bottom:6px;`;
var HeaderContent = styled40("div").withConfig({
  componentId: "sc-15ldyue-2"
})`background:${theme.colors.background};border:1px solid ${theme.colors.gray400};border-radius:8px;overflow:hidden;margin-left:-1px;margin-right:-1px;margin-bottom:-1px;`;
var EntityWrapper = ({
  children,
  header,
  style,
  aligned,
  skeleton
}) => {
  let contextValue = useMemo8(() => ({
    aligned: !!aligned
  }), [aligned]);
  let hasHeader = !!header;
  return /* @__PURE__ */ React46.createElement(EntityContext.Provider, { value: contextValue }, /* @__PURE__ */ React46.createElement(Wrapper18, { style, $hasHeader: hasHeader }, hasHeader && /* @__PURE__ */ React46.createElement(Header6, null, /* @__PURE__ */ React46.createElement(Title3, { as: "h2", size: "2", weight: "strong" }, header)), hasHeader ? /* @__PURE__ */ React46.createElement(HeaderContent, null, children) : children));
};

// src/entity/index.ts
var Entity = {
  Wrapper: EntityWrapper,
  Footer: EntityFooter,
  Field: EntityField,
  Content: EntityContent,
  Header: EntityHeader,
  ContentRaw: EntityContentRaw
};

// src/flex/index.tsx
import React47 from "react";
var Flex = ({
  children,
  direction = "row",
  align,
  justify,
  wrap = "nowrap",
  gap = 0,
  ...props
}) => {
  return /* @__PURE__ */ React47.createElement("div", { ...props, style: {
    display: "flex",
    flexDirection: direction,
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap,
    gap,
    ...props.style
  } }, children);
};

// src/group/index.tsx
import React48, { useContext as useContext4 } from "react";
import { styled as styled41 } from "styled-components";
var GroupWrapper = styled41("div").withConfig({
  componentId: "sc-h0wdxo-0"
})`border:1px solid ${theme.colors.gray400};box-shadow:${theme.shadows.small};background:${theme.colors.gray100};border-radius:8px;overflow:hidden;`;
var IsGroupContext = React48.createContext(false);
var useIsGroup = () => useContext4(IsGroupContext);
var Group4 = {
  Wrapper: (props) => /* @__PURE__ */ React48.createElement(IsGroupContext.Provider, { value: true }, /* @__PURE__ */ React48.createElement(GroupWrapper, null, props.children)),
  Header: (props) => /* @__PURE__ */ React48.createElement(Group4.HeaderRow, null, /* @__PURE__ */ React48.createElement("div", { style: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6
  } }, /* @__PURE__ */ React48.createElement("div", { style: {
    display: "flex",
    flexDirection: "column"
  } }, /* @__PURE__ */ React48.createElement(Title3, { as: "h2", size: "2", weight: "strong" }, props.title), props.description && /* @__PURE__ */ React48.createElement(Text, { size: "1", weight: "strong", color: "gray600" }, props.description)), /* @__PURE__ */ React48.createElement("div", { style: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10
  } }, props.actions))),
  HeaderRow: styled41("header").withConfig({
    componentId: "sc-h0wdxo-1"
  })`padding:10px 20px;display:flex;flex-direction:column;`,
  Row: styled41("main").withConfig({
    componentId: "sc-h0wdxo-2"
  })`display:flex;flex-direction:column;border-top:1px solid ${theme.colors.gray400};`,
  Content: styled41("div").withConfig({
    componentId: "sc-h0wdxo-3"
  })`padding:12px 17px;display:flex;flex-direction:column;background:${theme.colors.background};border-radius:8px;border:1px solid ${theme.colors.gray400};margin-left:-1px;margin-right:-1px;margin-bottom:-1px;overflow:hidden;`,
  Footer: styled41("footer").withConfig({
    componentId: "sc-h0wdxo-4"
  })`padding:10px 15px;display:flex;align-items:center;`
};

// src/info/index.tsx
import { RiInformation2Line } from "@remixicon/react";
import React49 from "react";
import { styled as styled42 } from "styled-components";
var Button2 = styled42("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-wuucny-0"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};& > div{border-radius:5px;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}}&:hover,&:focus{& > div{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InfoTooltip = (props) => {
  return /* @__PURE__ */ React49.createElement(Tooltip, { content: props.children }, /* @__PURE__ */ React49.createElement(Button2, null, /* @__PURE__ */ React49.createElement("div", { style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  } }, /* @__PURE__ */ React49.createElement(RiInformation2Line, null))));
};

// src/inlineCopy/index.tsx
import { RiCheckLine as RiCheckLine4, RiFileCopy2Line } from "@remixicon/react";
import copy2 from "copy-to-clipboard";
import React50, { useRef as useRef15, useState as useState21 } from "react";
import { toast as toast2 } from "sonner";
import { styled as styled43 } from "styled-components";
var IconBox = styled43("div").withConfig({
  componentId: "sc-2zba5c-0"
})`border-radius:5px;display:flex;align-items:center;justify-content:center;overflow:hidden;position:relative;transition:none;`;
var IconLayer = styled43("span").withConfig({
  componentId: "sc-2zba5c-1"
})`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}`;
var Button3 = styled43("button").withConfig({
  componentId: "sc-2zba5c-2"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};&:hover,&:focus{${IconBox}{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InlineCopy = (props) => {
  let [copied, setCopied] = useState21(false);
  let copiedToRef = useRef15(null);
  if (!props.value) return null;
  return /* @__PURE__ */ React50.createElement(Tooltip, { content: props.tooltip ?? "Copy" }, /* @__PURE__ */ React50.createElement(Button3, { type: "button", onClick: (e) => {
    e.stopPropagation();
    e.preventDefault();
    copy2(props.value);
    toast2.success("Copied to clipboard");
    setCopied(true);
    clearTimeout(copiedToRef.current);
    copiedToRef.current = setTimeout(() => {
      setCopied(false);
    }, 2e3);
  } }, /* @__PURE__ */ React50.createElement(IconBox, { "data-copied": copied, style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  } }, /* @__PURE__ */ React50.createElement(IconLayer, { key: copied ? "copied" : "copy" }, copied ? /* @__PURE__ */ React50.createElement(RiCheckLine4, null) : /* @__PURE__ */ React50.createElement(RiFileCopy2Line, null)))));
};

// src/keyValueInput/index.tsx
import { RiAddLine as RiAddLine2, RiDeleteBin4Line, RiEyeLine, RiEyeOffLine } from "@remixicon/react";
import React51, { useState as useState22 } from "react";
import { styled as styled44 } from "styled-components";

// src/keyValueInput/lib.ts
var unquote = (raw) => {
  let value = raw.trim();
  if (value.length < 2) return value;
  let quote = value[0];
  if (quote != '"' && quote != "'" || value[value.length - 1] != quote) return value;
  let inner = value.slice(1, -1);
  if (quote == "'") return inner;
  return inner.replace(/\\(["\\n])/g, (_, char) => char == "n" ? "\n" : char);
};
var parseKeyValueText = (text) => {
  let entries = [];
  for (let line of text.split(/\r?\n/)) {
    let trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    let assignment = trimmed.replace(/^export\s+/, "");
    let separatorIndex = assignment.indexOf("=");
    if (separatorIndex <= 0) return null;
    let key = assignment.slice(0, separatorIndex).trim();
    if (!key || /\s/.test(key)) return null;
    entries.push({
      key,
      value: unquote(assignment.slice(separatorIndex + 1))
    });
  }
  return entries.length ? entries : null;
};
var getDuplicateKeys = (entries) => {
  let seen = /* @__PURE__ */ new Set();
  let duplicates = /* @__PURE__ */ new Set();
  for (let entry of entries) {
    if (!entry.key) continue;
    if (seen.has(entry.key)) duplicates.add(entry.key);
    seen.add(entry.key);
  }
  return duplicates;
};
var insertKeyValueEntries = (entries, index, inserted, maxRows) => {
  let target = entries[index];
  let replacesTarget = !target || !target.key && !target.value;
  let next = [...entries.slice(0, index + (replacesTarget ? 0 : 1)), ...inserted, ...entries.slice(index + 1)];
  if (maxRows === void 0 || next.length <= maxRows) {
    return {
      entries: next,
      truncated: false
    };
  }
  return {
    entries: next.slice(0, maxRows),
    truncated: true
  };
};
var keyValueEntriesToRecord = (entries) => {
  let record = {};
  for (let entry of entries) {
    let key = entry.key.trim();
    if (key) record[key] = entry.value;
  }
  return record;
};
var recordToKeyValueEntries = (record) => Object.entries(record ?? {}).map(([key, value]) => ({
  key,
  value
}));

// src/keyValueInput/index.tsx
var emptyEntry = {
  key: "",
  value: ""
};
var Wrapper19 = styled44("div").withConfig({
  componentId: "sc-1yap1rr-0"
})`display:flex;flex-direction:column;width:100%;`;
var Rows = styled44("div").withConfig({
  componentId: "sc-1yap1rr-1"
})`display:flex;flex-direction:column;gap:8px;`;
var Columns = styled44("div").withConfig({
  componentId: "sc-1yap1rr-2"
})`display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.4fr) repeat(${({
  $actions
}) => $actions},${({
  $actionWidth
}) => $actionWidth});gap:8px;align-items:center;`;
var ColumnLabel = styled44(InputLabel).attrs({
  as: "span"
}).withConfig({
  componentId: "sc-1yap1rr-3"
})`margin-bottom:0;`;
var Footer4 = styled44("div").withConfig({
  componentId: "sc-1yap1rr-4"
})`display:flex;align-items:center;gap:10px;margin-top:10px;`;
var Count = styled44("span").withConfig({
  componentId: "sc-1yap1rr-5"
})`font-size:12px;color:${theme.colors.gray600};font-variant-numeric:tabular-nums;`;
var KeyValueInput = ({
  label,
  value,
  onChange,
  description,
  hideLabel,
  keyLabel = "Key",
  valueLabel = "Value",
  keyPlaceholder,
  valuePlaceholder,
  addLabel = "Add",
  maxRows,
  masked,
  error,
  disabled,
  size = "2"
}) => {
  let [revealed, setRevealed] = useState22(() => /* @__PURE__ */ new Set());
  let [focusIndex, setFocusIndex] = useState22(null);
  let [pasteTruncated, setPasteTruncated] = useState22(false);
  let isVirtual = value.length == 0;
  let rows = isVirtual ? [emptyEntry] : value;
  let duplicates = getDuplicateKeys(rows);
  let canAdd = !disabled && (maxRows === void 0 || rows.length < maxRows);
  let actionCount = masked ? 2 : 1;
  let actionWidth = getButtonSize(size).height;
  let updateRow = (index, patch) => {
    onChange(rows.map((row, i) => i == index ? {
      ...row,
      ...patch
    } : row));
  };
  let removeRow = (index) => {
    setRevealed((current) => new Set([...current].filter((i) => i != index).map((i) => i > index ? i - 1 : i)));
    setFocusIndex(null);
    setPasteTruncated(false);
    onChange(rows.filter((_, i) => i != index));
  };
  let addRow = () => {
    if (!canAdd) return;
    setFocusIndex(rows.length);
    onChange([...rows, emptyEntry]);
  };
  let handlePaste = (index, field, e) => {
    let text = e.clipboardData.getData("text");
    if (!text.includes("=")) return;
    if (field == "value" && !/\r?\n/.test(text.trim())) return;
    let parsed = parseKeyValueText(text);
    if (!parsed || field == "value" && parsed.length < 2) return;
    e.preventDefault();
    let result = insertKeyValueEntries(rows, index, parsed, maxRows);
    setPasteTruncated(result.truncated);
    onChange(result.entries);
  };
  let getRowError = (row, index) => {
    if (Array.isArray(error) && error[index]) return error[index];
    let isLaterDuplicate = duplicates.has(row.key) && rows.findIndex((other) => other.key == row.key) < index;
    if (isLaterDuplicate) return `Duplicate key "${row.key}".`;
    return void 0;
  };
  return /* @__PURE__ */ React51.createElement(Wrapper19, null, label && !hideLabel && /* @__PURE__ */ React51.createElement(InputLabel, null, label), description && /* @__PURE__ */ React51.createElement(InputDescription, null, description), /* @__PURE__ */ React51.createElement(Rows, { role: "group", "aria-label": typeof label == "string" ? label : void 0 }, /* @__PURE__ */ React51.createElement(Columns, { $actions: actionCount, $actionWidth: actionWidth, "aria-hidden": true }, /* @__PURE__ */ React51.createElement(ColumnLabel, null, keyLabel), /* @__PURE__ */ React51.createElement(ColumnLabel, null, valueLabel)), rows.map((row, index) => {
    let rowError = getRowError(row, index);
    let isRevealed = revealed.has(index);
    return /* @__PURE__ */ React51.createElement("div", { key: index }, /* @__PURE__ */ React51.createElement(Columns, { $actions: actionCount, $actionWidth: actionWidth }, /* @__PURE__ */ React51.createElement(Input, { label: `${keyLabel} ${index + 1}`, hideLabel: true, size, value: row.key, placeholder: keyPlaceholder, disabled, error: duplicates.has(row.key) || !!rowError, autoFocus: index === focusIndex, autoComplete: "off", spellCheck: false, onChange: (e) => updateRow(index, {
      key: e.target.value
    }), onPaste: (e) => handlePaste(index, "key", e) }), /* @__PURE__ */ React51.createElement(Input, { label: `${valueLabel} ${index + 1}`, hideLabel: true, size, value: row.value, placeholder: valuePlaceholder, disabled, type: masked && !isRevealed ? "password" : "text", autoComplete: "off", spellCheck: false, "data-1p-ignore": true, "data-lpignore": "true", onChange: (e) => updateRow(index, {
      value: e.target.value
    }), onPaste: (e) => handlePaste(index, "value", e), onKeyDown: (e) => {
      if (e.key != "Enter" || index != rows.length - 1) return;
      e.preventDefault();
      addRow();
    } }), masked && /* @__PURE__ */ React51.createElement(Button, { type: "button", size, variant: "soft", title: isRevealed ? "Hide Value" : "Show Value", "aria-label": isRevealed ? "Hide Value" : "Show Value", "aria-pressed": isRevealed, iconLeft: isRevealed ? /* @__PURE__ */ React51.createElement(RiEyeOffLine, null) : /* @__PURE__ */ React51.createElement(RiEyeLine, null), onClick: () => setRevealed((current) => {
      let next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    }) }), /* @__PURE__ */ React51.createElement(Button, { type: "button", size, variant: "soft", title: "Remove", "aria-label": "Remove", iconLeft: /* @__PURE__ */ React51.createElement(RiDeleteBin4Line, null), disabled: disabled || isVirtual, onClick: () => removeRow(index) })), rowError && /* @__PURE__ */ React51.createElement(Error, { size: 12, style: {
      marginTop: 6
    } }, rowError));
  })), /* @__PURE__ */ React51.createElement(Footer4, null, /* @__PURE__ */ React51.createElement(Button, { type: "button", size, variant: "outline", iconLeft: /* @__PURE__ */ React51.createElement(RiAddLine2, null), disabled: !canAdd, onClick: addRow }, addLabel), maxRows !== void 0 && /* @__PURE__ */ React51.createElement(Count, null, rows.length, " / ", maxRows)), pasteTruncated && maxRows !== void 0 && /* @__PURE__ */ React51.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, `Only the first ${maxRows} entries were kept.`), typeof error == "string" && /* @__PURE__ */ React51.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};

// src/linkTabs/index.tsx
import React53, { useMemo as useMemo9 } from "react";

// src/tabs/index.tsx
import { useIsSSR } from "@looped/hooks";
import React52, { useState as useState23 } from "react";
import { useWindowSize } from "react-use";
import { styled as styled45 } from "styled-components";
var Wrapper20 = styled45("div").withConfig({
  componentId: "sc-j2kvo3-0"
})`position:relative;width:100%;user-select:none;`;
var Inner5 = styled45("div").withConfig({
  componentId: "sc-j2kvo3-1"
})`display:flex;padding:0;margin:0;border-bottom:1px solid ${theme.colors.gray400};overflow-x:auto;white-space:nowrap;justify-content:center;scrollbar-color:${theme.colors.gray400} transparent;scrollbar-width:thin;`;
var List3 = styled45("ul").withConfig({
  componentId: "sc-j2kvo3-2"
})`list-style:none;display:flex;max-width:100%;overflow-x:auto;white-space:nowrap;scrollbar-color:${theme.colors.gray400} transparent;scrollbar-width:thin;`;
var Tab = styled45("li").withConfig({
  componentId: "sc-j2kvo3-3"
})`position:relative;z-index:10;button,a{font-size:14px;font-weight:500;color:${theme.colors.gray700};text-decoration:none;display:flex;align-items:center;justify-content:center;transition:none;background:none;border:none;padding:0;&:hover{color:${theme.colors.blue900};}}`;
var LineIndicator = styled45("div").withConfig({
  componentId: "sc-j2kvo3-4"
})`position:absolute;bottom:-1px;height:3px;background:${theme.colors.blue900};transition:none;border-radius:7px;`;
var TabIndicator = styled45("div").withConfig({
  componentId: "sc-j2kvo3-5"
})`position:absolute;transform:translateY(-50%);transition:none;border-radius:7px;z-index:0;`;
var Tabs = ({
  current,
  tabs,
  action,
  tabIndicator = true,
  lineIndicator = true,
  height = 30,
  gap = 20,
  padding: paddingRaw = {},
  margin = {
    bottom: 20,
    top: 0
  },
  variant = "soft",
  maxWidth = "100%"
}) => {
  let Link = getLink();
  let isServer = useIsSSR();
  useWindowSize();
  let padding = {
    left: paddingRaw.left ?? 0,
    right: paddingRaw.right ?? 0,
    top: paddingRaw.top ?? 0,
    bottom: paddingRaw.bottom ?? 6
  };
  let [listRef, setListRef] = useState23(null);
  let [innerRef, setInnerRef] = useState23(null);
  let [wrapperRef, setWrapperRef] = useState23(null);
  let [scrollOffset, setScrollOffset] = useState23(0);
  let [refs, setRefs] = useState23([]);
  let renderIndicators = false;
  let currentIndex = tabs.findIndex((t) => "to" in t ? t.to == current : t.id == current);
  let indicatorOffset = -scrollOffset;
  let indicatorWidth = refs[currentIndex]?.clientWidth ?? 0;
  if (refs[currentIndex] && wrapperRef) {
    renderIndicators = true;
    let tabRect = refs[currentIndex].getBoundingClientRect();
    let wrapperRect = wrapperRef.getBoundingClientRect();
    indicatorOffset = tabRect.left - wrapperRect.left;
    indicatorWidth = tabRect.width;
  }
  let clipIndicator = (indicator) => {
    let overflowThreshold = 10;
    let minLeft = -overflowThreshold;
    let maxRight = (wrapperRef?.clientWidth ?? 0) + overflowThreshold;
    let right = indicator.left + indicator.width;
    let clippedLeft = Math.max(indicator.left, minLeft);
    let clippedRight = Math.min(right, maxRight);
    return {
      left: clippedLeft,
      width: Math.max(clippedRight - clippedLeft, 0),
      visible: clippedRight - clippedLeft >= 30
    };
  };
  let clippedLineIndicator = clipIndicator({
    left: indicatorOffset,
    width: indicatorWidth
  });
  let clippedTabIndicator = clipIndicator({
    left: indicatorOffset - gap / 2,
    width: indicatorWidth + gap
  });
  return /* @__PURE__ */ React52.createElement(Wrapper20, { ref: setWrapperRef }, /* @__PURE__ */ React52.createElement(Inner5, { ref: setInnerRef, onScroll: (e) => setScrollOffset(e.currentTarget.scrollLeft + (listRef?.scrollLeft ?? 0)), style: {
    width: "100%",
    paddingLeft: padding.left,
    paddingRight: padding.right,
    paddingTop: padding.top,
    paddingBottom: padding.bottom,
    marginBottom: margin.bottom,
    marginTop: margin.top
  } }, /* @__PURE__ */ React52.createElement(List3, { ref: setListRef, onScroll: (e) => setScrollOffset((innerRef?.scrollLeft ?? 0) + e.currentTarget.scrollLeft), style: {
    width: maxWidth,
    gap
  } }, tabs.map((item, i) => /* @__PURE__ */ React52.createElement(Tab, { key: i, ref: (el) => {
    setRefs((refs2) => {
      refs2[i] = el;
      return refs2;
    });
  } }, "to" in item ? /* @__PURE__ */ React52.createElement(Link, { to: item.to, onClick: () => action(item.to), style: {
    height
  } }, item.label) : /* @__PURE__ */ React52.createElement("button", { onClick: () => action(item.id), style: {
    height
  }, type: "button" }, item.label))))), renderIndicators && /* @__PURE__ */ React52.createElement(React52.Fragment, null, lineIndicator && clippedLineIndicator.visible && /* @__PURE__ */ React52.createElement(LineIndicator, { style: {
    left: clippedLineIndicator.left,
    width: clippedLineIndicator.width
  } }), tabIndicator && clippedTabIndicator.visible && /* @__PURE__ */ React52.createElement(TabIndicator, { style: {
    left: clippedTabIndicator.left,
    width: clippedTabIndicator.width,
    height,
    top: `calc(50% - ${padding.bottom / 2}px)`,
    ...variant === "soft" && {
      background: theme.colors.gray300
    },
    ...variant === "outline" && {
      border: `1px solid ${theme.colors.gray400}`
    }
  } })));
};

// src/linkTabs/index.tsx
var LinkTabs = ({
  current,
  links,
  tabIndicator,
  lineIndicator,
  height,
  gap,
  padding,
  variant,
  margin,
  maxWidth
}) => {
  let linkString = useMemo9(() => links.map((link) => link.to).join(","), [links]);
  let normalizedLinks = useMemo9(() => links.map((link) => ({
    ...link,
    to: new URL(link.to, window.location.origin).pathname
  })), [linkString]);
  let actualCurrent = useMemo9(() => {
    let normalizedCurrent = new URL(current, window.location.origin).pathname;
    let [bestMatch] = normalizedLinks.map((link) => link.to).filter((link) => link == normalizedCurrent || normalizedCurrent.startsWith(`${link}/`)).sort((a, b) => b.length - a.length);
    return bestMatch;
  }, [current, normalizedLinks]);
  return /* @__PURE__ */ React53.createElement(Tabs, { current: actualCurrent, tabs: normalizedLinks, action: () => {
  }, tabIndicator, lineIndicator, height, gap, margin, padding, variant, maxWidth });
};

// src/logo.tsx
import { brand, brandMark } from "@metorial/brand";
import React54 from "react";
var Logo = ({
  size = 30,
  color = brand.color,
  accentColor = brand.accentColor
}) => {
  let {
    square,
    ring
  } = brandMark;
  return /* @__PURE__ */ React54.createElement("svg", { height: size, width: size, viewBox: brandMark.viewBox, fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": brand.name }, /* @__PURE__ */ React54.createElement("rect", { width: square.width, height: square.height, rx: square.rx, fill: color }), /* @__PURE__ */ React54.createElement("circle", { cx: ring.cx, cy: ring.cy, r: ring.r, fill: "none", stroke: accentColor, strokeWidth: ring.strokeWidth }));
};
var CenteredLogo = ({
  size
}) => {
  return /* @__PURE__ */ React54.createElement("div", { style: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  } }, /* @__PURE__ */ React54.createElement(Logo, { size }));
};

// src/multiSelect/index.tsx
import * as RadixPopover2 from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden7 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine4 } from "@remixicon/react";
import React55, { useId as useId7 } from "react";
import { useMeasure as useMeasure2 } from "react-use";
import { keyframes as keyframes14, styled as styled46 } from "styled-components";
var fadeIn6 = keyframes14`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger12 = styled46(RadixPopover2.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1nfjfai-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:none;border:none;font-weight:500;flex-shrink:0;&:focus-within,&:focus{background:${theme.colors.gray400};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content22 = styled46(RadixPopover2.Content).withConfig({
  componentId: "sc-1nfjfai-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:15px;box-shadow:${theme.shadows.medium};background:${theme.colors.background};font-size:14px;font-weight:500;border-radius:8px;gap:10px;width:100%;&[data-state='open']{animation:${fadeIn6} 0.15s ease forwards;}`;
var Item11 = styled46("div").withConfig({
  componentId: "sc-1nfjfai-2"
})``;
var Value3 = styled46("div").withConfig({
  componentId: "sc-1nfjfai-3"
})`font-size:12px;color:${theme.colors.background};background:${theme.colors.foreground};padding:3px 5px;border-radius:5px;box-shadow:${theme.shadows.small};`;
var TriggerValues = styled46("div").withConfig({
  componentId: "sc-1nfjfai-4"
})`display:flex;gap:5px;flex-wrap:wrap;`;
var MultiSelect = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId7();
  let sizeStyles = getButtonSize(size);
  let [ref, {
    width
  }] = useMeasure2();
  return /* @__PURE__ */ React55.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0
  } }, label && (hideLabel ? /* @__PURE__ */ React55.createElement(VisuallyHidden7, null, /* @__PURE__ */ React55.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React55.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React55.createElement(InputDescription, null, description), /* @__PURE__ */ React55.createElement(RadixPopover2.Root, null, /* @__PURE__ */ React55.createElement(Trigger12, { id, disabled, "aria-label": label, style: {
    ...sizeStyles,
    fontSize: 14
  }, ref }, /* @__PURE__ */ React55.createElement(TriggerValues, null, [...value ?? []].sort().map((v) => /* @__PURE__ */ React55.createElement(Value3, null, items.find((i) => i.id === v)?.label))), /* @__PURE__ */ React55.createElement(RiArrowDownSLine4, { size: 14, style: {
    opacity: 0.5
  } })), /* @__PURE__ */ React55.createElement(RadixPopover2.Portal, null, /* @__PURE__ */ React55.createElement(Content22, { sideOffset: 5, style: {
    width: width + 20,
    zIndex: 9999
  } }, items.map((item, i) => /* @__PURE__ */ React55.createElement(Item11, null, /* @__PURE__ */ React55.createElement(Checkbox, { checked: value?.includes(item.id), onCheckedChange: (checked) => {
    if (checked) {
      onChange?.([...value, item.id]);
    } else {
      onChange?.((value || [])?.filter((v) => v !== item.id));
    }
  }, label: item.label, disabled: item.disabled || disabled })))))), error && /* @__PURE__ */ React55.createElement(Error, null, error));
};

// src/numberInput/index.tsx
import React56, { useId as useId8 } from "react";
import { styled as styled47 } from "styled-components";
var Wrapper21 = styled47("div").withConfig({
  componentId: "sc-s40khn-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var Presets = styled47("div").withConfig({
  componentId: "sc-s40khn-1"
})`display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;`;
var isPresetInRange = (value, min, max) => (min === void 0 || value >= min) && (max === void 0 || value <= max);
var NumberInput = ({
  label,
  description,
  help,
  hideLabel,
  error,
  presets,
  style,
  ...fieldProps
}) => {
  let id = useId8();
  let isInteractive = !fieldProps.disabled && !fieldProps.readOnly;
  return /* @__PURE__ */ React56.createElement(Wrapper21, { style }, /* @__PURE__ */ React56.createElement(InputHeader, { id, label, hideLabel, help, description }), /* @__PURE__ */ React56.createElement(NumberInputField, { id, invalid: !!error, ...fieldProps }), !!presets?.length && /* @__PURE__ */ React56.createElement(Presets, { role: "group", "aria-label": "Quick picks" }, presets.map((preset, index) => {
    let isSelected = fieldProps.value === preset.value;
    let isAllowed = isInteractive && !preset.disabled && isPresetInRange(preset.value, fieldProps.min, fieldProps.max);
    return /* @__PURE__ */ React56.createElement(Button, { key: index, type: "button", size: "1", variant: isSelected ? "soft" : "outline", "aria-pressed": isSelected, disabled: !isAllowed, onClick: () => fieldProps.onChange(preset.value) }, preset.label);
  })), typeof error == "string" && /* @__PURE__ */ React56.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};

// src/optionToggle/index.tsx
import * as RadixToggleGroup from "@radix-ui/react-toggle-group";
import { VisuallyHidden as VisuallyHidden8 } from "@radix-ui/react-visually-hidden";
import React57, { useCallback as useCallback3, useEffect as useEffect15, useId as useId9, useLayoutEffect as useLayoutEffect4, useMemo as useMemo10, useRef as useRef16, useState as useState24 } from "react";
import { styled as styled48 } from "styled-components";
var Wrapper22 = styled48("div").withConfig({
  componentId: "sc-1ign3j1-0"
})`display:flex;flex-direction:column;align-items:flex-start;gap:8px;width:100%;`;
var Root21 = styled48(RadixToggleGroup.Root).withConfig({
  componentId: "sc-1ign3j1-1"
})`position:relative;display:inline-flex;width:${({
  $fullWidth
}) => $fullWidth ? "100%" : "fit-content"};padding:4px;border-radius:${({
  $borderRadius
}) => $borderRadius};background:${({
  $background
}) => theme.colors[$background]};isolation:isolate;transition:none;&:focus-within{background:${({
  $background
}) => theme.colors[$background]};}&[data-disabled]{opacity:0.55;pointer-events:none;}`;
var Indicator5 = styled48("div").withConfig({
  componentId: "sc-1ign3j1-2"
})`position:absolute;top:4px;left:4px;height:calc(100% - 8px);border-radius:${({
  $borderRadius
}) => $borderRadius};background:${theme.colors.background};box-shadow:${theme.shadows.small};transition:none;z-index:0;`;
var ItemTooltipTrigger = styled48("span").withConfig({
  componentId: "sc-1ign3j1-3"
})`display:inline-flex;flex:${({
  $fullWidth
}) => $fullWidth ? "1 1 0" : "0 0 auto"};`;
var ItemIcon2 = styled48("span").withConfig({
  componentId: "sc-1ign3j1-4"
})`display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;svg{width:14px;height:14px;}`;
var Item13 = styled48(RadixToggleGroup.Item).withConfig({
  componentId: "sc-1ign3j1-5"
})`position:relative;z-index:1;display:inline-flex;flex:${({
  $fullWidth
}) => $fullWidth ? "1 1 0" : "0 0 auto"};gap:6px;min-width:${({
  $minWidth
}) => $minWidth}px;min-height:${({
  $size
}) => getButtonSize($size).height};align-items:center;justify-content:center;border:none;border-radius:${({
  $borderRadius
}) => $borderRadius};background:transparent;padding:${({
  $size
}) => getButtonSize($size).padding};font-size:${({
  $size
}) => getButtonSize($size).fontSize};font-weight:500;color:${theme.colors.gray700};white-space:nowrap;cursor:pointer;transition:none;&:hover,&:focus-visible{color:${theme.colors.gray900};outline:none;}&[data-state='on']{color:${theme.colors.gray900};}&:disabled,&[aria-disabled='true']{cursor:not-allowed;opacity:0.5;}&[aria-disabled='true']:hover{color:${theme.colors.gray700};}`;
var OptionToggle = ({
  items,
  value,
  onChange,
  label,
  description,
  hideLabel,
  ariaLabel,
  disabled,
  fullWidth,
  background = "gray300",
  size = "3",
  itemMinWidth = 92
}) => {
  let id = useId9();
  let labelId = `${id}-label`;
  let rootRef = useRef16(null);
  let itemRefs = useRef16({});
  let measurementFrameRef = useRef16(null);
  let [indicator, setIndicator] = useState24(null);
  let sizeStyles = getButtonSize(size);
  let itemBorderRadius = `calc(${sizeStyles.borderRadius} - 2px)`;
  let selectedItem = useMemo10(() => items.find((item) => item.id == value), [items, value]);
  let itemIds = useMemo10(() => items.map((item) => item.id).join("\0"), [items]);
  let updateIndicator = useCallback3((nextIndicator) => {
    setIndicator((current) => {
      if (current === null && nextIndicator === null) return current;
      if (current && nextIndicator) {
        if (current.width === nextIndicator.width && current.left === nextIndicator.left) {
          return current;
        }
      }
      return nextIndicator;
    });
  }, []);
  let measureIndicator = useCallback3(() => {
    let root = rootRef.current;
    let item = selectedItem ? itemRefs.current[selectedItem.id] : null;
    if (!root || !item) {
      updateIndicator(null);
      return;
    }
    updateIndicator({
      width: item.offsetWidth,
      left: item.offsetLeft - 4
    });
  }, [selectedItem?.id, updateIndicator]);
  let scheduleMeasureIndicator = useCallback3(() => {
    if (measurementFrameRef.current !== null) {
      cancelAnimationFrame(measurementFrameRef.current);
    }
    measurementFrameRef.current = requestAnimationFrame(() => {
      measurementFrameRef.current = null;
      measureIndicator();
    });
  }, [measureIndicator]);
  useLayoutEffect4(() => {
    measureIndicator();
    let resizeObserver = typeof ResizeObserver == "undefined" ? null : new ResizeObserver(() => {
      scheduleMeasureIndicator();
    });
    if (rootRef.current) resizeObserver?.observe(rootRef.current);
    if (selectedItem) {
      let selectedNode = itemRefs.current[selectedItem.id];
      if (selectedNode) resizeObserver?.observe(selectedNode);
    }
    return () => {
      resizeObserver?.disconnect();
      if (measurementFrameRef.current !== null) {
        cancelAnimationFrame(measurementFrameRef.current);
        measurementFrameRef.current = null;
      }
    };
  }, [itemIds, measureIndicator, scheduleMeasureIndicator, selectedItem?.id, value]);
  useEffect15(() => {
    window.addEventListener("resize", scheduleMeasureIndicator);
    return () => window.removeEventListener("resize", scheduleMeasureIndicator);
  }, [scheduleMeasureIndicator]);
  return /* @__PURE__ */ React57.createElement(Wrapper22, null, label ? hideLabel ? /* @__PURE__ */ React57.createElement(VisuallyHidden8, null, /* @__PURE__ */ React57.createElement(InputLabel, { id: labelId }, label)) : /* @__PURE__ */ React57.createElement(InputLabel, { id: labelId, style: {
    marginBottom: 0
  } }, label) : null, description && /* @__PURE__ */ React57.createElement(InputDescription, { style: {
    marginBottom: 0
  } }, description), /* @__PURE__ */ React57.createElement(Root21, { ref: rootRef, type: "single", value, onValueChange: (nextValue) => {
    if (!nextValue || nextValue == value) return;
    if (items.find((item) => item.id == nextValue)?.disabled) return;
    onChange?.(nextValue);
  }, disabled, "aria-label": !label ? ariaLabel : void 0, "aria-labelledby": label ? labelId : void 0, $fullWidth: fullWidth, $borderRadius: sizeStyles.borderRadius, $background: background }, /* @__PURE__ */ React57.createElement(Indicator5, { "aria-hidden": true, $borderRadius: itemBorderRadius, style: {
    opacity: indicator ? 1 : 0,
    width: indicator?.width,
    transform: `translateX(${indicator?.left ?? 0}px)`
  } }), items.map((item) => {
    let isItemDisabled = !!(disabled || item.disabled);
    let explainsDisabled = isItemDisabled && !disabled && !!item.tooltip;
    let toggleItem = /* @__PURE__ */ React57.createElement(Item13, { key: item.id, value: item.id, disabled: isItemDisabled && !explainsDisabled, "aria-disabled": explainsDisabled || void 0, ref: (element) => {
      itemRefs.current[item.id] = element;
    }, $fullWidth: fullWidth, $size: size, $borderRadius: itemBorderRadius, $minWidth: itemMinWidth }, item.iconLeft && /* @__PURE__ */ React57.createElement(ItemIcon2, null, item.iconLeft), item.label, item.iconRight && /* @__PURE__ */ React57.createElement(ItemIcon2, null, item.iconRight));
    if (!item.tooltip) return toggleItem;
    return /* @__PURE__ */ React57.createElement(Tooltip, { key: item.id, content: item.tooltip }, /* @__PURE__ */ React57.createElement(ItemTooltipTrigger, { $fullWidth: fullWidth }, toggleItem));
  })));
};

// src/or/index.tsx
import React58, { memo as memo6 } from "react";
import { styled as styled49 } from "styled-components";
var Wrapper23 = styled49("div").withConfig({
  componentId: "sc-1plcqt7-0"
})`display:grid;grid-template-columns:1fr min-content 1fr;gap:10px;width:100%;`;
var LineWrapper = styled49("div").withConfig({
  componentId: "sc-1plcqt7-1"
})`display:flex;align-items:center;gap:10px;`;
var Line = styled49("div").withConfig({
  componentId: "sc-1plcqt7-2"
})`height:1px;background-color:${theme.colors.gray400};flex-grow:1;`;
var Text2 = styled49("p").withConfig({
  componentId: "sc-1plcqt7-3"
})`color:${theme.colors.gray600};font-size:12px;font-weight:500;`;
var Or = memo6(({
  text
}) => {
  return /* @__PURE__ */ React58.createElement(Wrapper23, null, /* @__PURE__ */ React58.createElement(LineWrapper, null, /* @__PURE__ */ React58.createElement(Line, null)), /* @__PURE__ */ React58.createElement(Text2, null, text ?? "or"), /* @__PURE__ */ React58.createElement(LineWrapper, null, /* @__PURE__ */ React58.createElement(Line, null)));
});

// src/popover/index.tsx
import * as RadixPopover3 from "@radix-ui/react-popover";
import React59, { useEffect as useEffect16, useState as useState25 } from "react";
import { keyframes as keyframes15, styled as styled50 } from "styled-components";
var fadeInTop3 = keyframes15`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutTop3 = keyframes15`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(-10px) scale(1);
  }
`;
var fadeInBottom4 = keyframes15`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutBottom4 = keyframes15`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(10px) scale(1);
  }
`;
var fadeInLeft3 = keyframes15`
  from {
    opacity: 0;
    transform: translateX(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutLeft3 = keyframes15`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(-10px) scale(1);
  }
`;
var fadeInRight4 = keyframes15`
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutRight4 = keyframes15`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(10px) scale(1);
  }
`;
var Wrapper24 = styled50(RadixPopover3.Content).withConfig({
  componentId: "sc-1j5n8m1-0"
})`display:flex;gap:10;align-items:center;cursor:pointer;background:rgba(245,245,245,0.7);backdrop-filter:blur(10px);border-radius:10px;min-width:200px;will-change:transform,opacity;outline:none;border:solid 1px ${theme.colors.gray400};box-shadow:0 5px 15px rgba(0,0,0,0.1);&[data-state='open'][data-side='top']{animation:${fadeInBottom4} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom4} 0.2s ease forwards;}&[data-state='open'][data-side='bottom']{animation:${fadeInTop3} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop3} 0.2s ease forwards;}&[data-state='open'][data-side='left']{animation:${fadeInLeft3} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft3} 0.2s ease forwards;}&[data-state='open'][data-side='right']{animation:${fadeInRight4} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight4} 0.2s ease forwards;}`;
var ContentWrapper = styled50("div").withConfig({
  componentId: "sc-1j5n8m1-1"
})`padding:15px;`;
var Arrow4 = styled50(RadixPopover3.Arrow).withConfig({
  componentId: "sc-1j5n8m1-2"
})`fill:${theme.colors.background};`;
var Root23 = React59.forwardRef(({
  trigger,
  children,
  arrow,
  operationKey,
  onOpenChange,
  onOpenAutoFocus,
  side,
  align,
  sideOffset,
  alignOffset,
  open: openProp,
  ...triggerProps
}, ref) => {
  let [open, setOpen] = useState25(false);
  let dialog = useDialogContext();
  useEffect16(() => {
    setOpen(false);
  }, [operationKey]);
  useEffect16(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open]);
  useEffect16(() => {
    if (typeof openProp === "boolean") setOpen(openProp);
  }, [openProp]);
  useSuppressTooltipWhileOpen(open);
  let zIndex = useZindex(open);
  return /* @__PURE__ */ React59.createElement(RadixPopover3.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React59.createElement(OverlayOpenProvider, { value: open }, /* @__PURE__ */ React59.createElement(RadixPopover3.Trigger, { ...triggerProps, asChild: true, ref }, trigger)), /* @__PURE__ */ React59.createElement(RadixPopover3.Portal, { container: dialog?.contentRef?.current }, /* @__PURE__ */ React59.createElement(Wrapper24, { style: {
    zIndex
  }, side, align, sideOffset, alignOffset, onOpenAutoFocus, onPointerDownOutside: preventPopoverDismissForNestedOverlay, onInteractOutside: preventPopoverDismissForNestedOverlay, onFocusOutside: preventPopoverDismissForNestedOverlay }, children, arrow && /* @__PURE__ */ React59.createElement(Arrow4, null))));
});
var Content24 = ({
  children,
  style
}) => {
  return /* @__PURE__ */ React59.createElement(ContentWrapper, { style }, children);
};
var Popover = Object.assign(Root23, {
  Content: Content24,
  Root: Root23
});

// src/progress/index.tsx
import { Progress as BaseProgress } from "@base-ui/react/progress";
import React60 from "react";
import { keyframes as keyframes16, styled as styled51 } from "styled-components";
var trackHeights = {
  "1": 6,
  "2": 10
};
var fillColors = {
  blue: "blue900",
  green: "green900",
  red: "red900",
  orange: "orange900",
  gray: "gray700"
};
var indeterminateSlide = keyframes16`
  from { transform: translateX(-100%); }
  to { transform: translateX(300%); }
`;
var Root24 = styled51(BaseProgress.Root).withConfig({
  componentId: "sc-1os55v4-0"
})`display:flex;flex-direction:column;gap:6px;width:100%;min-width:0;`;
var Header7 = styled51("div").withConfig({
  componentId: "sc-1os55v4-1"
})`display:flex;align-items:center;justify-content:space-between;gap:10px;min-width:0;`;
var Label2 = styled51(BaseProgress.Label).withConfig({
  componentId: "sc-1os55v4-2"
})`font-size:12px;font-weight:500;color:${theme.colors.gray900};min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
var ValueText = styled51("span").withConfig({
  componentId: "sc-1os55v4-3"
})`font-size:12px;font-weight:500;color:${theme.colors.gray600};font-variant-numeric:tabular-nums;white-space:nowrap;flex-shrink:0;`;
var Track = styled51(BaseProgress.Track).withConfig({
  componentId: "sc-1os55v4-4"
})`position:relative;width:100%;height:${({
  $height
}) => $height}px;border-radius:999px;background:${theme.colors.gray300};overflow:hidden;`;
var Indicator6 = styled51(BaseProgress.Indicator).withConfig({
  componentId: "sc-1os55v4-5"
})`display:block;height:100%;border-radius:999px;background:${({
  $color
}) => theme.colors[$color]};transition:width 0.3s ease,background 0.3s ease;&[data-indeterminate]{width:33%;animation:${indeterminateSlide} 1.2s cubic-bezier(0.65,0,0.35,1) infinite;}@media (prefers-reduced-motion:reduce){transition:none;&[data-indeterminate]{width:100%;opacity:0.4;animation:none;}}`;
var getPercent = (value, max) => {
  if (value === null || !Number.isFinite(value) || max <= 0) return null;
  return Math.min(100, Math.max(0, Math.round(value / max * 100)));
};
var Progress = ({
  value,
  max = 100,
  size = "1",
  color = "blue",
  label,
  valueLabel,
  showValue,
  style,
  ...rest
}) => {
  let percent = getPercent(value, max);
  let shouldShowValue = valueLabel !== void 0 || (showValue ?? !!label);
  let hasHeader = !!label || shouldShowValue;
  return /* @__PURE__ */ React60.createElement(Root24, { value: value === null ? null : Math.min(Math.max(value, 0), max), max, getAriaValueText: () => percent === null ? "Loading" : `${percent}%`, "aria-label": label ? void 0 : rest["aria-label"], style }, hasHeader && /* @__PURE__ */ React60.createElement(Header7, null, label ? /* @__PURE__ */ React60.createElement(Label2, null, label) : /* @__PURE__ */ React60.createElement("span", null), shouldShowValue && /* @__PURE__ */ React60.createElement(ValueText, null, valueLabel ?? (percent === null ? null : `${percent}%`))), /* @__PURE__ */ React60.createElement(Track, { $height: trackHeights[size] }, /* @__PURE__ */ React60.createElement(Indicator6, { $color: fillColors[color] })));
};

// src/progressSteps/index.tsx
import { VisuallyHidden as VisuallyHidden9 } from "@radix-ui/react-visually-hidden";
import { RiCheckLine as RiCheckLine5, RiCloseLine as RiCloseLine5 } from "@remixicon/react";
import React61 from "react";
import { keyframes as keyframes17, styled as styled52 } from "styled-components";

// src/progressSteps/lib.ts
var getProgressStepStates = (stepIds, currentId2, outcome) => {
  let currentIndex = currentId2 ? stepIds.indexOf(currentId2) : -1;
  let states = {};
  stepIds.forEach((id, index) => {
    if (outcome == "complete") {
      states[id] = "complete";
    } else if (index < currentIndex) {
      states[id] = "complete";
    } else if (index == currentIndex) {
      states[id] = outcome == "failed" ? "failed" : "active";
    } else {
      states[id] = "pending";
    }
  });
  return states;
};

// src/progressSteps/index.tsx
var stateLabels = {
  complete: "Completed",
  active: "In progress",
  pending: "Pending",
  failed: "Failed"
};
var labelColors = {
  complete: "gray900",
  active: "gray900",
  pending: "gray600",
  failed: "red900"
};
var spin = keyframes17`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
var List4 = styled52("ol").withConfig({
  componentId: "sc-1db5w2y-0"
})`display:flex;flex-direction:${({
  $orientation
}) => $orientation == "vertical" ? "column" : "row"};list-style:none;margin:0;padding:0;width:100%;min-width:0;`;
var Connector = styled52("span").withConfig({
  componentId: "sc-1db5w2y-1"
})`display:block;border-radius:2px;background:${({
  $complete
}) => $complete ? theme.colors.primary300 : theme.colors.gray300};transition:background 0.3s ease;`;
var VerticalStep = styled52("li").withConfig({
  componentId: "sc-1db5w2y-2"
})`position:relative;display:grid;grid-template-columns:20px minmax(0,1fr);column-gap:10px;padding-bottom:18px;&:last-child{padding-bottom:0;}${Connector}{position:absolute;left:9px;top:24px;bottom:2px;width:2px;}`;
var HorizontalStep = styled52("li").withConfig({
  componentId: "sc-1db5w2y-3"
})`display:flex;flex-direction:column;gap:8px;flex:1 1 0;min-width:0;&:last-child{flex:0 0 auto;max-width:30%;}`;
var HorizontalRail = styled52("div").withConfig({
  componentId: "sc-1db5w2y-4"
})`display:flex;align-items:center;gap:6px;${Connector}{flex:1;height:2px;margin-right:6px;}`;
var Indicator7 = styled52("span").withConfig({
  componentId: "sc-1db5w2y-5"
})`position:relative;display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;width:20px;height:20px;flex-shrink:0;border-radius:999px;border:1.5px solid ${({
  $state
}) => {
  if ($state == "complete") return theme.colors.primary300;
  if ($state == "failed") return theme.colors.red900;
  if ($state == "active") return theme.colors.blue300;
  return "rgba(17, 17, 17, 0.14)";
}};background:${({
  $state
}) => {
  if ($state == "complete") return theme.colors.primary300;
  if ($state == "failed") return theme.colors.red900;
  return theme.colors.background;
}};color:${theme.colors.background};transition:background 0.3s ease,border-color 0.3s ease;svg{width:13px;height:13px;}`;
var ActiveRing = styled52("span").withConfig({
  componentId: "sc-1db5w2y-6"
})`position:absolute;inset:-1.5px;border-radius:999px;border:1.5px solid transparent;border-top-color:${theme.colors.primary300};animation:${spin} 0.9s linear infinite;@media (prefers-reduced-motion:reduce){animation:none;border-color:${theme.colors.primary300};}`;
var Dot = styled52("span").withConfig({
  componentId: "sc-1db5w2y-7"
})`width:6px;height:6px;border-radius:999px;background:${({
  $active
}) => $active ? theme.colors.primary300 : "rgba(17, 17, 17, 0.22)"};`;
var Content25 = styled52("span").withConfig({
  componentId: "sc-1db5w2y-8"
})`display:flex;flex-direction:column;gap:2px;min-width:0;padding-top:1px;`;
var Label3 = styled52("span").withConfig({
  componentId: "sc-1db5w2y-9"
})`font-size:14px;line-height:18px;font-weight:${({
  $state
}) => $state == "active" ? 600 : 500};color:${({
  $state
}) => theme.colors[labelColors[$state]]};transition:color 0.3s ease;`;
var Description11 = styled52("span").withConfig({
  componentId: "sc-1db5w2y-10"
})`font-size:12px;line-height:16px;color:${theme.colors.gray600};`;
var StepIndicator = ({
  state
}) => /* @__PURE__ */ React61.createElement(Indicator7, { $state: state, "aria-hidden": true }, state == "complete" && /* @__PURE__ */ React61.createElement(RiCheckLine5, null), state == "failed" && /* @__PURE__ */ React61.createElement(RiCloseLine5, null), state == "active" && /* @__PURE__ */ React61.createElement(ActiveRing, null), (state == "active" || state == "pending") && /* @__PURE__ */ React61.createElement(Dot, { $active: state == "active" }));
var StepContent = ({
  step
}) => /* @__PURE__ */ React61.createElement(Content25, null, /* @__PURE__ */ React61.createElement(Label3, { $state: step.state }, step.label, /* @__PURE__ */ React61.createElement(VisuallyHidden9, null, ` (${stateLabels[step.state]})`)), step.description && /* @__PURE__ */ React61.createElement(Description11, null, step.description));
var ProgressSteps = ({
  steps,
  orientation = "vertical",
  style,
  ...rest
}) => /* @__PURE__ */ React61.createElement(List4, { $orientation: orientation, "aria-label": rest["aria-label"], style }, steps.map((step, index) => {
  let isLast = index == steps.length - 1;
  let connector = isLast ? null : /* @__PURE__ */ React61.createElement(Connector, { $complete: step.state == "complete" });
  let ariaCurrent = step.state == "active" ? "step" : void 0;
  if (orientation == "horizontal") {
    return /* @__PURE__ */ React61.createElement(HorizontalStep, { key: step.id, "aria-current": ariaCurrent }, /* @__PURE__ */ React61.createElement(HorizontalRail, null, /* @__PURE__ */ React61.createElement(StepIndicator, { state: step.state }), connector), /* @__PURE__ */ React61.createElement(StepContent, { step }));
  }
  return /* @__PURE__ */ React61.createElement(VerticalStep, { key: step.id, "aria-current": ariaCurrent }, /* @__PURE__ */ React61.createElement(StepIndicator, { state: step.state }), /* @__PURE__ */ React61.createElement(StepContent, { step }), connector);
}));

// src/selectList/index.tsx
import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { VisuallyHidden as VisuallyHidden10 } from "@radix-ui/react-visually-hidden";
import { RiArrowLeftLine, RiSearchLine } from "@remixicon/react";
import { AnimatePresence, motion as motion4 } from "framer-motion";
import React62, { useEffect as useEffect17, useId as useId10, useRef as useRef17, useState as useState26 } from "react";
import { styled as styled53 } from "styled-components";
var Wrapper25 = styled53("div").withConfig({
  componentId: "sc-1u8o2si-0"
})`display:flex;flex-direction:column;width:100%;`;
var ListBox = styled53("div").withConfig({
  componentId: "sc-1u8o2si-1"
})`position:relative;width:100%;border-radius:10px;background:${theme.colors.background};box-shadow:inset 0 0 0 1px ${theme.colors.gray300};overflow:hidden;`;
var ScrollArea = styled53("div").withConfig({
  componentId: "sc-1u8o2si-2"
})`max-height:${(p) => p.$maxHeight ? `${p.$maxHeight}px` : "none"};overflow-y:${(p) => p.$maxHeight ? "auto" : "visible"};`;
var SearchBar = styled53("div").withConfig({
  componentId: "sc-1u8o2si-3"
})`padding:8px;background:${theme.colors.background};border-bottom:1px solid ${theme.colors.gray300};border-top-left-radius:10px;border-top-right-radius:10px;`;
var SearchField = styled53("label").withConfig({
  componentId: "sc-1u8o2si-4"
})`display:flex;align-items:center;gap:8px;height:34px;padding:0 10px;border-radius:7px;background:${theme.colors.gray200};color:${theme.colors.gray700};outline:1px solid transparent;transition:background 0.2s ease,outline-color 0.2s ease;&:focus-within{background:${theme.colors.gray300};outline-color:${theme.colors.gray500};}`;
var SearchInput = styled53("input").withConfig({
  componentId: "sc-1u8o2si-5"
})`flex-grow:1;min-width:0;height:100%;border:none;background:transparent;outline:none;font-size:14px;color:${theme.colors.foreground};&::placeholder{color:${theme.colors.gray600};}`;
var SpinnerSlot2 = styled53("div").withConfig({
  componentId: "sc-1u8o2si-6"
})`display:flex;align-items:center;justify-content:center;width:14px;flex-shrink:0;opacity:${(p) => p.$visible ? 1 : 0};transform:scale(${(p) => p.$visible ? 1 : 0.85});transition:opacity 0.3s ease,transform 0.3s ease;pointer-events:none;`;
var Group5 = styled53(RadioGroup).withConfig({
  componentId: "sc-1u8o2si-7"
})`display:flex;flex-direction:column;width:100%;`;
var RowWrapper = styled53(motion4.div).withConfig({
  componentId: "sc-1u8o2si-8"
})`position:relative;overflow:hidden;& + &{box-shadow:inset 0 1px 0 ${theme.colors.gray300};}`;
var Row2 = styled53(Radio.Root).withConfig({
  componentId: "sc-1u8o2si-9"
})`display:grid;grid-template-columns:16px minmax(0,1fr) auto;column-gap:12px;align-items:flex-start;width:100%;padding:14px ${(p) => p.$reserveEnd ? "96px" : "18px"} 14px 18px;border:0;background:transparent;text-align:left;cursor:pointer;outline:none;transition:none;&:hover{background:${theme.colors.gray100};}&:focus-visible{background:${theme.colors.gray100};box-shadow:inset 0 0 0 1px ${theme.colors.gray500};}&[data-checked]{background:${theme.colors.gray200};}&[data-disabled]{cursor:not-allowed;opacity:0.55;}`;
var Indicator8 = styled53("span").withConfig({
  componentId: "sc-1u8o2si-10"
})`width:16px;height:16px;margin-top:1px;border-radius:999px;border:1.5px solid ${theme.colors.gray500};background:${theme.colors.background};display:inline-flex;align-items:center;justify-content:center;transition:none;${Row2}[data-checked] &{border-color:${theme.colors.primary};}`;
var IndicatorDot = styled53(Radio.Indicator).withConfig({
  componentId: "sc-1u8o2si-11"
})`width:7px;height:7px;border-radius:999px;background:${theme.colors.primary};transition:none;&[data-unchecked]{opacity:0;}`;
var RowContent = styled53("div").withConfig({
  componentId: "sc-1u8o2si-12"
})`display:flex;flex-direction:column;gap:3px;min-width:0;`;
var RowLabel = styled53("div").withConfig({
  componentId: "sc-1u8o2si-13"
})`font-size:14px;font-weight:600;line-height:1.25;color:${theme.colors.gray900};overflow-wrap:anywhere;`;
var RowDescription = styled53("div").withConfig({
  componentId: "sc-1u8o2si-14"
})`font-size:12px;line-height:1.35;color:${theme.colors.gray600};`;
var RowBadge = styled53("div").withConfig({
  componentId: "sc-1u8o2si-15"
})`display:flex;align-items:center;align-self:center;`;
var BackSlot = styled53("div").withConfig({
  componentId: "sc-1u8o2si-16"
})`position:absolute;top:50%;right:12px;transform:translateY(-50%);z-index:1;`;
var PaginationBar = styled53("div").withConfig({
  componentId: "sc-1u8o2si-17"
})`display:flex;justify-content:flex-end;gap:8px;padding:8px;border-top:1px solid ${theme.colors.gray300};`;
var Empty = styled53("div").withConfig({
  componentId: "sc-1u8o2si-18"
})`padding:18px;font-size:13px;color:${theme.colors.gray600};text-align:center;`;
var useDebouncedCallback = (callback, delay) => {
  let callbackRef = useRef17(callback);
  callbackRef.current = callback;
  let timeoutRef = useRef17(null);
  let [isPending, setIsPending] = useState26(false);
  useEffect17(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);
  let schedule = (value) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsPending(true);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setIsPending(false);
      callbackRef.current?.(value);
    }, delay);
  };
  let flush = (value) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPending(false);
    callbackRef.current?.(value);
  };
  return {
    schedule,
    flush,
    isPending
  };
};
var SelectList = (p) => {
  let id = useId10();
  let labelId = `${id}-label`;
  let descriptionId = `${id}-description`;
  let [search, setSearch] = useState26("");
  let debouncedSearch = useDebouncedCallback(p.onSearchChange, p.searchDebounceMs ?? 300);
  let groupRef = useRef17(null);
  let [pageDirection, setPageDirection] = useState26(null);
  useEffect17(() => {
    if (!p.isLoading) setPageDirection(null);
  }, [p.isLoading]);
  let collapseOn = p.collapseOn ?? [];
  let isCollapsed = p.collapsed ?? (!!p.value && collapseOn.includes(p.value));
  let visibleItems = isCollapsed ? p.items.filter((item) => item.id == p.value) : p.items;
  let transition = {
    duration: 0
  };
  let handleBack = () => {
    if (p.onBack) p.onBack();
    else p.onChange(null);
    requestAnimationFrame(() => {
      groupRef.current?.querySelector('[role="radio"]')?.focus();
    });
  };
  return /* @__PURE__ */ React62.createElement(Wrapper25, null, p.label && (p.hideLabel ? /* @__PURE__ */ React62.createElement(VisuallyHidden10, null, /* @__PURE__ */ React62.createElement("span", { id: labelId }, p.label)) : /* @__PURE__ */ React62.createElement(InputLabel, { as: "div", id: labelId }, p.label)), p.description && /* @__PURE__ */ React62.createElement(InputDescription, { id: descriptionId }, p.description), /* @__PURE__ */ React62.createElement(ListBox, null, /* @__PURE__ */ React62.createElement(ScrollArea, { $maxHeight: isCollapsed ? void 0 : p.maxHeight }, /* @__PURE__ */ React62.createElement(AnimatePresence, { initial: false }, p.searchable && !isCollapsed && /* @__PURE__ */ React62.createElement(motion4.div, { key: "search", initial: {
    height: 0,
    opacity: 0
  }, animate: {
    height: "auto",
    opacity: 1
  }, exit: {
    height: 0,
    opacity: 0
  }, transition, style: {
    position: "sticky",
    top: 0,
    zIndex: 2
  } }, /* @__PURE__ */ React62.createElement(SearchBar, null, /* @__PURE__ */ React62.createElement(SearchField, null, /* @__PURE__ */ React62.createElement(RiSearchLine, { size: 14, "aria-hidden": true }), /* @__PURE__ */ React62.createElement(SearchInput, { type: "search", value: search, placeholder: p.searchPlaceholder ?? "Search", "aria-label": p.searchPlaceholder ?? "Search", disabled: p.disabled, onChange: (e) => {
    setSearch(e.target.value);
    debouncedSearch.schedule(e.target.value.trim());
  }, onKeyDown: (e) => {
    if (e.key == "Enter") {
      e.preventDefault();
      debouncedSearch.flush(e.currentTarget.value.trim());
    }
  } }), /* @__PURE__ */ React62.createElement(SpinnerSlot2, { $visible: !!p.isLoading || debouncedSearch.isPending, "aria-hidden": true }, /* @__PURE__ */ React62.createElement(Spinner, { size: 14, foreground: theme.colors.gray700, background: "transparent" })))))), /* @__PURE__ */ React62.createElement(Group5, { ref: groupRef, value: p.value ?? null, disabled: p.disabled, "aria-label": !p.label ? p.ariaLabel : void 0, "aria-labelledby": p.label ? labelId : void 0, "aria-describedby": p.description ? descriptionId : void 0, onValueChange: (value, details) => {
    let nextValue = value;
    let isExplicitActivation = typeof PointerEvent != "undefined" && details.event instanceof PointerEvent || typeof KeyboardEvent != "undefined" && details.event instanceof KeyboardEvent;
    if (collapseOn.includes(nextValue) && !isExplicitActivation) return;
    if (nextValue == p.value) return;
    p.onChange(nextValue);
  } }, /* @__PURE__ */ React62.createElement(AnimatePresence, { initial: false }, visibleItems.map((item) => /* @__PURE__ */ React62.createElement(RowWrapper, { key: item.id, layout: "position", initial: {
    height: 0,
    opacity: 0
  }, animate: {
    height: "auto",
    opacity: 1
  }, exit: {
    height: 0,
    opacity: 0
  }, transition }, /* @__PURE__ */ React62.createElement(Row2, { value: item.id, disabled: item.disabled, $reserveEnd: isCollapsed, onFocus: (e) => {
    if (collapseOn.includes(item.id)) {
      e.preventDefault();
    }
  }, onKeyDown: (e) => {
    if (item.disabled || p.disabled) return;
    if (e.key == "Enter" || e.key == " ") {
      e.preventDefault();
      if (item.id != p.value) p.onChange(item.id);
    }
  } }, /* @__PURE__ */ React62.createElement(Indicator8, { "aria-hidden": true }, /* @__PURE__ */ React62.createElement(IndicatorDot, { keepMounted: true })), /* @__PURE__ */ React62.createElement(RowContent, null, /* @__PURE__ */ React62.createElement(RowLabel, null, item.label), item.description && /* @__PURE__ */ React62.createElement(RowDescription, null, item.description)), item.badge && !isCollapsed ? /* @__PURE__ */ React62.createElement(RowBadge, null, item.badge) : /* @__PURE__ */ React62.createElement("span", null)), isCollapsed && /* @__PURE__ */ React62.createElement(BackSlot, null, /* @__PURE__ */ React62.createElement(Button, { type: "button", size: "1", variant: "outline", iconLeft: /* @__PURE__ */ React62.createElement(RiArrowLeftLine, null), disabled: p.disabled, onClick: handleBack }, p.backLabel ?? "Back")))))), visibleItems.length == 0 && !p.isLoading && /* @__PURE__ */ React62.createElement(Empty, null, p.empty ?? "No results found.")), p.pagination && !isCollapsed && (p.pagination.hasPrevious || p.pagination.hasNext) && /* @__PURE__ */ React62.createElement(PaginationBar, null, /* @__PURE__ */ React62.createElement(Button, { type: "button", size: "1", variant: "outline", disabled: !p.pagination.hasPrevious || !!p.isLoading, loading: !!p.isLoading && pageDirection == "previous", onClick: () => {
    setPageDirection("previous");
    p.pagination?.onPrevious();
  } }, "Previous"), /* @__PURE__ */ React62.createElement(Button, { type: "button", size: "1", variant: "outline", disabled: !p.pagination.hasNext || !!p.isLoading, loading: !!p.isLoading && pageDirection == "next", onClick: () => {
    setPageDirection("next");
    p.pagination?.onNext();
  } }, "Next"))), /* @__PURE__ */ React62.createElement(AnimatePresence, { initial: false }, isCollapsed && p.children && /* @__PURE__ */ React62.createElement(motion4.div, { key: "collapsed-content", initial: {
    height: 0,
    opacity: 0
  }, animate: {
    height: "auto",
    opacity: 1
  }, exit: {
    height: 0,
    opacity: 0
  }, transition, style: {
    overflow: "hidden"
  } }, /* @__PURE__ */ React62.createElement("div", { style: {
    paddingTop: 16
  } }, p.children))));
};

// src/showModal/confirm.tsx
import React63 from "react";
var confirm = ({
  title,
  description,
  onConfirm,
  onCancel,
  cancelText = "Cancel",
  confirmText = "Confirm"
}) => {
  showModal(({
    dialogProps
  }) => {
    return /* @__PURE__ */ React63.createElement(Alert.Wrapper, { ...dialogProps }, /* @__PURE__ */ React63.createElement(Alert.Title, null, title), /* @__PURE__ */ React63.createElement(Alert.Description, null, description), /* @__PURE__ */ React63.createElement(Alert.Actions, null, /* @__PURE__ */ React63.createElement(Alert.Action, { type: "cancel" }, /* @__PURE__ */ React63.createElement(Button, { type: "button", variant: "soft", onClick: onCancel }, cancelText)), /* @__PURE__ */ React63.createElement(Alert.Action, { type: "action" }, /* @__PURE__ */ React63.createElement(Button, { type: "button", variant: "solid", onClick: onConfirm }, confirmText))));
  });
};

// src/showModal/showModal.tsx
import React64, { useEffect as useEffect18, useRef as useRef18, useState as useState27 } from "react";
var modalAtom = atom([]);
var currentId = 0;
var showModal = (Body, opts = {}) => {
  let id = currentId++;
  let setOpenRef = {
    current: null
  };
  let hide = () => {
    setOpenRef = {
      current: null
    };
    setTimeout(() => {
      modalAtom.set((modals) => modals.filter((m) => m.id != id));
    }, 500);
  };
  let Component = () => {
    let [open, setOpen] = useState27(false);
    setOpenRef.current = setOpen;
    let onCloseInternal = () => {
      setOpen(false);
      hide();
      opts.onClose?.();
    };
    useEffect18(() => () => {
      setOpenRef.current = null;
    }, []);
    useEffect18(() => {
      setTimeout(() => setOpen(true), 2);
    }, []);
    let onOpenChange = (isOpen) => {
      setOpen(isOpen);
      if (!isOpen) onCloseInternal();
    };
    return /* @__PURE__ */ React64.createElement(Body, { isOpen: open, dialogProps: {
      isOpen: open,
      onOpenChange
    }, close: onCloseInternal });
  };
  modalAtom.set((modals) => [...modals, {
    id,
    component: Component
  }]);
  return {
    close: () => {
      if (setOpenRef.current) setOpenRef.current(false);
      hide();
    }
  };
};
var ModalRoot = () => {
  let modals = useAtom(modalAtom);
  let pathname = usePathname();
  useEffect18(() => {
    modalAtom.set([]);
  }, [pathname]);
  return /* @__PURE__ */ React64.createElement(React64.Fragment, null, modals.map((m) => /* @__PURE__ */ React64.createElement(m.component, { key: m.id })));
};
var patchHistoryOnce = /* @__PURE__ */ (() => {
  let patched = false;
  return () => {
    if (patched || typeof window == "undefined") return;
    patched = true;
    for (let key of ["pushState", "replaceState"]) {
      let original = window.history[key];
      window.history[key] = function(...args) {
        let result = original.apply(this, args);
        window.dispatchEvent(new Event("metorial:locationchange"));
        return result;
      };
    }
  };
})();
var usePathname = () => {
  patchHistoryOnce();
  let [pathname, setPathname] = useState27(() => typeof window != "undefined" ? window.location.pathname : "/");
  let currentPathRef = useRef18(pathname);
  currentPathRef.current = pathname;
  useEffect18(() => {
    let handler = () => {
      if (currentPathRef.current != window.location.pathname) {
        setPathname(window.location.pathname);
      }
    };
    window.addEventListener("popstate", handler);
    window.addEventListener("metorial:locationchange", handler);
    return () => {
      window.removeEventListener("popstate", handler);
      window.removeEventListener("metorial:locationchange", handler);
    };
  }, []);
  return pathname;
};

// src/slider/index.tsx
import * as RadixSlider from "@radix-ui/react-slider";
import clsx3 from "clsx";
import React65, { useEffect as useEffect19, useRef as useRef19, useState as useState28 } from "react";
import { useDebounce } from "react-use";
import { styled as styled54 } from "styled-components";
var maxStopLabels = 5;
var Wrapper26 = styled54.div.withConfig({
  componentId: "sc-44clea-0"
})`display:flex;flex-direction:column;.disabled{& > *{cursor:not-allowed !important;}}`;
var Header8 = styled54.header.withConfig({
  componentId: "sc-44clea-1"
})`display:flex;justify-content:space-between;align-items:center;`;
var Label4 = styled54.label.withConfig({
  componentId: "sc-44clea-2"
})`font-size:12px;font-weight:500;`;
var Input3 = styled54.input.withConfig({
  componentId: "sc-44clea-3"
})`border:solid 1px transparent;border-radius:5px;padding:4px 5px;font-size:12px;font-weight:500;outline:none;transition:all 0.2s ease;text-align:right;margin-right:-5px;color:${theme.colors.gray600};&:focus{border-color:${theme.colors.gray400};color:${theme.colors.gray300};}&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}&[type='number']{-moz-appearance:textfield;appearance:textfield;}`;
var ValueInput = styled54.div.withConfig({
  componentId: "sc-44clea-4"
})`display:flex;align-items:center;border:1px solid transparent;border-radius:5px;color:${theme.colors.gray600};font-size:12px;font-weight:500;&:focus-within{border-color:${theme.colors.gray400};color:${theme.colors.gray300};}input{width:70px;margin-right:0;border:none;color:inherit;}span{padding-right:5px;}`;
var Description12 = styled54.p.withConfig({
  componentId: "sc-44clea-5"
})`font-size:12px;margin-bottom:3px;color:${theme.colors.gray600};`;
var Root26 = styled54(RadixSlider.Root).withConfig({
  componentId: "sc-44clea-6"
})`position:relative;display:flex;align-items:center;user-select:none;touch-action:none;width:100%;height:20px;`;
var Track3 = styled54(RadixSlider.Track).withConfig({
  componentId: "sc-44clea-7"
})`background:#ddd;position:relative;flex-grow:1;border-radius:9999;height:3px;`;
var Range2 = styled54(RadixSlider.Range).withConfig({
  componentId: "sc-44clea-8"
})`position:absolute;background:${theme.colors.primary};border-radius:9999;height:100%;`;
var Thumb4 = styled54(RadixSlider.Thumb).withConfig({
  componentId: "sc-44clea-9"
})`display:block;width:20px;height:20px;background:white;border-radius:10px;border:solid 1px ${theme.colors.gray300};box-shadow:0 0 10px rgba(0,0,0,0.1);transition:none;outline:none;&:focus{border:solid 1px ${theme.colors.primary};}`;
var ContinuousSlider = ({
  value: value_,
  onChange: onChange_,
  description,
  step = 1,
  min = 0,
  max = 100,
  label,
  disabled,
  unit,
  onValueCommit
}) => {
  let [value, setValue] = useState28(() => value_ ?? min ?? 0);
  let lastChangedRef = useRef19(0);
  let onChange = (value2) => {
    lastChangedRef.current = Date.now();
    setValue(value2);
  };
  useEffect19(() => {
    if (Date.now() - lastChangedRef.current < 50) return;
    onChange(value_ ?? min ?? 0);
  }, [value_]);
  useDebounce(() => {
    onChange_?.(value);
  }, 150, [value]);
  return /* @__PURE__ */ React65.createElement(Wrapper26, { className: clsx3({
    disabled
  }) }, /* @__PURE__ */ React65.createElement(Header8, null, /* @__PURE__ */ React65.createElement(Label4, null, label), /* @__PURE__ */ React65.createElement(ValueInput, null, /* @__PURE__ */ React65.createElement(Input3, { type: "number", "aria-label": unit ? `${label} (${unit})` : label, value: value ?? 0, onChange: (e) => onChange?.(Math.max(min, Math.min(max, Number(e.target.value)))), min, max, step, disabled }), unit && /* @__PURE__ */ React65.createElement("span", { "aria-hidden": "true" }, unit))), description && /* @__PURE__ */ React65.createElement(Description12, null, description), /* @__PURE__ */ React65.createElement(Root26, { step, min, max, value: typeof value == "number" ? [value] : void 0, onValueChange: ([value2]) => onChange?.(value2), onValueCommit: ([value2]) => onValueCommit?.(value2), "aria-label": label, disabled }, /* @__PURE__ */ React65.createElement(Track3, null, /* @__PURE__ */ React65.createElement(Range2, null)), /* @__PURE__ */ React65.createElement(Thumb4, null)));
};
var Stops = styled54.div.withConfig({
  componentId: "sc-44clea-10"
})`position:relative;height:18px;margin-top:5px;button{position:absolute;white-space:nowrap;border:none;background:transparent;padding:0;font-size:11px;color:${theme.colors.gray700};cursor:pointer;}button:disabled{color:${theme.colors.gray500};cursor:not-allowed;text-decoration:line-through;}`;
var DiscreteSlider = ({
  stops,
  value,
  onChange,
  label,
  description,
  disabled,
  unit,
  onValueCommit
}) => {
  let [inputValue, setInputValue] = useState28(value === void 0 ? "" : String(value));
  useEffect19(() => {
    setInputValue(value === void 0 ? "" : String(value));
  }, [value]);
  let index = Math.max(0, stops.findIndex((stop) => stop.value === value));
  let labelIndices = new Set(Array.from({
    length: Math.min(maxStopLabels, stops.length)
  }, (_, index2) => Math.round(index2 * (stops.length - 1) / Math.max(1, Math.min(maxStopLabels, stops.length) - 1))));
  let select = (next, commit = false) => {
    let stop = stops[next];
    if (disabled || !stop || stop.disabled) return;
    onChange?.(stop.value);
    if (commit) onValueCommit?.(stop.value);
  };
  let commitInput = () => {
    let entered = Number(inputValue);
    let enabled = stops.filter((stop) => !stop.disabled);
    if (disabled || !inputValue.trim() || !Number.isFinite(entered) || !enabled.length) {
      setInputValue(value === void 0 ? "" : String(value));
      return;
    }
    let nearest = enabled.reduce((best, stop) => Math.abs(stop.value - entered) < Math.abs(best.value - entered) ? stop : best);
    setInputValue(String(nearest.value));
    select(stops.indexOf(nearest), true);
  };
  return /* @__PURE__ */ React65.createElement(Wrapper26, { className: clsx3({
    disabled
  }) }, /* @__PURE__ */ React65.createElement(Header8, null, /* @__PURE__ */ React65.createElement(Label4, null, label), /* @__PURE__ */ React65.createElement(ValueInput, null, /* @__PURE__ */ React65.createElement(Input3, { type: "number", "aria-label": unit ? `${label} (${unit})` : label, value: inputValue, min: stops[0]?.value, max: stops[stops.length - 1]?.value, step: "any", disabled: disabled || stops.every((stop) => stop.disabled), onChange: (event) => setInputValue(event.target.value), onBlur: commitInput, onKeyDown: (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitInput();
    }
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    event.preventDefault();
    let direction = event.key === "ArrowUp" ? 1 : -1;
    let next = index + direction;
    while (next >= 0 && next < stops.length && stops[next].disabled) next += direction;
    select(next, true);
  } }), unit && /* @__PURE__ */ React65.createElement("span", { "aria-hidden": "true" }, unit))), description && /* @__PURE__ */ React65.createElement(Description12, null, description), /* @__PURE__ */ React65.createElement(Root26, { min: 0, max: Math.max(1, stops.length - 1), step: 1, value: [index], disabled: disabled || stops.every((stop) => stop.disabled), onValueChange: ([next]) => select(next), onValueCommit: ([next]) => select(next, true), onKeyDown: (event) => {
    let direction = ["ArrowRight", "ArrowUp"].includes(event.key) ? 1 : ["ArrowLeft", "ArrowDown"].includes(event.key) ? -1 : 0;
    if (!direction && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    let next = event.key === "Home" ? 0 : event.key === "End" ? stops.length - 1 : index + direction;
    let step = event.key === "End" ? -1 : direction || 1;
    while (next >= 0 && next < stops.length && stops[next].disabled) next += step;
    select(next, true);
  } }, /* @__PURE__ */ React65.createElement(Track3, null, /* @__PURE__ */ React65.createElement(Range2, null)), /* @__PURE__ */ React65.createElement(Thumb4, { "aria-label": label, "aria-valuetext": stops[index]?.label ?? String(value) })), /* @__PURE__ */ React65.createElement(Stops, null, stops.map((stop, index2) => labelIndices.has(index2) && /* @__PURE__ */ React65.createElement("button", { key: stop.value, style: {
    left: `${index2 / Math.max(1, stops.length - 1) * 100}%`,
    transform: index2 === 0 ? void 0 : index2 === stops.length - 1 ? "translateX(-100%)" : "translateX(-50%)"
  }, type: "button", disabled: disabled || stop.disabled, title: stop.disabled ? "Unavailable" : void 0, onClick: () => select(index2, true) }, stop.label ?? stop.value))));
};
var Slider = (props) => props.stops ? /* @__PURE__ */ React65.createElement(DiscreteSlider, { ...props, stops: props.stops }) : /* @__PURE__ */ React65.createElement(ContinuousSlider, { ...props });

// src/sortableCheckList/index.tsx
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS as CSS2 } from "@dnd-kit/utilities";
import { RiDraggable } from "@remixicon/react";
import React66, { useEffect as useEffect20, useState as useState29 } from "react";
import { styled as styled55 } from "styled-components";
var List5 = styled55("ul").withConfig({
  componentId: "sc-1ofe4dx-0"
})`list-style:none;padding:0;margin:0;display:flex;flex-direction:column;`;
var ListItem = styled55("li").withConfig({
  componentId: "sc-1ofe4dx-1"
})`padding:5px 0px;display:flex;gap:10px;align-items:center;width:100%;`;
var SortableCheckList = ({
  items,
  onChange,
  syncItems,
  sortable = true
}) => {
  let [innerItems, setInnerItems] = useState29(() => items);
  let sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  let setItems = (items2) => {
    setInnerItems(items2);
    onChange(items2);
  };
  useEffect20(() => {
    if (syncItems && items != innerItems) {
      setInnerItems(items);
    } else if (!syncItems) {
      let newIDs = new Set(items.map((i) => i.id));
      let currentIDs = new Set(innerItems.map((i) => i.id));
      if (newIDs.size !== currentIDs.size || [...newIDs].some((id) => !currentIDs.has(id))) {
        setInnerItems(items);
      }
    }
  }, [items, syncItems]);
  return /* @__PURE__ */ React66.createElement(DndContext, { sensors, collisionDetection: closestCenter, onDragEnd: (event) => {
    let {
      active,
      over
    } = event;
    if (active && over && active.id !== over.id) {
      let oldIndex = innerItems.findIndex((item) => item.id === active.id);
      let newIndex = innerItems.findIndex((item) => item.id === over.id);
      setItems(arrayMove(innerItems, oldIndex, newIndex));
    }
  } }, /* @__PURE__ */ React66.createElement(SortableContext, { items: innerItems, strategy: verticalListSortingStrategy }, /* @__PURE__ */ React66.createElement(List5, null, innerItems.map((item, i) => /* @__PURE__ */ React66.createElement(SortableItem, { key: `${item.id}-${i}`, id: item.id, item, sortable, onCheckedChange: () => {
    setItems(innerItems.map((i2) => {
      if (i2.id === item.id) {
        return {
          ...i2,
          isChecked: !i2.isChecked
        };
      }
      return i2;
    }));
  } })))));
};
var SortableItem = ({
  id,
  item,
  sortable,
  onCheckedChange
}) => {
  let {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id
  });
  let style = {
    transform: CSS2.Transform.toString(transform),
    transition
  };
  return /* @__PURE__ */ React66.createElement(ListItem, { "data-dnd-id": id, ref: setNodeRef, style }, sortable && /* @__PURE__ */ React66.createElement("div", { ...attributes, ...listeners, style: {
    display: "flex",
    alignItems: "center",
    cursor: "grab"
  } }, /* @__PURE__ */ React66.createElement(RiDraggable, { size: 16, color: String(theme.colors.gray600) })), /* @__PURE__ */ React66.createElement(Checkbox, { label: item.label, checked: item.isChecked, onCheckedChange }));
};
var CheckList = (p) => {
  return /* @__PURE__ */ React66.createElement(SortableCheckList, { ...p, sortable: false });
};

// src/textArrayInput/index.tsx
import { RiDeleteBin4Line as RiDeleteBin4Line2 } from "@remixicon/react";
import React67, { useEffect as useEffect21, useRef as useRef20, useState as useState30 } from "react";
import { styled as styled56 } from "styled-components";
var Wrapper27 = styled56("div").withConfig({
  componentId: "sc-kg5jvs-0"
})`display:flex;flex-direction:column;gap:10px;`;
var Item14 = styled56("div").withConfig({
  componentId: "sc-kg5jvs-1"
})`display:flex;flex-direction:column;gap:5px;`;
var ItemInner2 = styled56("div").withConfig({
  componentId: "sc-kg5jvs-2"
})`display:flex;gap:10px;align-items:flex-end;`;
var TextArrayInput = ({
  value,
  description,
  label,
  error,
  onChange,
  placeholder,
  autoAdd,
  hideAddButton
}) => {
  useEffect21(() => {
    if (value.length === 0) onChange([""]);
  }, [value]);
  let [enterPressed, setEnterPressed] = useState30(false);
  let addedRef = useRef20(0);
  useEffect21(() => {
    if (Date.now() - addedRef.current < 100) return;
    addedRef.current = Date.now();
    if (autoAdd && value[value.length - 1] !== "") onChange([...value, ""]);
  }, [value, autoAdd]);
  return /* @__PURE__ */ React67.createElement(React67.Fragment, null, label && /* @__PURE__ */ React67.createElement(InputLabel, null, label), description && /* @__PURE__ */ React67.createElement(InputDescription, null, description), label && /* @__PURE__ */ React67.createElement(Spacer, { height: 5 }), /* @__PURE__ */ React67.createElement(Wrapper27, null, value.map((v, i) => /* @__PURE__ */ React67.createElement(Item14, { key: i }, /* @__PURE__ */ React67.createElement(ItemInner2, null, /* @__PURE__ */ React67.createElement(Input, { value: v, placeholder, onChange: (e) => {
    let newValue = [...value];
    newValue[i] = e.target.value;
    onChange(newValue);
  }, onKeyDown: (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setEnterPressed(true);
      onChange([...value, ""]);
    }
  }, label, style: {
    flexGrow: 1
  }, size: "2", hideLabel: true, autoFocus: i == value.length - 1 && enterPressed }), /* @__PURE__ */ React67.createElement(Button, { onClick: () => onChange(value.filter((_, j) => j != i)), title: "Remove", iconLeft: /* @__PURE__ */ React67.createElement(RiDeleteBin4Line2, null), size: "2", variant: "soft", disabled: value.length === 1, type: "button" })), Array.isArray(error) && error[i] && /* @__PURE__ */ React67.createElement(Error, null, error[i]))), !autoAdd && !hideAddButton && /* @__PURE__ */ React67.createElement("div", { style: {
    display: "flex",
    gap: 10
  } }, /* @__PURE__ */ React67.createElement(Button, { size: "2", onClick: () => onChange([...value, ""]), type: "button" }, "Add"))));
};

// src/toaster/index.tsx
import React68 from "react";
import { Toaster as BaseToaster, toast as toast3 } from "sonner";
var Toaster = () => /* @__PURE__ */ React68.createElement(BaseToaster, { position: "bottom-right", richColors: true });
export {
  Accordion,
  AccordionSingle,
  Alert,
  AnimateContent,
  AnimateHeight,
  AnimatePanes,
  Attributes,
  Avatar,
  Badge,
  Button,
  Callout,
  CardLinks,
  CenteredLogo,
  CenteredSpinner,
  CheckList,
  Checkbox,
  Code,
  CodeInput,
  Combobox,
  ConnectionBadge,
  ContextMenu,
  Control,
  Copy,
  Datalist,
  DatePicker,
  Dialog,
  DurationInput,
  Entity,
  Error,
  Flex,
  FocusDialog,
  Group4 as Group,
  InfoTooltip,
  InlineCopy,
  Input,
  InputDescription,
  InputHeader,
  InputLabel,
  Kbd,
  KeyValueInput,
  LargePanelDialog,
  LinkButton,
  LinkTabs,
  Logo,
  Menu,
  ModalRoot,
  MultiSelect,
  NumberInput,
  OptionToggle,
  Or,
  OverlayOpenProvider,
  Panel,
  Popover,
  Progress,
  ProgressSteps,
  RenderDate,
  Select,
  SelectList,
  Skeleton,
  Slider,
  SortableCheckList,
  Spacer,
  Spinner,
  Switch,
  Tabs,
  Text,
  TextArrayInput,
  Title3 as Title,
  Toaster,
  Tooltip,
  TooltipSuppressorProvider,
  atom,
  buttonTheme,
  confirm,
  defaultDurationUnits,
  durationUnitSeconds,
  getBadgeStyles,
  getButtonSize,
  getButtonStyles,
  getConnectionStatusDotColor,
  getDuplicateKeys,
  getDurationParts,
  getLink,
  getProgressStepStates,
  inputFieldStyles,
  inputSurfaceStyles,
  insertKeyValueEntries,
  keyValueEntriesToRecord,
  parseKeyValueText,
  pickDurationUnit,
  preventPopoverDismissForNestedOverlay,
  pulseConnectionDot,
  recordToKeyValueEntries,
  showModal,
  theme,
  toast3 as toast,
  useAtom,
  useCopy,
  useDialog,
  useDialogContext,
  useDialogZIndex,
  useIsGroup,
  useIsInDialog,
  useIsInsideOpenOverlay,
  useSuppressTooltipWhileOpen,
  useTooltipSuppressor,
  useZIndex,
  useZindex
};
//# sourceMappingURL=index.js.map